import type { CalendarBreak } from "../../shared/calendar-break";
export type { CalendarBreak } from "../../shared/calendar-break";
import { translateUi } from "./i18n.ts";
import { browserSessionStorage } from "./browser-storage.ts";
import { markSessionExpired, sessionExpiredEvent, sessionClearedEvent } from "./auth.ts";
import { createSessionTransport } from "./session-transport.ts";
import { ApiRequestError, retryTransientRead } from "./api-read-retry.ts";
import {isIdempotentEvidenceUpload,retryIdempotentEvidenceUpload} from "./idempotent-upload-retry.ts";
import { NETWORK_FAILURE_MESSAGE, createClientErrorReporter, isNetworkFailure } from "./client-error-report.ts";
import { createApiReadCoordinator } from "./api-read-coordinator.ts";
import { createApiResponseCache } from "./api-response-cache.ts";
import { apiErrorMessage, createApiReadCooldown, parseApiJson } from "./api-response.ts";
import type { PlanPersonalInstruction } from "../../shared/plan-personal-instructions.ts";
export type { PlanPersonalInstruction } from "../../shared/plan-personal-instructions.ts";
import type {
  BtecStandardisationDetail,
  BtecStandardisationDraftInput,
  BtecStandardisationListItem,
  BtecStandardisationUpdateInput,
} from "../modules/btec/standardisation/types";

const configuredApiUrl =
  import.meta.env?.VITE_API_URL?.replace(/\/$/, "") ||
  "https://edu.bgt.school";
const apiReads = createApiReadCoordinator();
const apiResponses = createApiResponseCache();

const directApiUrl = typeof window !== "undefined" && window.location.origin === "https://edu.bgt.school" && configuredApiUrl === window.location.origin
  ? "https://edubgt.bgtschoolmediac070de.workers.dev" : configuredApiUrl;
// A live binding also routes existing upload and file helpers through the selected session.
export let API_URL = directApiUrl;
const sessionTransport = createSessionTransport({
  proxy: configuredApiUrl,
  direct: directApiUrl,
  fetch: (...args) => fetch(...args),
  storage: browserSessionStorage,
  directFirst: true,
  onOriginChange: (origin) => { API_URL = origin; },
});
if (typeof window !== "undefined") window.addEventListener(sessionClearedEvent, () => { apiReads.invalidate(); apiReadCooldowns.clear(); currentActiveRole = null; sessionTransport.reset(); });
export function enableDirectApiSession(userId: string) { apiReads.invalidate(); return sessionTransport.enable(userId); }

// When a superadmin previews a role as a test user, every request carries this id in the
// X-View-As header so the backend impersonates that (view-only) account. null = normal session.
let currentViewAsId: string | null = null;
let currentActiveRole: string | null = null;
export function setApiActiveRole(role: string | null) {
  if (currentActiveRole !== role) { apiReads.invalidate(); apiResponses.clear(); }
  currentActiveRole = role;
}
let currentElearningAcademicYearId: string | null = null;
export function setViewAsId(id: string | null) {
  if (currentViewAsId !== (id || null)) apiReads.invalidate();
  currentViewAsId = id || null;
}
export function getViewAsId(): string | null {
  return currentViewAsId;
}

export function setElearningAcademicYearId(id: string | null) {
  if (currentElearningAcademicYearId !== (id?.trim() || null)) apiReads.invalidate();
  currentElearningAcademicYearId = id?.trim() || null;
}

export function getElearningAcademicYearId() {
  return currentElearningAcademicYearId;
}

export function withElearningAcademicYear(path: string) {
  if (!currentElearningAcademicYearId) return path;
  const url = new URL(path, "https://edubgt.local");
  const scopedPrefixes = [
    "/api/assessment-worksheets/",
    "/api/elearning/",
    "/api/syllabus/",
    "/api/plans/",
    "/api/professor/my-schedule",
    "/api/professor/syllabus",
    "/api/professor/plan",
    "/api/professor/materials",
    "/api/professor/attendance",
    "/api/professor/lesson-topics",
    "/api/professor/activity-work",
    "/api/student/my-schedule",
    "/api/student/plan",
    "/api/student/plans",
    "/api/student/materials",
    "/api/student/attendance",
    "/api/parent/elearning/",
    "/api/admin/syllabus",
    "/api/admin/school-subjects",
    "/api/admin/subject-courses",
    "/api/admin/timetable",
    "/api/admin/import-reconciliation",
  ];
  if (!scopedPrefixes.some((prefix) => url.pathname === prefix.replace(/\/$/, "") || url.pathname.startsWith(prefix))) return path;
  url.searchParams.set("academicYearId", currentElearningAcademicYearId);
  return `${url.pathname}${url.search}${url.hash}`;
}
// Attaches the X-View-As header to raw fetch() calls (blob downloads) that bypass apiFetch.
function viewAsInit(base: RequestInit = {}): RequestInit {
  const init: RequestInit = { credentials: "include", ...base };
  const headers = new Headers(base.headers);
  if (currentViewAsId) headers.set("X-View-As", currentViewAsId);
  if (currentActiveRole) headers.set("X-Active-Role", currentActiveRole);
  init.headers = headers;
  return init;
}

export type ApiRole = "superadmin" | "admin" | "administration" | "support_staff" | "professor" | "student_adviser" | "counselor" | "student" | "parent" | "food_company";
export type AuthProvider = "microsoft" | "local";
export type ReferralUrgency = "low" | "medium" | "high";

export type School = {
  id: string;
  name: string;
  shortName: string;
  city: string | null;
  timezone?: string | null;
};

export type UserSchoolRole = {
  schoolId: string;
  role: ApiRole;
};

export type UserSchoolAccess = {
  school: School;
  roles: ApiRole[];
};

export type ElearningAcademicYear = {
  id: string;
  name: string;
  startsOn: string | null;
  endsOn: string | null;
  isActive: boolean;
};

export type ApiUser = {
  id: string;
  email: string;
  // false per llogaritë "pa email" (regjistruar per orar/eLearning, ende pa mundesi login) -
  // shih worker/src/index.ts registerRosterPendingPersonNoAccount.
  hasAccountEmail?: boolean;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  role: ApiRole;
  roles: ApiRole[];
  schools?: School[];
  foodDeliverySchools?: School[];
  schoolRoles?: UserSchoolRole[];
  activeSchoolId?: string;
  canSwitchSchools?: boolean;
  lastLoginAt: string | null;
  loginCount: number;
};

export type AdminUser = ApiUser & {
  isActive: boolean;
  authProvider: AuthProvider;
  schoolIds?: string[];
  schoolRoles?: UserSchoolRole[];
  activeStudentSchoolIds?: string[];
  graduatedStudentSchoolIds?: string[];
  invitedAt: string | null;
  passwordResetRequestedAt: string | null;
  gender: "male" | "female";
  hasPhoto: boolean;
};

export type AdministrationStaffMember = Pick<
  ApiUser,
  "id" | "email" | "name" | "firstName" | "lastName" | "role" | "roles"
>;

export type SchoolSubject = {
  id: string;
  schoolId: string;
  academicYearId: string;
  name: string;
  shortName: string | null;
  assignmentCount: number;
};

export type StaffSubjectAssignment = {
  id: string;
  schoolId: string;
  academicYearId: string;
  staffUserId: string;
  staffName: string;
  staffEmail: string;
  subjectId: string;
  subjectName: string;
  subjectShortName: string | null;
  classId: string | null;
};

export type TimetableClass = {
  id: string;
  schoolId: string;
  academicYearId: string;
  sourceId: string | null;
  name: string;
  shortName: string | null;
  grade: number | null;
};

export type LoginAuditEvent = {
  id: string;
  userId: string;
  email: string;
  authProvider: AuthProvider;
  loginAt: string;
  ipHash: string | null;
  userAgentHash: string | null;
};

export type GuardianRelationship = "parent" | "guardian" | "father" | "mother" | "other";

export type GuardianStudentLink = {
  id: string;
  guardianUserId: string;
  guardianName: string | null;
  guardianEmail: string;
  studentUserId: string;
  studentName: string | null;
  studentEmail: string;
  gradeClass: string | null;
  relationship: GuardianRelationship;
  isPrimary: boolean;
};

export type ImportUsersResult = {
  created: number;
  updated: number;
  linked: number;
  staged: number;
  pending: number;
  invited: number;
  skipped: number;
  errors: Array<{ row: number; message: string }>;
  suspiciousNames?: string[];
  xmlStats?: {
    teachers: number;
    students: number;
    classes: number;
    subjects: number;
    lessons: number;
    cards: number;
  };
};

export type AdminOverview = {
  usersTotal: number;
  superadminsTotal: number;
  adminsTotal: number;
  administrationsTotal: number;
  professorsTotal: number;
  studentAdvisersTotal: number;
  studentsTotal: number;
  parentsTotal: number;
  activeTotal: number;
};

export type PublicSettings = {
  schoolDomain: string;
  loginHeroUrl: string | null;
  turnstileSiteKey: string | null;
  stagingLoginEnabled?: boolean;
};

export type AuthLookup = {
  exists: boolean;
  isActive: boolean;
  authProvider: AuthProvider | null;
  isSchoolAccount: boolean;
};

export type StudentCareStudent = {
  id: string;
  email: string;
  name: string;
  gradeClass: string | null;
  parentEmail: string | null;
};

export type StudentCareAdviser = {
  id: string;
  email: string;
  name: string;
};

export type CounselorAdvisory = {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  gradeClass: string | null;
  reasons: string[];
  content: string;
  createdAt: string;
};

export type TeacherAdvisory = CounselorAdvisory & {
  counselorName: string;
};

export type StudentCareTeacher = {
  id: string;
  email: string;
  name: string;
};

export type TeacherFeedbackStatus = "improved" | "unchanged" | "worse" | "not_observed";

export type TeacherFeedbackRequest = {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  gradeClass: string | null;
  teacherId: string;
  teacherName: string;
  teacherEmail: string;
  counselorName: string;
  question: string;
  dueAt: string | null;
  responseStatus: TeacherFeedbackStatus | null;
  responseComment: string | null;
  respondedAt: string | null;
  createdAt: string;
};

export type IntakeReport = {
  id: string;
  studentName: string;
  gradeClass: string | null;
  content: string;
  createdAt: string;
};

export type StudentRecord = {
  id: string;
  classId?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  gradeClass: string;
  parentEmail: string;
  guardianEmails?: string[];
  parentName: string;
  gender?: "male" | "female" | "other" | "unspecified" | null;
  dateOfBirth?: string | null;
  birthplace?: string | null;
  municipality?: string | null;
  country?: string | null;
  homeroomName?: string | null;
  homeroomName2?: string | null;
  fullName: string;
};

export type ProfessorReferralSummary = {
  id: string;
  referredAt: string;
  gradeClass: string;
  urgency: ReferralUrgency;
  status: string;
  studentName: string;
  studentEmail: string;
  notes: string;
  counselorMessage: string | null;
  academicReasons: string[];
  socialEmotionalReasons: string[];
  confidential: true;
};

export type CounselorReferralSummary = {
  id: string;
  referredAt: string;
  gradeClass: string;
  urgency: ReferralUrgency;
  status: string;
  studentName: string;
  studentEmail: string;
  parentEmail: string | null;
  teacherName: string;
  teacherEmail: string;
  notes: string;
  academicReasons: string[];
  socialEmotionalReasons: string[];
  isAssignedToCurrentCounselor: boolean;
};

export type CounselorNotification = {
  id: string;
  type: "new_referral" | "appointment_reminder" | "appointment_changed";
  title: string;
  message: string;
  referral_id: string | null;
  appointment_id: string | null;
  read_at: string | null;
  created_at: string;
};

export type CounselorAppointment = {
  id: string;
  referralId: string | null;
  studentId: string;
  studentName: string;
  gradeClass: string;
  scheduledAt: string;
  status: string;
  meetingType: "student" | "parent";
  parentName: string | null;
  parentEmail: string | null;
};

export type CounselorMonthlyStats = {
  total: number;
  completed: number;
  pending: number;
  highPriority: number;
};

export type ReferralEvent = {
  id: string;
  event_type: string;
  details: string;
  created_at: string;
  actor_name: string;
};

export type CounselorPrivateNote = {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
};

export type CreatePsychologistReferralInput = {
  studentId: string;
  psychologistId: string;
  gradeClass: string;
  academicReasons: string[];
  socialEmotionalReasons: string[];
  notes: string;
  urgency: ReferralUrgency;
};

export type BtecSummary = {
  projectsTotal: number;
  packagesTotal: number;
  documentsTotal: number;
  unitsTotal: number;
};

export type BtecProject = {
  id: string;
  projectTitle: string;
  subject: string | null;
  className: string | null;
  deadline: string | null;
  documentLanguage: "sq" | "en" | "both";
  unitNumber: string | null;
  unitName: string | null;
  learningAims: string | null;
  topic: string | null;
  shortDescription: string | null;
  scenario: string | null;
  scenarioSource: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export type BtecCriterion = {
  id: string;
  projectId: string;
  code: string;
  level: "PASS" | "MERIT" | "DISTINCTION";
  description: string | null;
  sortOrder: number;
  createdAt: string;
};

export type BtecPackage = {
  id: string;
  projectId: string | null;
  qualification: string | null;
  unitNumber: string | null;
  unitTitle: string | null;
  learningAims: string | null;
  assignmentTitle: string;
  className: string;
  handInDeadline: string | null;
  issueDate: string | null;
  language: "sq" | "en" | "both";
  assessorName: string;
  scenarioSource: string;
  scenarioText: string | null;
  taskText: string | null;
  evidenceRequired: string | null;
  scenarioApproved: boolean;
  scenarioReviewStatus: "none" | "pending" | "approved" | "actions_required" | "rejected";
  scenarioReviewNote: string | null;
  scenarioRequestedAt: string | null;
  scenarioReviewedAt: string | null;
  stagePlanApproved: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
};

export type CombinedProjectStatus = "not_started" | "in_progress" | "ready" | "complete" | "failed";

export type BtecProjectOverviewRow = {
  windowId: string;
  unitNumber: string;
  unitTitle: string | null;
  learningAimScope: string;
  className: string | null;
  generation: string | null;
  releaseAt: string | null;
  studentDueAt: string | null;
  teacherDueAt: string | null;
  coordinatorDueAt: string | null;
  packageId: string | null;
  packageStatus: string | null;
  stagePlanApproved: boolean | null;
  combinedStatus: CombinedProjectStatus;
};

export type BtecScenarioRequest = {
  id: string;
  unitNumber: string | null;
  unitTitle: string | null;
  assignmentTitle: string;
  className: string;
  scenarioText: string | null;
  requestedAt: string | null;
  teacherName: string | null;
  teacherEmail: string | null;
};

export type BriefIvDecision = "approved" | "actions_required" | "rejected";

export function decideBriefIv(
  token: string,
  packageId: string,
  payload: { decision: BriefIvDecision; checklist: Record<string, boolean>; note?: string },
) {
  return apiFetch<{ ok: boolean }>(`/api/btec/scenario-requests/${packageId}/decide`, {
    method: "POST",
    token,
    body: JSON.stringify(payload),
  });
}

// Streams the "Internal Verification of the Assignment Brief" DOCX record (nothing persisted
// server-side). Mirrors downloadIvForm's fetch shape - session cookie auth, no persisted URL.
export async function downloadBriefIvForm(token: string, packageId: string, unitNumber: string | null) {
  const response = await fetch(
    `${API_URL}/api/btec/packages/${encodeURIComponent(packageId)}/brief-iv-form.docx`,
    { credentials: "include" },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error || translateUi("Shkarkimi i formularit dështoi.", "Form download failed."));
  }
  const blob = await response.blob();
  await downloadResponseBlob(blob, `verifikimi-brief-unit-${unitNumber ?? "x"}.docx`);
}

export type BtecStageSummary = {
  stageNumber: number;
  title: string;
  opensAt: string;
  closesAt: string;
  criterionCodes: string[];
};

export type BtecStageCriterionResult = {
  code: string;
  met: boolean;
  note: string;
};

export type BtecStageStudentRow = {
  studentUserId: string;
  studentName: string | null;
  classLabel: string;
  status: "locked" | "open" | "submitted" | "ai_flagged" | "approved";
  aiCriteriaTotal: number | null;
  aiCriteriaMet: number | null;
  aiCriteriaResults: BtecStageCriterionResult[];
  questionId: string;
  submissionId: string;
  submittedAt: string | null;
  closesAt: string;
  isLate: boolean;
};

export type BtecPackageDocument = {
  id: string;
  packageId: string;
  stageId: string | null;
  stageNumber: number | null;
  title: string;
  documentType: string;
  folder: string;
  fileName: string;
  status: string;
  templateKey: string | null;
  templateClass: string | null;
  completionMode: "student_fillable" | "read_only" | "teacher_only";
  updatedAt: string;
};

export type BtecProjectDetail = {
  project: BtecProject;
  criteria: BtecCriterion[];
  documents: BtecDocument[];
  packages: BtecPackage[];
  packageDocuments: BtecPackageDocument[];
};

export type BtecDocument = {
  id: string;
  projectId: string | null;
  packageId: string | null;
  title: string;
  docType: string;
  status: string;
  updatedAt: string;
};

export type BtecGuideDocumentCategory = "project" | "assessment";
export type BtecAssessmentReportKind = "assessment_record" | "learner_commentary";

export type BtecGuideDocument = {
  id: string;
  category: BtecGuideDocumentCategory;
  reportKind: BtecAssessmentReportKind | null;
  title: string;
  description: string | null;
  originalFilename: string;
  fileSize: number;
  contentType: string;
  uploadedByName: string | null;
  uploadedByEmail: string | null;
  createdAt: string;
  updatedAt: string;
};

export type BtecCurriculumDocumentCategory =
  | "qualification_specification"
  | "sample_delivery_plan"
  | "content_mapping"
  | "scheme_of_work";

export type BtecCurriculumDocument = {
  id: string;
  sha256: string;
  category: BtecCurriculumDocumentCategory;
  unitNumber: string | null;
  title: string;
  publisher: string;
  issueLabel: string | null;
  publishedOn: string | null;
  glh: number | null;
  sourceLessonMinutes: number | null;
  sourceLessonCount: number | null;
  pageCount: number;
  originalFilename: string;
  fileSize: number;
  contentType: string;
  isActive: boolean;
  uploadedByName: string | null;
  uploadedByEmail: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function listBtecGuideDocuments(token: string, schoolId: string, kind?: "assessment_reports"): Promise<BtecGuideDocument[]> {
  const res = await apiFetch<{ documents: BtecGuideDocument[] }>(
    `/api/btec/guides?schoolId=${encodeURIComponent(schoolId)}${kind ? `&kind=${kind}` : ""}`,
    { token },
  );
  return res.documents ?? [];
}

export async function uploadBtecGuideDocument(
  token: string,
  schoolId: string,
  input: { category: BtecGuideDocumentCategory; reportKind?: BtecAssessmentReportKind | null; title: string; description: string; file: File },
): Promise<BtecGuideDocument | null> {
  const body = new FormData();
  body.set("category", input.category);
  if (input.reportKind) body.set("reportKind", input.reportKind);
  body.set("title", input.title);
  body.set("description", input.description);
  body.set("file", input.file);
  const res = await apiFetch<{ document: BtecGuideDocument | null }>(
    `/api/btec/guides?schoolId=${encodeURIComponent(schoolId)}`,
    { token, method: "POST", body },
  );
  return res.document;
}

export async function deleteBtecGuideDocument(token: string, schoolId: string, id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/api/btec/guides/${encodeURIComponent(id)}?schoolId=${encodeURIComponent(schoolId)}`,
    { token, method: "DELETE" },
  );
}

export async function downloadBtecGuideDocument(token: string, schoolId: string, item: BtecGuideDocument): Promise<void> {
  const response = await fetch(`${API_URL}/api/btec/guides/${encodeURIComponent(item.id)}/download?schoolId=${encodeURIComponent(schoolId)}`, {
    credentials: "include",
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || translateUi("Shkarkimi dështoi.", "Download failed."));
  }
  const blob = await response.blob();
  await downloadResponseBlob(blob, item.originalFilename);
}

export async function listBtecCurriculumDocuments(token: string, schoolId: string): Promise<BtecCurriculumDocument[]> {
  const res = await apiFetch<{ documents: BtecCurriculumDocument[] }>(
    `/api/btec/curriculum-documents?schoolId=${encodeURIComponent(schoolId)}`,
    { token },
  );
  return res.documents ?? [];
}

export async function uploadBtecCurriculumDocument(
  token: string,
  schoolId: string,
  input: {
    category: BtecCurriculumDocumentCategory;
    unitNumber?: string;
    title: string;
    issueLabel?: string;
    publishedOn?: string;
    glh?: string;
    sourceLessonMinutes?: string;
    sourceLessonCount?: string;
    file: File;
  },
) {
  const body = new FormData();
  body.set("category", input.category);
  if (input.unitNumber) body.set("unitNumber", input.unitNumber);
  body.set("title", input.title);
  if (input.issueLabel) body.set("issueLabel", input.issueLabel);
  if (input.publishedOn) body.set("publishedOn", input.publishedOn);
  if (input.glh) body.set("glh", input.glh);
  if (input.sourceLessonMinutes) body.set("sourceLessonMinutes", input.sourceLessonMinutes);
  if (input.sourceLessonCount) body.set("sourceLessonCount", input.sourceLessonCount);
  body.set("file", input.file);
  return apiFetch<{ document: BtecCurriculumDocument | null; deduplicated: boolean }>(
    `/api/btec/curriculum-documents?schoolId=${encodeURIComponent(schoolId)}`,
    { token, method: "POST", body },
  );
}

export async function deactivateBtecCurriculumDocument(token: string, schoolId: string, id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/api/btec/curriculum-documents/${encodeURIComponent(id)}?schoolId=${encodeURIComponent(schoolId)}`,
    { token, method: "DELETE" },
  );
}

export async function downloadBtecCurriculumDocument(
  token: string,
  schoolId: string,
  item: BtecCurriculumDocument,
): Promise<void> {
  const response = await fetch(`${API_URL}/api/btec/curriculum-documents/${encodeURIComponent(item.id)}/download?schoolId=${encodeURIComponent(schoolId)}`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error(await response.text().catch(() => translateUi("Shkarkimi dështoi.", "Download failed.")));
  const blob = await response.blob();
  await downloadResponseBlob(blob, item.originalFilename);
}

export type GlobalAiSettings = {
  provider: "openai" | "anthropic" | "gemini";
  model: string;
  enabled: boolean;
  hasApiKey: boolean;
  keyLast4: string | null;
  apiKeyUpdatedAt: string | null;
  allowedSchoolIds: string[];
  updatedAt: string | null;
  schoolPolicies: {
    schoolId: string;
    schoolName: string;
    aiEnabled: boolean;
    allowGlobalAi: boolean;
    allowedModules: string[];
    schoolProvider: "openai" | "anthropic" | "gemini";
    schoolModel: string;
    schoolEnabled: boolean;
    schoolHasApiKey: boolean;
    schoolKeyLast4: string | null;
    schoolUpdatedAt: string | null;
  }[];
};

export type GlobalAiTestResult = {
  ok: boolean;
  provider: GlobalAiSettings["provider"];
  model: string;
  message: string;
  latencyMs: number | null;
};

export type BtecUnit = {
  id: string;
  unitNumber: string;
  title: string;
  credits: number | null;
  sortOrder: number;
  generation: string | null;
  updatedAt: string;
  // Admin lists only: false when the shared-catalog Unit is switched off for the selected branch.
  branchActive?: boolean;
};

export type BtecUnitLearningAim = {
  aimLetter: "A" | "B" | "C" | "D";
  title: string;
  sortOrder: number;
  projectGroup: number | null;
};

export type BtecStudentProgressProject = {
  scope: string;
  aims: Array<{ aimLetter: string; title: string }>;
  windowCount: number;
  rostered: boolean;
  status: "planned" | "in_progress" | "graded";
  grade: string | null;
  approvedAt: string | null;
  assessmentId: string | null;
  criteria: Array<{
    code: string;
    description: string | null;
    achieved: boolean;
  }>;
};

export type BtecStudentProgressUnit = {
  unitNumber: string;
  title: string;
  credits: number | null;
  generation: string | null;
  finalGrade: string | null;
  finalPending: boolean;
  projects: BtecStudentProgressProject[];
};

export async function fetchStudentBtecProgress(token: string, schoolId: string, studentUserId?: string): Promise<BtecStudentProgressUnit[]> {
  const res = await apiFetch<{ units: BtecStudentProgressUnit[] }>(
    `/api/student/btec/progress${schoolQs(schoolId, studentUserId ? { studentUserId } : undefined)}`,
    { token },
  );
  return res.units ?? [];
}

export type StudentBtecProjectBucket = "current" | "upcoming" | "past";
export type StudentBtecProject = {
  id: string;
  windowId: string;
  assessmentId: string | null;
  unitNumber: string;
  unitName: string | null;
  learningAimScope: string;
  title: string;
  releaseAt: string | null;
  studentDueAt: string | null;
  bucket: StudentBtecProjectBucket;
  submissionStatus: "missing" | "draft" | "submitted" | "overdue" | string;
  submittedAt: string | null;
  finalGrade: string | null;
  finalFeedback: string | null;
  approvedAt: string | null;
  gradeUnderVerification: boolean;
  gradeReverification: boolean;
  learnerDeclarationConfirmedAt: string | null;
  learnerReport: { id: string; fileName: string } | null;
  stageCount: number;
  openStageCount: number;
  submittedStageCount: number;
};

export type StudentBtecAction = {
  projectId: string;
  kind: "grade_risk" | "overdue" | "due_soon" | "stage_ready";
  title: string;
  unitNumber: string;
  learningAimScope: string;
  message: string;
};

export async function fetchStudentBtecProjects(token: string, schoolId: string, studentUserId?: string, viewAsUserId?: string) {
  return apiFetch<{ projects: StudentBtecProject[]; actions: StudentBtecAction[] }>(
    `/api/student/btec/projects${schoolQs(schoolId, {
      ...(studentUserId ? { studentUserId } : {}), ...(viewAsUserId ? { viewAsUserId } : {}),
    })}`,
    { token },
  );
}

export async function downloadStudentBtecReport(
  report: { id: string; fileName: string },
  schoolId: string,
  studentUserId?: string,
) {
  const response = await fetch(
    `${API_URL}/api/student/btec/reports/${encodeURIComponent(report.id)}/download${schoolQs(schoolId, studentUserId ? { studentUserId } : undefined)}`,
    { credentials: "include" },
  );
  if (!response.ok) throw new Error(translateUi("Shkarkimi i raportit dështoi.", "Report download failed."));
  const blob = await response.blob();
  await downloadResponseBlob(blob, report.fileName);
}

export type BtecParentChild = {
  studentUserId: string;
  studentName: string;
  classLabel: string;
};

export async function fetchBtecParentChildren(token: string, schoolId: string, viewAsUserId?: string): Promise<BtecParentChild[]> {
  const res = await apiFetch<{ children: BtecParentChild[] }>(
    `/api/btec/parent/children${schoolQs(schoolId, viewAsUserId ? { viewAsUserId } : undefined)}`,
    { token },
  );
  return res.children ?? [];
}

export type BtecParentStageFeedback = {
  id: string; stageNumber: number; subjectName: string; text: string; publishedAt: string;
  summary: { done: string; toFix: string; weak: string } | null;
};

export async function fetchBtecParentStageFeedback(token: string, schoolId: string, studentUserId: string, viewAsUserId?: string): Promise<BtecParentStageFeedback[]> {
  const res = await apiFetch<{ feedback: BtecParentStageFeedback[] }>(
    `/api/btec/parent/stage-feedback${schoolQs(schoolId, { studentUserId, ...(viewAsUserId ? { viewAsUserId } : {}) })}`,
    { token },
  );
  return res.feedback ?? [];
}

export type BtecParentWorksheet = {
  sessionId: string; occurrenceDate: string; lessonNumber: number; topic: string; subjectName: string; classLabel: string;
  documentTitle: string; status: string; submittedAt: string | null; closesAt: string; downloadable: boolean; teacherGrade: number | null; teacherComment?: string;
};

export async function fetchBtecParentWorksheets(token: string, schoolId: string, studentUserId: string): Promise<BtecParentWorksheet[]> {
  const res = await apiFetch<{ worksheets: BtecParentWorksheet[] }>(
    `/api/btec/parent/worksheets${schoolQs(schoolId, { studentUserId })}`, { token },
  );
  return res.worksheets ?? [];
}

export type BtecParentWorksheetDetail = {
  title: string; subjectName: string; lessonNumber: number; topic: string; subtopic: string; submittedAt: string | null;
  schema: import("../modules/assessment/WorksheetFormParts").WorksheetSchema; answers: Record<string, string>;
};

export async function fetchBtecParentWorksheet(token: string, schoolId: string, studentUserId: string, sessionId: string): Promise<BtecParentWorksheetDetail> {
  const res = await apiFetch<{ worksheet: BtecParentWorksheetDetail }>(
    `/api/btec/parent/worksheets/${encodeURIComponent(sessionId)}${schoolQs(schoolId, { studentUserId })}`, { token },
  );
  return res.worksheet;
}

export function downloadBtecParentWorksheet(schoolId: string, studentUserId: string, worksheet: BtecParentWorksheet) {
  return downloadFile(`/api/btec/parent/worksheets/${encodeURIComponent(worksheet.sessionId)}/download${schoolQs(schoolId, { studentUserId })}`,
    `${worksheet.documentTitle || "Worksheet"}.docx`);
}

export async function fetchUnitLearningAims(token: string, schoolId: string, unitNumber: string): Promise<BtecUnitLearningAim[]> {
  const res = await apiFetch<{ data?: { aims?: BtecUnitLearningAim[] } }>(
    `/api/btec/units/${encodeURIComponent(unitNumber)}/learning-aims${schoolQs(schoolId)}`,
    { token },
  );
  return res.data?.aims ?? [];
}

export async function saveUnitLearningAims(
  token: string,
  schoolId: string,
  unitNumber: string,
  aims: { aimLetter: string; title: string; projectGroup: number | null }[],
): Promise<BtecUnitLearningAim[]> {
  const res = await apiFetch<{ data?: { aims?: BtecUnitLearningAim[] } }>(
    `/api/btec/units/${encodeURIComponent(unitNumber)}/learning-aims${schoolQs(schoolId)}`,
    { method: "PUT", token, body: JSON.stringify({ aims }) },
  );
  return res.data?.aims ?? [];
}

export type BtecPackageTemplate = {
  id: string;
  key: string;
  title: string;
  description: string | null;
  templateClass: string;
  templateGroup: string;
  isRequired: boolean;
  isActive: boolean;
  defaultOrder: number;
  appliesToPackageType: string;
  appliesToSourceType: string;
  contentMarkdown: string;
  placeholdersJson: string;
  folder: string;
  documentType: string;
  unitScope: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function listPackageTemplatesByClass(
  token: string,
  cls: "DEFAULT_STATIC" | "REFERENCE_EXAMPLE",
): Promise<BtecPackageTemplate[]> {
  const res = await apiFetch<{ templates: BtecPackageTemplate[] }>(
    `/api/btec/admin/package-templates/by-class/${cls}`,
    { token },
  );
  return res.templates ?? [];
}

export type BtecPackageTemplateSummary = Pick<BtecPackageTemplate,
  "id" | "key" | "title" | "templateClass" | "documentType" | "unitScope" | "isActive" | "createdAt" | "updatedAt">;

export async function listPackageTemplateSummaries(token: string, cls: "DEFAULT_STATIC" | "REFERENCE_EXAMPLE"): Promise<BtecPackageTemplateSummary[]> {
  const response = await apiFetch<{ templates: BtecPackageTemplateSummary[] }>(
    `/api/btec/admin/package-templates/by-class/${cls}?view=summary`, { token });
  return response.templates ?? [];
}

export async function getPackageTemplate(token: string, key: string): Promise<BtecPackageTemplate> {
  const response = await apiFetch<{ template: BtecPackageTemplate }>(
    `/api/btec/admin/package-templates/${encodeURIComponent(key)}`, { token });
  return response.template;
}

export async function createPackageTemplate(
  token: string,
  input: {
    title: string;
    contentMarkdown: string;
    templateClass: "DEFAULT_STATIC" | "REFERENCE_EXAMPLE";
    documentType: string;
    description?: string;
    unitScope?: string;
  },
): Promise<BtecPackageTemplate | null> {
  const res = await apiFetch<{ template: BtecPackageTemplate | null }>(
    `/api/btec/admin/package-templates`,
    { token, method: "POST", body: JSON.stringify(input) },
  );
  return res.template;
}

export async function updatePackageTemplate(
  token: string,
  key: string,
  input: {
    title: string;
    contentMarkdown: string;
    documentType: string;
    unitScope?: string | null;
  },
): Promise<BtecPackageTemplate | null> {
  const res = await apiFetch<{ template: BtecPackageTemplate | null }>(
    `/api/btec/admin/package-templates/${encodeURIComponent(key)}`,
    { token, method: "PATCH", body: JSON.stringify(input) },
  );
  return res.template;
}

export async function deletePackageTemplate(token: string, key: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/api/btec/admin/package-templates/${encodeURIComponent(key)}`,
    { token, method: "DELETE" },
  );
}

export type BtecEvidenceSubmission = {
  id: string;
  templateId: string | null;
  assignedUserId: string;
  schoolSubjectId: string | null;
  title: string;
  subject: string;
  unitNumber: string;
  learningAim: string;
  requiredItemsJson: string;
  dueAt: string;
  releaseAt: string | null;
  studentDueAt: string | null;
  coordinatorDueAt: string | null;
  status: string;
  effectiveStatus: string;
  statusLabel: string;
  professorMessage: string | null;
  submittedAt: string | null;
  lockedAt: string | null;
  reopenedAt: string | null;
  reopenedBy: string | null;
  reopenedReason: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  assigneeName?: string | null;
  assigneeEmail?: string | null;
};

export type BtecEvidenceRequiredItem = {
  key: string;
  label: string;
  required: boolean;
};

export type BtecEvidenceFile = {
  id: string;
  assignmentId: string;
  itemKey: string;
  originalFilename: string;
  fileSize: number;
  contentType: string;
  uploadedBy: string;
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
};

export type BtecSubmissionDetail = {
  submission: BtecEvidenceSubmission;
  files: BtecEvidenceFile[];
  requiredItems: BtecEvidenceRequiredItem[];
};

export type BtecAccessStatus = {
  canAccess: boolean;
  reason: string | null;
  linkedSubjects: number;
  linkedUnits: number;
  isBtecCoordinator: boolean;
  canReadHistoricalAssessmentWindows: boolean;
};

export type BtecAssessmentAcademicYear = {
  id: string;
  name: string;
  isActive: boolean;
};

export type BtecAssignableTeacher = {
  id: string;
  name: string | null;
  email: string;
};

export type BtecSubmissionOptionProfessor = {
  id: string;
  name: string | null;
  email: string;
};

export type BtecSubmissionOption = {
  key: string;
  schoolSubjectId: string;
  subjectName: string;
  subjectShortName: string | null;
  unitNumber: string;
  unitTitle: string | null;
  professors: BtecSubmissionOptionProfessor[];
};

export type BtecOverdueEmailRecipient = {
  id: string;
  name: string | null;
  email: string;
};

export type BtecOverdueEmailSettings = {
  enabled: boolean;
  reminderOffsetDays: number[];
  subjectTemplate: string;
  bodyTemplate: string;
  recipients: BtecOverdueEmailRecipient[];
  administrationUsers: BtecOverdueEmailRecipient[];
  ccRecipients: BtecOverdueEmailRecipient[];
  includeProfessorCc: boolean;
};

export type BtecScenarioEmailKindSettings = {
  enabled: boolean;
  subjectTemplate: string;
  bodyTemplate: string;
  recipients: BtecOverdueEmailRecipient[];
};

export type BtecScenarioEmailSettings = {
  request: BtecScenarioEmailKindSettings;
  decision: BtecScenarioEmailKindSettings;
  candidateUsers: BtecOverdueEmailRecipient[];
};

export type BtecAssessmentStartNotificationTemplate = {
  offsetDays: number;
  enabled: boolean;
  subjectTemplate: string;
  bodyTemplate: string;
};

export type BtecAssessmentStartNotificationSettings = {
  enabled: boolean;
  sendEmail: boolean;
  createPlatformNotification: boolean;
  notifyProfessors: boolean;
  notifyStudents: boolean;
  notifyParents: boolean;
  templates: BtecAssessmentStartNotificationTemplate[];
};

export type BtecEmailTestConfig = {
  graphClientId: boolean;
  graphClientSecret: boolean;
  graphTenantId: boolean;
  mailSender: boolean;
};

export type BtecEmailTestAssignment = {
  id: string;
  title: string;
  unitNumber: string;
  learningAim: string;
};

export type BtecEmailTestAssessmentWindow = {
  id: string;
  title: string;
  unitNumber: string;
  learningAim: string;
  releaseAt: string | null;
  assessorEmail: string | null;
};

export type BtecEmailTestInfo = {
  config: BtecEmailTestConfig;
  adminEmail: string;
  assignments: BtecEmailTestAssignment[];
  assessmentWindows: BtecEmailTestAssessmentWindow[];
  teacherReminderOffsetDays?: number[];
};

export type BtecAssessmentStartRunItem = {
  windowId: string;
  title: string;
  unitNumber: string;
  learningAim: string;
  releaseAt: string | null;
  offsetDays: number;
  recipientRole: "professor" | "student" | "parent";
  recipientName: string | null;
  recipientEmail: string | null;
  decision: "sent" | "skipped" | "error";
  reason: string;
};

export type BtecAssessmentDeadlineRunItem = {
  windowId: string;
  title: string;
  deadlineType: "student" | "teacher";
  effectiveDueAt: string;
  offsetDays: number;
  recipientRole: "professor" | "student" | "parent" | "administration";
  recipientName: string | null;
  recipientEmail: string | null;
  decision: "sent" | "skipped" | "error";
  reason: string;
};

export type BtecTeacherOverdueDiagnosticReport = {
  windowId: string;
  title: string;
  teacherDueAt: string;
  assessorName: string | null;
  assessorEmail: string | null;
  recipients: BtecOverdueEmailRecipient[];
  enabled: boolean;
  suppressed: boolean;
  eligible: boolean;
  testDelivered: boolean;
};

function withSchoolPath(path: string, schoolId: string) {
  return `${path}${path.includes("?") ? "&" : "?"}schoolId=${encodeURIComponent(schoolId)}`;
}

export type UserAccessProfile = {
  isBtecCoordinator: boolean;
  receivesOverdueNotifications: boolean;
};

export function fetchUserAccessProfile(token: string | null, userId: string, schoolId: string) {
  return apiFetch<UserAccessProfile>(
    withSchoolPath(`/api/admin/users/${encodeURIComponent(userId)}/access-profile`, schoolId),
    { token },
  );
}

export function updateUserAccessProfile(
  token: string | null,
  userId: string,
  schoolId: string,
  body: UserAccessProfile,
) {
  return apiFetch<UserAccessProfile>(
    withSchoolPath(`/api/admin/users/${encodeURIComponent(userId)}/access-profile`, schoolId),
    { token, method: "PATCH", body: JSON.stringify(body) },
  );
}

export function fetchBtecEmailTestInfo(token: string | null, schoolId: string) {
  return apiFetch<BtecEmailTestInfo>(withSchoolPath("/api/btec/admin/email-test", schoolId), { token });
}

export function sendBtecTestEmail(
  token: string | null,
  schoolId: string,
  input: { to: string; cc?: string; type?: "generic" | "overdue"; assignmentId?: string },
) {
  return apiFetch<{ ok: boolean; to?: string; cc?: string[]; error?: string }>(
    withSchoolPath("/api/btec/admin/email-test/send", schoolId),
    { method: "POST", token, body: JSON.stringify(input) },
  );
}

export function runBtecAssessmentStartNotificationTest(
  token: string | null,
  schoolId: string,
  input: { windowId?: string; offsetDays: number; force?: boolean; dryRun?: boolean; testEmail?: string },
) {
  return apiFetch<{ ok: boolean; report?: { items: BtecAssessmentStartRunItem[] }; error?: string }>(
    withSchoolPath("/api/btec/admin/assessment-start-notification-run", schoolId),
    { method: "POST", token, body: JSON.stringify(input) },
  );
}

export function runBtecAssessmentDeadlineReminderTest(
  token: string | null,
  schoolId: string,
  input: {
    windowId: string;
    deadlineType: "student" | "teacher";
    offsetDays: number;
    testEmail?: string;
  },
) {
  return apiFetch<{
    ok: boolean;
    report?: { items: BtecAssessmentDeadlineRunItem[] };
    testNotificationCreated?: boolean;
    error?: string;
  }>(
    withSchoolPath("/api/btec/admin/assessment-deadline-reminder-run", schoolId),
    { method: "POST", token, body: JSON.stringify(input) },
  );
}

export function runBtecTeacherOverdueDiagnostic(
  token: string | null,
  schoolId: string,
  input: { windowId: string; testEmail?: string },
) {
  return apiFetch<{
    ok: boolean;
    report?: BtecTeacherOverdueDiagnosticReport;
    testNotificationCreated?: boolean;
    error?: string;
  }>(
    withSchoolPath("/api/btec/admin/assessment-teacher-overdue-diagnostic-run", schoolId),
    { method: "POST", token, body: JSON.stringify(input) },
  );
}

type ApiOptions = RequestInit & {
  token?: string | null;
  suppressUnauthorizedSessionEvent?: boolean;
  retryTransientRead?: boolean;
  deduplicateRead?: boolean;
  // Reference reads only: reuse a successful response this long; any mutation clears all cached responses.
  cacheMs?: number;
};

export type ApiSessionInfo = {
  expiresAt: string;
  lastActivityAt: string;
  idleTimeoutSeconds: number;
};

const apiReadCooldowns = new Map<string, ReturnType<typeof createApiReadCooldown>>();
function apiReadCooldown(origin: string) {
  let cooldown = apiReadCooldowns.get(origin);
  if (!cooldown) { cooldown = createApiReadCooldown(); apiReadCooldowns.set(origin, cooldown); }
  return cooldown;
}

const reportClientError = createClientErrorReporter(
  (entry) => apiFetchOnce("/api/client-errors", { method: "POST", body: JSON.stringify(entry), suppressUnauthorizedSessionEvent: true }),
  (() => { try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; } })(),
);

// What was being sent, without its content: e.g. a 4.2 MB image/png upload.
function requestShape(body: unknown) {
  if (typeof FormData === "undefined" || !(body instanceof FormData)) return "";
  return [...body.values()].flatMap((value) => typeof File !== "undefined" && value instanceof File
    ? [`${value.type || "pa tip"} ${Math.round(value.size / 1024)} KB`] : []).join(", ");
}

// "Failed to fetch" means no answer reached the browser at all (connection dropped, blocked or the
// Worker was cut off). It is reported to the global error log and, for changes, shown in Albanian.
export async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  try {
    return await apiFetchWithoutReport<T>(path, options);
  } catch (error) {
    const method = (options.method || "GET").toUpperCase();
    if (isNetworkFailure(error) && !path.startsWith("/api/client-errors") && !options.signal?.aborted) {
      void reportClientError({ kind: "network", method, route: path, message: (error as Error).message,
        details: [requestShape(options.body), typeof navigator !== "undefined" && navigator.onLine === false ? "pajisja offline" : ""].filter(Boolean).join(" · ") });
      if (!["GET", "HEAD", "OPTIONS"].includes(method)) throw new Error(translateUi(NETWORK_FAILURE_MESSAGE,
        "The connection to the server dropped before an answer arrived. Check the internet and try again; your work is not lost. The error was logged for the administrator."));
    }
    throw error;
  }
}

async function apiFetchWithoutReport<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { retryTransientRead: retry, deduplicateRead, cacheMs, ...requestOptions } = options;
  const method = (options.method || "GET").toUpperCase();
  const requestPath = withElearningAcademicYear(path);
  const headers = new Headers(options.headers);
  if (currentViewAsId && !headers.has("X-View-As")) headers.set("X-View-As", currentViewAsId);
  if (currentActiveRole) headers.set("X-Active-Role", currentActiveRole);
  requestOptions.headers = headers;
  const read = (signal?: AbortSignal | null) => {
    const once = (sharedSignal?: AbortSignal | null) => apiFetchOnce<T>(path, { ...requestOptions, signal: sharedSignal }, requestPath);
    const send = (sharedSignal?: AbortSignal | null) => retry===false ? once(sharedSignal)
      : retryTransientRead(once,sharedSignal,retry?{}:{networkOnly:true,timeoutMs:0});
    if (deduplicateRead === false || requestPath.startsWith("/api/auth/") || options.body) return send(signal);
    const key = JSON.stringify([sessionTransport.originFor(requestPath), requestPath, options.token, [...headers].sort(),
      options.credentials, options.cache, options.mode, options.redirect, options.integrity, options.referrer, options.referrerPolicy,
      options.suppressUnauthorizedSessionEvent, retry ?? "network-only"]);
    return apiReads.read(key, send, signal);
  };
  if (method === "GET") {
    if (!cacheMs) return read(options.signal);
    const cacheKey = JSON.stringify([sessionTransport.originFor(requestPath), requestPath, options.token, [...headers].sort()]);
    const cached = apiResponses.get<T>(cacheKey);
    if (cached !== undefined) return cached;
    const value = await read(options.signal);
    apiResponses.set(cacheKey, value, cacheMs);
    return value;
  }
  if (["HEAD", "OPTIONS"].includes(method)) return apiFetchOnce<T>(path, requestOptions, requestPath);
  apiReads.invalidate();
  apiResponses.clear();
  try {
    const send=()=>apiFetchOnce<T>(path,requestOptions,requestPath);
    return await (isIdempotentEvidenceUpload(requestPath,method,options.body)?retryIdempotentEvidenceUpload(send):send());
  }
  finally { apiReads.invalidate(); apiResponses.clear(); }
}

async function apiFetchOnce<T>(path: string, options: Omit<ApiOptions, "retryTransientRead" | "deduplicateRead">, scopedPath?: string): Promise<T> {
  const { token: _token, suppressUnauthorizedSessionEvent, ...requestOptions } = options;
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (currentActiveRole) headers.set("X-Active-Role", currentActiveRole);
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const method = (options.method || "GET").toUpperCase();

  if (options.body && !headers.has("Content-Type") && !isFormData) {
    headers.set("Content-Type", "application/json");
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    headers.set("X-EDUBGT-Request", "1");
  }
  if (!scopedPath && currentViewAsId && !headers.has("X-View-As")) {
    headers.set("X-View-As", currentViewAsId);
  }
  const requestPath = scopedPath ?? withElearningAcademicYear(path);
  if (method === "GET" || method === "HEAD") apiReadCooldown(sessionTransport.originFor(requestPath)).check(requestPath);
  const { response, origin } = await sessionTransport.request(requestPath, { ...requestOptions, headers });
  const text = await response.text();
  const data = parseApiJson(text);
  const invalidJson = Boolean(text.trim()) && data === null && text.trim() !== "null";
  if (response.status === 429) {
    apiReadCooldown(origin).record(requestPath, response.headers.get("Retry-After"), invalidJson);
  }
  if (response.status === 503 && data && typeof data === "object" && "code" in data && data.code === "D1_BUSY") {
    apiReadCooldown(origin).record(requestPath, response.headers.get("Retry-After") || "3", true,
      new ApiRequestError(apiErrorMessage(503, data), 503, data));
  }
  if (response.ok && invalidJson) {
    throw new ApiRequestError(apiErrorMessage(502, null), 502);
  }

  if (!response.ok) {
    const message = apiErrorMessage(response.status, data);
    if (response.status === 401 && !suppressUnauthorizedSessionEvent) {
      markSessionExpired();
      if (typeof window !== "undefined") window.dispatchEvent(new Event(sessionExpiredEvent));
    }
    if(response.status===403 && !requestPath.startsWith("/api/permissions/effective") && typeof window!=="undefined")window.dispatchEvent(new Event("edubgt:permission-denied"));
    throw new ApiRequestError(message, response.status, data);
  }

  return data as T;
}

export type PlanSourceStatus = "pending" | "ready" | "partially_readable" | "clear_scan" | "unreadable" | "failed";
export type PlanSourceOrigin = "uploaded" | "btec_curriculum" | "teacher_material";

export type PlanSource = {
  id: string;
  versionId: string;
  scopeType: "course" | "lesson";
  originType: PlanSourceOrigin;
  title: string;
  author: string | null;
  editionLabel: string | null;
  language: string;
  status: PlanSourceStatus;
  active: boolean;
  versionNumber: number;
  fileName: string | null;
  contentType: string | null;
  fileSize: number;
  pageCount: number | null;
  readablePageCount: number | null;
  processingProgress?: { processedPages: number; totalPages: number; truncated: boolean };
  copyrightAcknowledged: boolean;
  visualAnalysisApproved?: boolean;
  createdAt: string;
  updatedAt: string;
};

type PlanSourceCourseScopeInput = {
  type: "course";
  schoolId?: string;
  teacherUserId: string;
  classId: string;
  subjectId: string;
  asProfessor?: boolean;
};

type PlanSourceLessonScopeInput = Omit<PlanSourceCourseScopeInput, "type"> & {
  type: "lesson";
  planTopicId: string;
  planItemKind: "topic" | "subtopic";
};

export type PlanSourceScopeInput = PlanSourceCourseScopeInput | PlanSourceLessonScopeInput;

function planSourceScopePayload(scope: PlanSourceScopeInput) {
  return {
    scopeType: scope.type,
    teacherUserId: scope.teacherUserId,
    classId: scope.classId,
    subjectId: scope.subjectId,
    ...(scope.type === "lesson" ? { planTopicId: scope.planTopicId, planItemKind: scope.planItemKind } : {}),
  };
}

function planSourcePath(path: string, scope: PlanSourceScopeInput, includeScope = false) {
  const query = new URLSearchParams();
  if (scope.schoolId) query.set("schoolId", scope.schoolId);
  if (scope.asProfessor) query.set("as", "professor");
  if (includeScope) {
    for (const [key, value] of Object.entries(planSourceScopePayload(scope))) query.set(key, value);
  }
  const encoded = query.toString();
  return encoded ? `${path}?${encoded}` : path;
}

function publicPlanSource(value: unknown): PlanSource {
  const row = value && typeof value === "object" ? value as Record<string, unknown> : {};
  return {
    id: String(row.id || ""),
    versionId: String(row.versionId || ""),
    scopeType: row.scopeType === "lesson" ? "lesson" : "course",
    originType: row.originType === "btec_curriculum" || row.originType === "teacher_material" ? row.originType : "uploaded",
    title: String(row.title || ""),
    author: typeof row.author === "string" ? row.author : null,
    editionLabel: typeof row.editionLabel === "string" ? row.editionLabel : null,
    language: String(row.language || "sq"),
    status: ["pending", "ready", "partially_readable", "clear_scan", "unreadable", "failed"].includes(String(row.status))
      ? row.status as PlanSourceStatus
      : "failed",
    active: row.active === true,
    versionNumber: Number(row.versionNumber) || 1,
    fileName: typeof row.fileName === "string" ? row.fileName : null,
    contentType: typeof row.contentType === "string" ? row.contentType : null,
    fileSize: Math.max(0, Number(row.fileSize) || 0),
    pageCount: row.pageCount == null ? null : Math.max(0, Number(row.pageCount) || 0),
    readablePageCount: row.readablePageCount == null ? null : Math.max(0, Number(row.readablePageCount) || 0),
    copyrightAcknowledged: row.copyrightAcknowledged === true,
    visualAnalysisApproved: row.visualAnalysisApproved === true,
    createdAt: String(row.createdAt || ""),
    updatedAt: String(row.updatedAt || ""),
  };
}

export async function listPlanSources(scope: PlanSourceScopeInput) {
  const response = await apiFetch<{ sources: unknown[] }>(planSourcePath("/api/plans/sources", scope, true));
  return (response.sources || []).map(publicPlanSource);
}

export type PlanBtecCurriculumDocument = Pick<BtecCurriculumDocument, "id" | "title" | "category" | "unitNumber" | "issueLabel">;

export async function listPlanBtecCurriculumDocuments(scope: PlanSourceScopeInput, signal?: AbortSignal) {
  const response = await apiFetch<{ documents: PlanBtecCurriculumDocument[] }>(
    planSourcePath("/api/plans/sources/btec-documents", scope, true),
    { signal, retryTransientRead: true },
  );
  if (!response || !Array.isArray(response.documents) || response.documents.some((document) =>
    !document || typeof document.id !== "string" || typeof document.title !== "string"
  )) throw new Error("Invalid BTEC document list response");
  return response.documents;
}

export type UploadPlanSourceInput = {
  file: File;
  sourceId?: string;
  title: string;
  author?: string;
  editionLabel?: string;
  language: string;
  copyrightNote?: string;
  copyrightAcknowledged: boolean;
  idempotencyKey?: string;
};

export async function uploadPlanSource(scope: PlanSourceScopeInput, input: UploadPlanSourceInput) {
  const body = new FormData();
  for (const [key, value] of Object.entries(planSourceScopePayload(scope))) body.set(key, value);
  body.set("file", input.file);
  if (input.sourceId) body.set("sourceId", input.sourceId);
  body.set("title", input.title);
  body.set("author", input.author || "");
  body.set("editionLabel", input.editionLabel || "");
  body.set("language", input.language || "sq");
  body.set("copyrightNote", input.copyrightNote || "");
  body.set("copyrightAcknowledged", String(input.copyrightAcknowledged));
  const idempotencyKey = input.idempotencyKey || crypto.randomUUID();
  const response = await apiFetch<{ source: unknown }>(planSourcePath("/api/plans/sources/upload", scope), {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body,
  });
  return publicPlanSource(response.source);
}

export type ReferencePlanSourceInput = {
  originType: Exclude<PlanSourceOrigin, "uploaded">;
  btecDocumentId?: string;
  teacherMaterialId?: string;
  title: string;
  author?: string;
  editionLabel?: string;
  language: string;
  copyrightNote?: string;
  copyrightAcknowledged: boolean;
  idempotencyKey?: string;
};

export async function referencePlanSource(scope: PlanSourceScopeInput, input: ReferencePlanSourceInput) {
  const idempotencyKey = input.idempotencyKey || crypto.randomUUID();
  const response = await apiFetch<{ source: unknown }>(planSourcePath("/api/plans/sources/reference", scope), {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ ...planSourceScopePayload(scope), ...input, idempotencyKey }),
  });
  return publicPlanSource(response.source);
}

export async function deletePlanSource(sourceId: string, scope: PlanSourceScopeInput) {
  await apiFetch<{ ok: true }>(planSourcePath(`/api/plans/sources/${encodeURIComponent(sourceId)}`, scope), { method: "DELETE" });
}

export async function downloadPlanSource(source: PlanSource, scope: PlanSourceScopeInput) {
  await downloadFile(
    planSourcePath(`/api/plans/sources/${encodeURIComponent(source.id)}/download`, scope),
    source.fileName || source.title || "burim",
  );
}

export type PlanSourcePagePreview = {
  pageNumber: number;
  status: PlanSourceStatus;
  charCount: number;
  contentHash: string | null;
};

export type PlanSourcePageContent = {
  pageNumber: number;
  status: PlanSourceStatus | null;
  charCount: number;
  text: string;
  regions: Array<{
    id: string | null;
    kind: string | null;
    text: string;
    rawHash: string | null;
    coordinates: unknown;
  }>;
  assets: Array<{
    id: string | null;
    kind: string | null;
    mimeType: string | null;
    sha256: string | null;
    coordinates: unknown;
  }>;
};

export type PlanSourceDetails = {
  id: string;
  title: string;
  author: string | null;
  editionLabel: string | null;
  language: string;
  copyrightNote: string | null;
  copyrightAcknowledged: boolean;
  createdAt: string;
  updatedAt: string;
  versions: Array<{
    id: string;
    number: number;
    status: PlanSourceStatus;
    fileName: string | null;
    contentType: string | null;
    fileSize: number;
    sha256: string;
    pageCount: number | null;
    readablePageCount: number | null;
    textLayerCoverage: number | null;
    active: boolean;
    createdAt: string;
    processing: {
      status: string;
      pipelineVersion: string | null;
      magicMimeType: string | null;
      magicValidationStatus: string | null;
      integrityStatus: string | null;
      encryptionStatus: string | null;
      visualApprovalStatus: string | null;
      indexSummary: Record<string, unknown>;
    } | null;
    pages: PlanSourcePagePreview[];
  }>;
};

export async function getPlanSourceDetails(sourceId: string, scope: PlanSourceScopeInput) {
  const response = await apiFetch<{ source: PlanSourceDetails }>(
    planSourcePath(`/api/plans/sources/${encodeURIComponent(sourceId)}/details`, scope, true),
  );
  return response.source;
}

export async function getPlanSourcePageContent(
  sourceId: string,
  versionId: string,
  pageNumber: number,
  scope: PlanSourceScopeInput,
) {
  const response = await apiFetch<{ page: PlanSourcePageContent; originalDownloadUrl: string }>(
    planSourcePath(`/api/plans/sources/${encodeURIComponent(sourceId)}/versions/${encodeURIComponent(versionId)}/pages/${encodeURIComponent(String(pageNumber))}`, scope, true),
  );
  return response.page;
}

export async function approvePlanSourceVisualAnalysis(
  sourceId: string,
  versionId: string,
  scope: PlanSourceScopeInput,
) {
  return apiFetch<{ ok: true; replayed: boolean }>(
    planSourcePath(`/api/plans/sources/${encodeURIComponent(sourceId)}/versions/${encodeURIComponent(versionId)}/visual-approval`, scope),
    { method: "POST", body: JSON.stringify({ approved: true }) },
  );
}

export async function activatePlanSourceVersion(
  sourceId: string,
  versionId: string,
  scope: PlanSourceScopeInput,
) {
  return apiFetch<{ ok: true }>(
    planSourcePath(`/api/plans/sources/${encodeURIComponent(sourceId)}/versions/${encodeURIComponent(versionId)}/activate`, scope),
    { method: "POST" },
  );
}

export async function retryPlanSourceProcessing(
  sourceId: string,
  versionId: string,
  scope: PlanSourceScopeInput,
) {
  return apiFetch<{ ok: true; processing: "queued" }>(
    planSourcePath(`/api/plans/sources/${encodeURIComponent(sourceId)}/versions/${encodeURIComponent(versionId)}/processing/retry`, scope),
    { method: "POST" },
  );
}

export async function downloadPlanSourceVersion(
  sourceId: string,
  version: PlanSourceDetails["versions"][number],
  scope: PlanSourceScopeInput,
) {
  await downloadFile(
    planSourcePath(`/api/plans/sources/${encodeURIComponent(sourceId)}/versions/${encodeURIComponent(version.id)}/download`, scope, true),
    version.fileName || `burim-v${version.number}`,
  );
}

export type PlanWorksheetArchive = {
  id: string;
  fileName: string;
  fileSize: number;
  sha256: string;
  createdAt: string;
  entries: Array<{
    id: string;
    path: string;
    fileName: string;
    contentType: string;
    extension: string;
    fileSize: number;
    sha256: string;
  }>;
};

export async function listPlanWorksheetArchives(scope: Extract<PlanSourceScopeInput, { type: "lesson" }>) {
  const response = await apiFetch<{ archives: PlanWorksheetArchive[] }>(
    planSourcePath(`/api/plans/lessons/${encodeURIComponent(scope.planTopicId)}/worksheets`, scope, true),
  );
  return response.archives || [];
}

export async function uploadPlanWorksheetArchive(
  scope: Extract<PlanSourceScopeInput, { type: "lesson" }>,
  file: File,
  idempotencyKey: string = crypto.randomUUID(),
) {
  const body = new FormData();
  for (const [key, value] of Object.entries(planSourceScopePayload(scope))) body.set(key, value);
  body.set("file", file);
  const response = await apiFetch<{ archive: PlanWorksheetArchive }>(
    planSourcePath(`/api/plans/lessons/${encodeURIComponent(scope.planTopicId)}/worksheets`, scope),
    { method: "POST", headers: { "Idempotency-Key": idempotencyKey }, body },
  );
  return response.archive;
}

export async function downloadPlanWorksheetEntry(
  entry: PlanWorksheetArchive["entries"][number],
  scope: Extract<PlanSourceScopeInput, { type: "lesson" }>,
) {
  await downloadFile(
    planSourcePath(`/api/plans/worksheet-entries/${encodeURIComponent(entry.id)}/download`, scope, true),
    entry.fileName,
  );
}

export type PlanOutputType = "presentation" | "lesson_plan" | "teaching_material" | "in_class_activity" | "homework_assignment" | "workbook_check" | "project" | "emergency_lesson_plan" | "custom";
export type PlanSourceMode = "exact" | "same_examples" | "based_on" | "ai_only" | "combined";
export type PlanOutputStatus = "draft" | "in_review" | "approved" | "published" | "superseded" | "source_conflict" | "syllabus_conflict";

export type PlanActivityCatalogItem = {
  id: string;
  schoolId: string | null;
  title: string;
  description: string;
  collaborationMode: "individual" | "pairs" | "groups" | "mixed";
  durationMinutes: number | null;
  topicTags: string[];
  learningAims: string[];
  materials: string[];
  expectedEvidence: string;
  difficulty: "foundation" | "intermediate" | "advanced" | null;
  prerequisites: string;
  worksOffline: boolean | null;
  substituteSuitable: boolean | null;
  checkMethod: string;
  sourceLabel: string;
  sourceReference: string | null;
  status: "active" | "archived";
  createdAt: string;
  updatedAt: string;
};

export async function listPlanActivityCatalog(scope: PlanSourceScopeInput) {
  const response = await apiFetch<{ items: PlanActivityCatalogItem[] }>(
    planSourcePath("/api/plans/activity-catalog", scope, true),
  );
  return response.items || [];
}

export type PlanOutputConfiguration = {
  language: string;
  level: "foundation" | "intermediate" | "advanced";
  difficulty: "foundation" | "intermediate" | "advanced";
  durationMinutes: number;
  equipment: string[];
  customPrompt: string;
  learningAim: string | null;
  differentiation: string;
  slideCount: number;
};

export type PlanGenerationRequest = {
  planTopicId: string;
  planItemKind: "topic" | "subtopic";
  outputs: PlanOutputType[];
  sourceSelections: Array<{
    sourceId: string;
    versionId: string;
    mode: PlanSourceMode;
    selectedPages?: number[];
    selectedRegions?: Array<{ pageNumber: number; x: number; y: number; width: number; height: number }>;
  }>;
  language: string;
  level: "foundation" | "intermediate" | "advanced";
  durationMinutes: number;
  equipment: string[];
  customPrompt: string;
  customOutputLabel: string | null;
  learningAim: string | null;
  worksheet: { sourceId: string; versionId: string; relevanceConfirmed: boolean } | null;
  outputModes?: Partial<Record<PlanOutputType, PlanSourceMode>>;
  sectionModeOverrides?: Array<{
    outputType: PlanOutputType;
    sectionId: string;
    mode: PlanSourceMode;
    sourceIds: string[];
  }>;
  generationMode?: "all_at_once" | "one_by_one";
  outputConfigurations?: Partial<Record<PlanOutputType, PlanOutputConfiguration>>;
  activityFormat?: "individual" | "pairs" | "groups" | "mixed";
  activityCatalogItemId?: string | null;
  activitySelectionMode?: "suggested" | "catalog" | "custom";
  customActivity?: string;
  differentiation?: string;
  slideCount?: number;
  difficulty?: "foundation" | "intermediate" | "advanced";
  templateVersionId?: string | null;
  budgetApprovalId?: string | null;
};

export type PlanActivitySuggestions = {
  method: "catalog_rules";
  lessonTitle: string;
  items: Array<{ catalogItemId: string; title: string; reason: string; warnings: string[] }>;
};

export async function getPlanActivitySuggestions(
  scope: Extract<PlanSourceScopeInput, { type: "lesson" }>,
  input: { durationMinutes: number; learningAim: string; customPrompt: string; equipment: string[]; sourceIds: string[]; language: string },
) {
  return apiFetch<PlanActivitySuggestions>(planSourcePath("/api/plans/activity-suggestions", scope), {
    method: "POST", body: JSON.stringify({ ...input, ...planSourceScopePayload(scope) }),
  });
}

export type PlanGenerationPreview = {
  outputCount: number;
  sourcePageCount: number;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  estimatedTotalTokens: number;
  estimatedCostUsd: number;
  costBasis: "historical_provider_model" | "fallback_budget_rate";
  blockingIssues: string[];
  warnings: string[];
};

export type PlanGenerationMutationResult =
  | { recipeId: string; jobId: string; status: PlanGenerationJob["status"] }
  | {
      approvalRequired: true;
      approvalRequest: { id: string; status: string; replayed: boolean };
      message: string;
    };

export type PlanGenerationPlan = {
  request: PlanGenerationRequest;
  topic: Record<string, unknown>;
  sources: Array<{
    sourceId: string;
    versionId: string;
    mode: PlanSourceMode;
    authority: string;
    title: string;
    status: PlanSourceStatus;
    pageCount: number | null;
    copyrightAcknowledged: boolean;
    selectedPages: number[];
    selectedRegions: Array<{ pageNumber: number; x: number; y: number; width: number; height: number }>;
  }>;
  continuityEntryCount: number;
  preview: PlanGenerationPreview;
  provider: string;
  model: string;
  canGenerate: boolean;
  outputPlans?: Array<{
    outputType: PlanOutputType;
    label?: string;
    mode: PlanSourceMode;
    estimatedTokens?: number;
    sections: Array<{ id: string; title?: string; mode: PlanSourceMode }>;
  }>;
  continuitySummary?: {
    entryCount: number;
    entries: Array<{ entryId: string; entryVersion: number; outputVersionId: string; masterLessonNumber: number }>;
    warnings: string[];
  };
  template?: Partial<Record<"document" | "presentation", {
    id: string;
    kind: "document" | "presentation";
    versionNumber: number;
    sha256: string;
    placeholderCapacity: number;
  }>>;
};

export type PlanGeneratedOutput = {
  id: string;
  type: PlanOutputType;
  key: string;
  customLabel: string | null;
  version: {
    id: string;
    number: number;
    status: PlanOutputStatus;
    title: string;
    content: { sections?: Array<{ id: string; title: string; body: string; exactFragments?: Array<{ text: string }> }> };
    provenanceSummary: Record<string, unknown>;
    // AI content completed after the run's five-minute limit; saved instead of discarded.
    generatedAfterTimeLimit?: boolean;
    createdAt: string;
    updatedAt: string;
    files: Array<{
      id: string;
      format: "docx" | "pptx" | "pdf" | "worksheet";
      fileName: string;
      contentType: string;
      fileSize: number;
      sha256: string;
      templateVersion: string;
    }>;
  };
  provenance: Array<{
    authority: string;
    sourceMode: PlanSourceMode;
    sourceId: string | null;
    sourceVersionId: string | null;
    referenceType: string;
    referenceId: string | null;
    pageNumber: number | null;
    citation: Record<string, unknown>;
  }>;
};

export type PlanGenerationJob = {
  id: string;
  recipeId: string;
  status: "queued" | "running" | "partial" | "completed" | "failed" | "cancelled" | "stale";
  completedOutputCount: number;
  expectedOutputCount: number;
  error: { code: string; message: string | null } | null;
  planTopicId: string;
  planItemKind: "topic" | "subtopic";
  preview: PlanGenerationPreview;
  queuedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  runDeadlineAt?: number | null;
  generationMode?: "all_at_once" | "one_by_one";
  outputOrder?: PlanOutputType[];
  remainingOutputKeys?: string[];
  outputs: PlanGeneratedOutput[];
};

export type PlanOutputHistoryItem = PlanGeneratedOutput;

export type PlanOutputDiff = {
  fromVersionId: string;
  toVersionId: string;
  titleChanged: boolean;
  oldTitle: string;
  newTitle: string;
  sections: Array<{
    id: string;
    change: "added" | "removed" | "changed" | "unchanged";
    before: { id: string; title: string; body: string } | null;
    after: { id: string; title: string; body: string } | null;
  }>;
};

export type PlanDailyLessonQuota = {
  date: string;
  timezone: string;
  weeklyLessonCount: number;
  limitLessonCount: number | null;
  usedLessonCount: number;
  remainingLessonCount: number | null;
  resetsAt: string;
  source: "timetable" | "requirements" | "unavailable";
};

export async function getPlanDailyLessonQuota(scope: PlanSourceScopeInput) {
  const response = await apiFetch<{ quota: PlanDailyLessonQuota }>(
    planSourcePath("/api/plans/daily-lesson-quota", { ...scope, type: "course" }, true),
  );
  return response.quota;
}

export async function getPlanCourseDefaults(scope: PlanSourceScopeInput) {
  const response = await apiFetch<{ defaults: Partial<PlanGenerationRequest> | null }>(
    planSourcePath("/api/plans/course-defaults", { ...scope, type: "course" }, true),
  );
  return response.defaults;
}

export async function listPlanPersonalInstructions(schoolId: string) {
  const response = await apiFetch<{ instructions: PlanPersonalInstruction[] }>(
    withSchoolPath("/api/plans/personal-instructions", schoolId),
  );
  return response.instructions;
}

export async function savePlanPersonalInstruction(schoolId: string, id: string, input: { name: string; instruction: string }) {
  const response = await apiFetch<{ instruction: PlanPersonalInstruction }>(
    withSchoolPath(`/api/plans/personal-instructions/${encodeURIComponent(id)}`, schoolId),
    { method: "PUT", body: JSON.stringify(input) },
  );
  return response.instruction;
}

export async function deletePlanPersonalInstruction(schoolId: string, id: string) {
  await apiFetch<{ ok: true }>(withSchoolPath(`/api/plans/personal-instructions/${encodeURIComponent(id)}`, schoolId), { method: "DELETE" });
}

export type PlanLessonTimetableContext = {
  timetableDurationMinutes: number | null;
  timetableLessonCount: number;
  occurrenceDate: string | null;
};

export async function getPlanLessonTimetableContext(scope: Extract<PlanSourceScopeInput, { type: "lesson" }>) {
  return apiFetch<PlanLessonTimetableContext>(
    planSourcePath(`/api/plans/lessons/${encodeURIComponent(scope.planTopicId)}/timetable-context`, scope, true),
  );
}

export async function savePlanCourseDefaults(scope: PlanSourceScopeInput, defaults: Partial<PlanGenerationRequest>) {
  const response = await apiFetch<{ defaults: Partial<PlanGenerationRequest> }>(
    planSourcePath("/api/plans/course-defaults", { ...scope, type: "course" }),
    { method: "PUT", body: JSON.stringify({ ...planSourceScopePayload({ ...scope, type: "course" }), defaults }) },
  );
  return response.defaults;
}

export async function previewPlanGeneration(scope: Extract<PlanSourceScopeInput, { type: "lesson" }>, request: PlanGenerationRequest) {
  const response = await apiFetch<{ plan: PlanGenerationPlan }>(
    planSourcePath(`/api/plans/lessons/${encodeURIComponent(scope.planTopicId)}/generation-plan`, scope),
    { method: "POST", body: JSON.stringify({ ...request, ...planSourceScopePayload(scope) }) },
  );
  return response.plan;
}

export async function startPlanGeneration(
  scope: Extract<PlanSourceScopeInput, { type: "lesson" }>,
  request: PlanGenerationRequest,
  idempotencyKey: string = crypto.randomUUID(),
) {
  const response = await apiFetch<
    | { job: Extract<PlanGenerationMutationResult, { jobId: string }> }
    | Extract<PlanGenerationMutationResult, { approvalRequired: true }>
  >(
    planSourcePath(`/api/plans/lessons/${encodeURIComponent(scope.planTopicId)}/generations`, scope),
    { method: "POST", headers: { "Idempotency-Key": idempotencyKey }, body: JSON.stringify({ ...request, ...planSourceScopePayload(scope) }) },
  );
  return "job" in response ? response.job : response;
}

export async function getPlanGenerationJob(jobId: string, scope: Extract<PlanSourceScopeInput, { type: "lesson" }>) {
  const response = await apiFetch<{ job: PlanGenerationJob }>(
    planSourcePath(`/api/plans/generations/${encodeURIComponent(jobId)}`, scope, true),
  );
  return response.job;
}

export async function getLatestPlanGenerationJob(
  scope: Extract<PlanSourceScopeInput, { type: "lesson" }>,
  outputType?: PlanOutputType | null,
) {
  const path = planSourcePath(`/api/plans/lessons/${encodeURIComponent(scope.planTopicId)}/generations/latest`, scope, true);
  const outputQuery = outputType ? `${path.includes("?") ? "&" : "?"}outputType=${encodeURIComponent(outputType)}` : "";
  const response = await apiFetch<{ job: PlanGenerationJob | null }>(
    `${path}${outputQuery}`,
  );
  return response.job;
}

export async function retryPlanGeneration(
  jobId: string,
  scope: Extract<PlanSourceScopeInput, { type: "lesson" }>,
  outputKeys?: string[],
) {
  return apiFetch<{ ok: true }>(
    planSourcePath(`/api/plans/generations/${encodeURIComponent(jobId)}/retry`, scope, true),
    { method: "POST", body: outputKeys?.length ? JSON.stringify({ outputKeys }) : undefined },
  );
}

export async function cancelPlanGeneration(jobId: string, scope: Extract<PlanSourceScopeInput, { type: "lesson" }>) {
  return apiFetch<{ ok: true }>(
    planSourcePath(`/api/plans/generations/${encodeURIComponent(jobId)}/cancel`, scope, true),
    { method: "POST" },
  );
}

export async function transitionPlanOutputVersion(versionId: string, status: PlanOutputStatus, scope: PlanSourceScopeInput) {
  return apiFetch<{ ok: true; status: PlanOutputStatus }>(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/status`, scope),
    { method: "POST", body: JSON.stringify({ status }) },
  );
}

export async function deletePlanOutputMaterial(versionId: string, scope: PlanSourceScopeInput) {
  return apiFetch<{ ok: true; supersededVersions: number }>(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/material`, scope),
    { method: "DELETE" },
  );
}

export type PlanMaterialVisibility = {
  mode: "private" | "students" | "scheduled";
  visibleFrom: string | null;
  published: boolean;
  teacherOnly?: boolean;
};

export async function getPlanMaterialVisibility(origin: "uploaded" | "generated", id: string, scope: PlanSourceScopeInput) {
  return apiFetch<{ visibility: PlanMaterialVisibility }>(planSourcePath(`/api/plans/visibility/${origin}/${encodeURIComponent(id)}`, scope));
}

export async function setPlanMaterialVisibility(origin: "uploaded" | "generated", id: string, scope: PlanSourceScopeInput, setting: Pick<PlanMaterialVisibility, "mode" | "visibleFrom">) {
  return apiFetch<{ visibility: PlanMaterialVisibility }>(planSourcePath(`/api/plans/visibility/${origin}/${encodeURIComponent(id)}`, scope), {
    method: "PATCH", body: JSON.stringify(setting),
  });
}

export type PlanPublicationDetails = {
  visibility: PlanMaterialVisibility;
  publication?: { status: string; replacesPublished: boolean; lesson: { startAt: string; endAt: string; timeZone: string } | null };
};

export async function getPlanPublicationDetails(origin: "uploaded" | "generated", id: string, scope: PlanSourceScopeInput) {
  const path = planSourcePath(`/api/plans/visibility/${origin}/${encodeURIComponent(id)}`, scope);
  return apiFetch<PlanPublicationDetails>(`${path}${path.includes("?") ? "&" : "?"}publication=1`);
}

export async function schedulePlanMaterialAtLesson(origin: "uploaded" | "generated", id: string, scope: PlanSourceScopeInput, expectedStartAt: string) {
  return apiFetch<{ visibility: PlanMaterialVisibility }>(planSourcePath(`/api/plans/visibility/${origin}/${encodeURIComponent(id)}`, scope), {
    method: "PATCH", body: JSON.stringify({ mode: "lesson_start", expectedStartAt }),
  });
}

export async function publishUploadedPlanMaterial(id: string, scope: Extract<PlanSourceScopeInput, { type: "lesson" }>) {
  return apiFetch(planSourcePath(`/api/professor/plan/${encodeURIComponent(scope.planTopicId)}/materials/${encodeURIComponent(id)}/publication`, scope), {
    method: "PATCH", body: JSON.stringify({ published: true }),
  });
}

export async function renamePlanOutputFiles(
  versionId: string,
  fileName: string,
  scope: PlanSourceScopeInput,
) {
  return apiFetch<{ ok: true; fileName: string }>(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/file-name`, scope),
    { method: "PATCH", body: JSON.stringify({ fileName }) },
  );
}

export async function downloadPlanOutputFile(
  file: PlanGeneratedOutput["version"]["files"][number],
  scope: PlanSourceScopeInput,
) {
  await downloadFile(
    planSourcePath(`/api/plans/output-files/${encodeURIComponent(file.id)}/download`, scope),
    file.fileName,
  );
}

export async function downloadPlanOutputFilesArchive(
  outputs: PlanGeneratedOutput[],
  scope: PlanSourceScopeInput,
  fileName: string,
  uploaded: Array<{ id: string; fileName: string }> = [],
) {
  const { createMaterialArchive } = await import("./plan-material-archive.ts");
  const entries = [
    ...outputs.flatMap((output) => output.version.files.map((file) => ({
      folder: `generated-${output.version.id}`,
      fileName: file.fileName,
      path: planSourcePath(`/api/plans/output-files/${encodeURIComponent(file.id)}/download`, scope),
    }))),
    ...uploaded.map((file) => ({
      folder: `uploaded-${file.id}`,
      fileName: file.fileName,
      path: planSourcePath(`/api/elearning/plan/materials/${encodeURIComponent(file.id)}`, scope),
    })),
  ];
  const bytes = await createMaterialArchive(entries, async (path) => {
    const response = await fetch(`${API_URL}${path}`, viewAsInit());
    if (!response.ok) throw new Error(translateUi("Një nga skedarët nuk u shkarkua.", "One of the files could not be downloaded."));
    return new Uint8Array(await response.arrayBuffer());
  });
  await downloadResponseBlob(new Blob([new Uint8Array(bytes)], { type: "application/zip" }), fileName);
}

export async function fetchPlanOutputFilePreviewUrl(
  file: PlanGeneratedOutput["version"]["files"][number],
  scope: PlanSourceScopeInput,
) {
  const response = await fetch(
    `${API_URL}${planSourcePath(`/api/plans/output-files/${encodeURIComponent(file.id)}/download`, scope)}`,
    viewAsInit(),
  );
  if (!response.ok) throw new Error(translateUi("Pamja reale e dokumentit nuk u ngarkua.", "The document preview could not be loaded."));
  const blob = await response.blob();
  if (blob.type !== "application/pdf") throw new Error(translateUi("Formati i preview-t nuk është PDF.", "The preview is not in PDF format."));
  return URL.createObjectURL(blob);
}

export type PlanMaterialReview = {
  materialId: string;
  materialType: string;
  outputType: PlanOutputType;
  title: string;
  fileName: string;
  publicationStatus: "private" | "published";
  editVersion: number;
  content: {
    sections: Array<{ id: string; title: string; body: string }>;
  } | null;
  previewAvailable: boolean;
};

export async function preparePlanMaterialReview(
  materialId: string,
  scope: Extract<PlanSourceScopeInput, { type: "lesson" }>,
) {
  const response = await apiFetch<{ review: PlanMaterialReview }>(
    planSourcePath(`/api/plans/materials/${encodeURIComponent(materialId)}/review`, scope),
    { method: "POST" },
  );
  return response.review;
}

export async function updatePlanMaterialReview(
  materialId: string,
  scope: Extract<PlanSourceScopeInput, { type: "lesson" }>,
  input: { title: string; editVersion: number; content: NonNullable<PlanMaterialReview["content"]> },
) {
  const response = await apiFetch<{ review: PlanMaterialReview }>(
    planSourcePath(`/api/plans/materials/${encodeURIComponent(materialId)}/review`, scope),
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return response.review;
}

export async function fetchPlanMaterialPreviewUrl(
  materialId: string,
  scope: Extract<PlanSourceScopeInput, { type: "lesson" }>,
) {
  const response = await fetch(
    `${API_URL}${planSourcePath(`/api/plans/materials/${encodeURIComponent(materialId)}/review/preview`, scope)}`,
    viewAsInit(),
  );
  if (!response.ok) throw new Error(translateUi("Preview i materialit nuk u ngarkua.", "The material preview could not be loaded."));
  const blob = await response.blob();
  if (blob.type !== "application/pdf") throw new Error(translateUi("Formati i preview-t nuk është PDF.", "The preview is not in PDF format."));
  return URL.createObjectURL(blob);
}

export type PlanWorkbookReminder = {
  id: string;
  cycleNumber: number;
  completedLessonThreshold: number;
  status: "pending" | "checked" | "recorded" | "snoozed";
  note: string;
  snoozedUntil: string | null;
  actionedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function listPlanWorkbookReminders(scope: PlanSourceScopeInput) {
  return apiFetch<{ completedLessonCount: number; nextThreshold: number; reminders: PlanWorkbookReminder[] }>(
    planSourcePath("/api/plans/workbook-reminders", { ...scope, type: "course" }, true),
  );
}

export async function updatePlanWorkbookReminder(
  reminderId: string,
  scope: PlanSourceScopeInput,
  input: { status: "checked" | "recorded" | "snoozed"; note: string; snoozedUntil: string | null },
) {
  return apiFetch<{ ok: true }>(
    planSourcePath(`/api/plans/workbook-reminders/${encodeURIComponent(reminderId)}`, scope),
    { method: "PATCH", body: JSON.stringify(input) },
  );
}

export type PlanUsageTotals = {
  usedCents: number;
  reservedCents?: number;
  committedCents?: number;
  totalTokens: number;
  requestCount: number;
};

export type PlanUsage = {
  month: string;
  startsAt: string;
  endsAt: string;
  school: PlanUsageTotals;
  user: PlanUsageTotals;
};

export type PlanBudgetPolicy = {
  schoolId: string;
  monthlyHardLimitCents: number | null;
  monthlySoftLimitPercent: number;
  perUserMonthlyLimitCents: number | null;
  approvalThresholdCents: number | null;
  enforcementMode: "monitor" | "soft" | "hard";
  updatedAt: string | null;
};

export type PlanUsageSummary = {
  usage: PlanUsage;
  policy: PlanBudgetPolicy;
};

export type PlanBudgetApproval = {
  id: string;
  requestedByUserId: string;
  requestedByName: string;
  estimatedCostCents: number;
  requestSummary: Record<string, unknown>;
  status: string;
  reviewNote: string;
  createdAt: string;
  reviewedAt: string | null;
  expiresAt: string | null;
};

export async function getPlanUsage(scope: PlanSourceScopeInput) {
  return apiFetch<PlanUsageSummary>(planSourcePath("/api/plans/usage", { ...scope, type: "course" }, true));
}

export async function getPlanBudgetPolicy(schoolId: string) {
  return apiFetch<{ policy: PlanBudgetPolicy; approvals: PlanBudgetApproval[] }>(`/api/plans/budget-policy?schoolId=${encodeURIComponent(schoolId)}`);
}

export async function updatePlanBudgetPolicy(schoolId: string, policy: PlanBudgetPolicy) {
  return apiFetch<{ policy: PlanBudgetPolicy }>(`/api/plans/budget-policy?schoolId=${encodeURIComponent(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify({
      monthlyHardLimitCents: policy.monthlyHardLimitCents,
      monthlySoftLimitPercent: policy.monthlySoftLimitPercent,
      perUserMonthlyLimitCents: policy.perUserMonthlyLimitCents,
      approvalThresholdCents: policy.approvalThresholdCents,
      enforcementMode: policy.enforcementMode,
    }),
  });
}

export async function reviewPlanBudgetApproval(
  schoolId: string,
  approvalId: string,
  decision: "approved" | "rejected",
  note: string,
) {
  return apiFetch<{ ok: true }>(
    `/api/plans/budget-approvals/${encodeURIComponent(approvalId)}?schoolId=${encodeURIComponent(schoolId)}`,
    { method: "PATCH", body: JSON.stringify({ decision, note }) },
  );
}

export type PlanSubstituteSessionOption = {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  substituteTeacherId: string | null;
  substituteTeacherName: string;
};

export async function listEligiblePlanSubstituteSessions(scope: PlanSourceScopeInput) {
  const response = await apiFetch<{ sessions: PlanSubstituteSessionOption[] }>(
    planSourcePath("/api/plans/substitute-sessions", { ...scope, type: "course" }, true),
  );
  return response.sessions || [];
}

export async function sendPlanOutputToSubstitute(
  versionId: string,
  sessionId: string,
  scope: PlanSourceScopeInput,
) {
  return apiFetch<{ handoffId: string; replayed: boolean }>(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/substitute-handoff`, scope),
    { method: "POST", body: JSON.stringify({ sessionId }) },
  );
}

export type PlanSubstituteHandoff = {
  id: string;
  outputVersionId: string;
  sessionId: string;
  sessionTitle: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  sessionStatus: string;
  destination: {
    primaryTeacherId: string | null;
    primaryTeacherName: string | null;
    secondaryTeacherId: string | null;
    secondaryTeacherName: string | null;
  };
  sentAt: string;
  stale: boolean;
  newerVersionNumber: number | null;
};

export type SubstituteEmergencyPlanHandoff = {
  id: string;
  sentAt: string;
  title: string;
  content: { sections?: Array<{ id: string; title: string; body: string }> };
  lesson: { lessonNumber: number; topic: string; subtopic: string };
  files: Array<{
    id: string; format: string; fileName: string; contentType: string;
    fileSize: number; sha256: string;
  }>;
  materials: Array<{
    id: string; materialType: string; title: string; fileName: string;
    contentType: string | null; fileSize: number; sha256: string | null;
    publishedAt: string | null;
  }>;
};

export async function getSubstituteEmergencyPlan(token: string, schoolId: string, sessionId: string) {
  const response = await apiFetch<{ handoff: SubstituteEmergencyPlanHandoff }>(
    `/api/substitute/sessions/${encodeURIComponent(sessionId)}/emergency-plan?schoolId=${encodeURIComponent(schoolId)}`,
    { token },
  );
  return {
    ...response.handoff,
    files: response.handoff.files || [],
    materials: response.handoff.materials || [],
  };
}

export async function listPlanOutputSubstituteHandoffs(versionId: string, scope: PlanSourceScopeInput) {
  const response = await apiFetch<{ handoffs: PlanSubstituteHandoff[] }>(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/substitute-handoffs`, scope, true),
  );
  return response.handoffs || [];
}

export type PlanOutputTemplate = {
  id: string;
  kind: "document" | "presentation";
  versionNumber: number;
  name: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  sha256: string;
  placeholderCapacity: number;
  isActive: boolean;
  createdAt: string;
  activatedAt: string | null;
};

export async function listPlanOutputTemplates(schoolId: string) {
  const response = await apiFetch<{ templates: PlanOutputTemplate[] }>(`/api/plans/templates?schoolId=${encodeURIComponent(schoolId)}`);
  return response.templates || [];
}

export async function uploadPlanOutputTemplate(
  schoolId: string,
  name: string,
  file: File,
  idempotencyKey: string = crypto.randomUUID(),
) {
  const body = new FormData();
  body.set("name", name);
  body.set("file", file);
  const response = await apiFetch<{ templates: PlanOutputTemplate[] }>(`/api/plans/templates?schoolId=${encodeURIComponent(schoolId)}`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body,
  });
  return response.templates || [];
}

export async function activatePlanOutputTemplate(schoolId: string, templateId: string) {
  return apiFetch<{ ok: true }>(`/api/plans/templates/${encodeURIComponent(templateId)}/activate?schoolId=${encodeURIComponent(schoolId)}`, { method: "POST" });
}

export async function downloadPlanOutputTemplate(template: PlanOutputTemplate, schoolId: string) {
  await downloadFile(
    `/api/plans/templates/${encodeURIComponent(template.id)}/download?schoolId=${encodeURIComponent(schoolId)}`,
    template.fileName,
  );
}

export async function applyActivePlanOutputTemplate(
  versionId: string,
  scope: PlanSourceScopeInput,
  idempotencyKey: string = crypto.randomUUID(),
) {
  return apiFetch<{ version: { versionId: string; versionNumber: number }; replayed: boolean }>(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/apply-active-template`, scope),
    { method: "POST", headers: { "Idempotency-Key": idempotencyKey } },
  );
}

export async function updateDraftPlanOutput(
  versionId: string,
  scope: PlanSourceScopeInput,
  input: { title: string; sections: Array<{ id: string; title: string; body: string }> },
) {
  return apiFetch<{ ok: true }>(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}`, scope),
    { method: "PATCH", body: JSON.stringify(input) },
  );
}

export async function listPlanOutputHistory(scope: Extract<PlanSourceScopeInput, { type: "lesson" }>) {
  const response = await apiFetch<{ history: Array<{
    outputId: string;
    outputType: PlanOutputType;
    outputKey: string;
    customLabel: string | null;
    version: PlanGeneratedOutput["version"];
  }> }>(
    planSourcePath(`/api/plans/lessons/${encodeURIComponent(scope.planTopicId)}/output-history`, scope, true),
  );
  return (response.history || []).map((item): PlanOutputHistoryItem => ({
    id: item.outputId,
    type: item.outputType,
    key: item.outputKey,
    customLabel: item.customLabel,
    version: item.version,
    provenance: [],
  }));
}

export async function getPlanOutputDiff(
  versionId: string,
  againstVersionId: string,
  scope: PlanSourceScopeInput,
) {
  const path = planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/diff`, scope, true);
  const separator = path.includes("?") ? "&" : "?";
  const response = await apiFetch<{ diff: PlanOutputDiff }>(`${path}${separator}againstVersionId=${encodeURIComponent(againstVersionId)}`);
  return response.diff;
}

export type PlanOutputCopyTargets = {
  sourceRevision: string;
  targets: Array<{
    classId: string; classLabel: string; approved: boolean;
    lessons: Array<{ planTopicId: string; planItemKind: "topic" | "subtopic"; title: string;
      lessonNumber: number; lessonDate: string | null; suggested: boolean }>;
  }>;
};

export type PlanOutputCopyTarget = {
  classId: string; planTopicId: string; planItemKind: "topic" | "subtopic"; sourceRevision: string;
};

export async function listPlanOutputCopyTargets(versionId: string, scope: PlanSourceScopeInput) {
  return apiFetch<PlanOutputCopyTargets>(planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/copy-targets`, scope));
}

export async function copyPlanOutputVersion(versionId: string, scope: PlanSourceScopeInput,
  target: PlanOutputCopyTarget, idempotencyKey: string,
) {
  return apiFetch<{ outputId: string; versionId: string; jobId: string; replayed: boolean }>(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/copy`, scope),
    { method: "POST", headers: { "Idempotency-Key": idempotencyKey }, body: JSON.stringify(target) },
  );
}

export type PlanMaterialCopySource = { origin: "generated" | "upload"; id: string };

export async function listPlanMaterialCopyTargets(material: PlanMaterialCopySource, scope: PlanSourceScopeInput) {
  if (material.origin === "generated") return listPlanOutputCopyTargets(material.id, scope);
  return apiFetch<PlanOutputCopyTargets>(planSourcePath(`/api/plans/uploaded-materials/${encodeURIComponent(material.id)}/copy-targets`, scope));
}

export async function copyPlanMaterial(material: PlanMaterialCopySource, scope: PlanSourceScopeInput,
  target: PlanOutputCopyTarget, idempotencyKey: string,
): Promise<{ replayed: boolean }> {
  if (material.origin === "generated") return copyPlanOutputVersion(material.id, scope, target, idempotencyKey);
  return apiFetch<{ materialId: string; replayed: boolean }>(
    planSourcePath(`/api/plans/uploaded-materials/${encodeURIComponent(material.id)}/copy`, scope),
    { method: "POST", headers: { "Idempotency-Key": idempotencyKey }, body: JSON.stringify(target) },
  );
}

export async function getPlanCopyUpdates(material: PlanMaterialCopySource, scope: PlanSourceScopeInput) {
  return apiFetch<import('../../shared/plan-material-copy-updates.ts').PlanCopyUpdatesView>(
    planSourcePath(`/api/plans/copy-updates/${material.origin}/${encodeURIComponent(material.id)}`, scope),
  );
}

export async function applyPlanCopyUpdate(material: PlanMaterialCopySource, scope: PlanSourceScopeInput, copyId: string,
  request: import('../../shared/plan-material-copy-updates.ts').PlanCopyUpdateRequest, idempotencyKey: string,
) {
  return apiFetch<import('../../shared/plan-material-copy-updates.ts').PlanCopyUpdateResult>(
    planSourcePath(`/api/plans/copy-updates/${material.origin}/${encodeURIComponent(material.id)}/${encodeURIComponent(copyId)}`, scope),
    { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(request) },
  );
}

export async function downloadPlanUploadedCopyEdition(materialId: string, title: string, scope: PlanSourceScopeInput) {
  await downloadFile(planSourcePath(`/api/elearning/plan/materials/${encodeURIComponent(materialId)}`, scope), title);
}

export async function returnPlanOutputToDraft(
  versionId: string,
  scope: PlanSourceScopeInput,
  idempotencyKey: string = crypto.randomUUID(),
) {
  return apiFetch<{ version: { id?: string; versionId?: string; versionNumber?: number }; replayed: boolean }>(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/return-to-draft`, scope),
    { method: "POST", headers: { "Idempotency-Key": idempotencyKey } },
  );
}

export async function regeneratePlanOutputVersion(
  versionId: string,
  scope: PlanSourceScopeInput,
  sectionId?: string,
  idempotencyKey: string = crypto.randomUUID(),
  budgetApprovalId?: string | null,
  instruction?: string,
) {
  const response = await apiFetch<
    | { job: Extract<PlanGenerationMutationResult, { jobId: string }> }
    | Extract<PlanGenerationMutationResult, { approvalRequired: true }>
  >(
    planSourcePath(`/api/plans/output-versions/${encodeURIComponent(versionId)}/regenerate`, scope),
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({
        ...(sectionId ? { sectionId } : {}),
        ...(budgetApprovalId ? { budgetApprovalId } : {}),
        ...(instruction?.trim() ? { instruction: instruction.trim() } : {}),
      }),
    },
  );
  return "job" in response ? response.job : response;
}

export function microsoftLoginUrl(email: string, options: { forceLogin?: boolean; unlockStudentCareReports?: boolean; targetModule?: "studentCare"; studentCareView?: "professorReports" | "counselorReports"; returnPath?: string } = {}) {
  const callbackUrl = new URL(`${window.location.origin}/auth/callback`);
  if (options.unlockStudentCareReports) callbackUrl.searchParams.set("student_care_reports", "1");
  if (options.targetModule) callbackUrl.searchParams.set("target_module", options.targetModule);
  if (options.studentCareView) callbackUrl.searchParams.set("student_care_view", options.studentCareView);
  if (options.returnPath) callbackUrl.searchParams.set("app_return_to", options.returnPath);
  const params = new URLSearchParams({ return_to: callbackUrl.toString(), email });
  if (options.forceLogin) params.set("prompt", "login");
  return `${directApiUrl}/api/auth/microsoft/start?${params.toString()}`;
}

export function microsoftPasswordResetUrl(email: string) {
  const params = new URLSearchParams({ username: email });
  return `https://passwordreset.microsoftonline.com/?${params.toString()}`;
}

// ---- BTEC official assessment workflow (windows / submissions / AI / approvals / export) ----

export type AssessmentWindowListItem = {
  id: string;
  unitNumber: string;
  unitName: string | null;
  learningAimScope: string;
  title: string;
  className: string | null;
  status: string;
  isLocked: boolean;
  releaseAt: string | null;
  studentDueAt: string | null;
  teacherDueAt: string | null;
  effectiveStudentDueAt: string | null;
  effectiveTeacherDueAt: string | null;
  coordinatorDueAt: string | null;
  rosterCount: number;
  criteriaCount: number;
  sourcePackageId?: string | null;
  criteriaSnapshotCreatedAt?: string | null;
};

export type AssessmentSubmissionState = {
  studentId: string;
  rosterStatus: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  className: string | null;
  submission: { id: string; status: string; submittedAt: string | null; effectiveDueAt: string | null; reopenedUntil: string | null } | null;
  canSubmit: boolean;
  isOverdue: boolean;
  effectiveDueAt: string | null;
};

export type AssessmentAiResult = {
  id: string;
  windowCriterionId: string | null;
  criterionCode: string | null;
  learningAim: string | null;
  proposedGrade: string | null;
  proposedFeedback: string | null;
  status: string;
  provider: string | null;
  model: string | null;
  achievementStatus: "met" | "partially_met" | "not_met" | "cannot_judge" | null;
  evidenceNotes: string | null;
  reviewWarnings: string[];
  extractionMetadata: {
    totalChars?: number;
    truncated?: boolean;
    qualityStatus?: "reliable" | "review_required";
    qualityReasons?: string[];
    canProposeFail?: boolean;
    files?: Array<{
      originalFilename?: string;
      charCount?: number;
      extractionMethod?: "native_text" | "ai_ocr" | "ai_markdown";
      pageCount?: number;
      nativeCharCount?: number;
    }>;
    ignored?: Array<{
      name?: string;
      reason?: string;
    }>;
    visualEvidence?: {
      imageCount: number;
      pdfCount: number;
      totalBytes: number;
      truncated: boolean;
      files?: Array<{
        sourceName?: string;
        kind?: "pdf" | "image";
        byteCount?: number;
      }>;
      ignored?: Array<{
        name?: string;
        reason?: string;
      }>;
    } | null;
  } | null;
  createdAt: string;
};

export type AssessmentApproval = {
  id: string;
  windowCriterionId: string | null;
  learningAim: string | null;
  finalGrade: string;
  finalFeedback: string | null;
  source: string;
  approvedBy: string;
  approvedAt: string;
  editedAfterApproval: boolean;
};

export type AssessmentExport = {
  id: string;
  windowId: string;
  exportType: string;
  fileName: string;
  version: number;
  rowCount: number;
  createdAt: string;
};

export type AssessmentApprovalInputRow = {
  windowCriterionId: string;
  finalGrade: string;
  finalFeedback?: string | null;
  source: "manual" | "ai_accepted" | "ai_edited";
};

const schoolQs = (schoolId: string, params?: Record<string, string>) => {
  const query = new URLSearchParams({ schoolId, ...(params ?? {}) });
  return `?${query.toString()}`;
};

export type BtecPlannedFinalDelivery = {
  deliveryId: string; classId: string; classLabel: string; subjectId: string; subjectName: string;
  teacherUserId: string; unitNumber: string; learningAimScope: string; title: string;
  releaseAt: string | null; studentDueAt: string | null; teacherDueAt: string | null; windowId: string | null; canConfigure: boolean; scheduleComplete: boolean;
  dateSource: "syllabus" | "occurrence";
};
export function listBtecPlannedFinalDeliveries(token: string, schoolId: string, academicYearId?: string) {
  const query = schoolQs(schoolId) + (academicYearId ? `&academicYearId=${encodeURIComponent(academicYearId)}` : "");
  return apiFetch<{data:{deliveries:BtecPlannedFinalDelivery[];learningAims?:Array<{unitNumber:string;scope:string}>}}>(`/api/btec/assessment/planned-final-deliveries${query}`, {token}).then(res=>res.data);
}
export function configureBtecFinalDeliveryDates(token: string, schoolId: string, input: {
  deliveryId: string; classId: string; subjectId: string; teacherDueAt: string; justification: string;
}) {
  return apiFetch<{data:{windowId:string}}>(`/api/btec/assessment/planned-final-deliveries${schoolQs(schoolId)}`, {
    token,method:"POST",body:JSON.stringify(input),
  }).then(res=>res.data);
}

export function listAssessmentWindows(token: string, schoolId: string, academicYearId?: string) {
  return apiFetch<{ data: { windows: AssessmentWindowListItem[] } }>(
    `/api/btec/assessment/windows${schoolQs(schoolId, academicYearId ? { academicYearId } : undefined)}`,
    { token },
  ).then((res) => res.data.windows);
}

export function listBtecStandardisations(token: string, schoolId: string, academicYearId?: string) {
  return apiFetch<{ standardisations: BtecStandardisationListItem[] }>(
    `/api/btec/standardisations${schoolQs(schoolId, academicYearId ? { academicYearId } : undefined)}`,
    { token },
  ).then((response) => response.standardisations);
}

export function listBtecStandardisationOptions(token: string, schoolId: string) {
  return apiFetch<{ units: import("../modules/btec/standardisation/types").BtecStandardisationOption[] }>(
    `/api/btec/standardisations/options${schoolQs(schoolId)}`,
    { token },
  ).then((response) => response.units);
}

export function getBtecStandardisation(
  token: string,
  schoolId: string,
  recordId: string,
  academicYearId?: string,
) {
  return apiFetch<{ standardisation: BtecStandardisationDetail }>(
    `/api/btec/standardisations/${encodeURIComponent(recordId)}${schoolQs(
      schoolId,
      academicYearId ? { academicYearId } : undefined,
    )}`,
    { token },
  ).then((response) => response.standardisation);
}

export function createBtecStandardisation(
  token: string,
  schoolId: string,
  input: BtecStandardisationDraftInput,
  idempotencyKey: string,
) {
  return apiFetch<{ standardisation: BtecStandardisationDetail }>(
    `/api/btec/standardisations${schoolQs(schoolId)}`,
    {
      token,
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(input),
    },
  ).then((response) => response.standardisation);
}

export function updateBtecStandardisation(
  token: string,
  schoolId: string,
  recordId: string,
  input: BtecStandardisationUpdateInput,
) {
  return apiFetch<{ standardisation: BtecStandardisationDetail }>(
    `/api/btec/standardisations/${encodeURIComponent(recordId)}${schoolQs(schoolId)}`,
    { token, method: "PATCH", body: JSON.stringify(input) },
  ).then((response) => response.standardisation);
}

function runBtecStandardisationAction(
  token: string,
  schoolId: string,
  recordId: string,
  action: "submit" | "confirm" | "request-changes" | "close",
  input: { version: number; reason?: string },
) {
  return apiFetch<{ standardisation: BtecStandardisationDetail }>(
    `/api/btec/standardisations/${encodeURIComponent(recordId)}/${action}${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify(input) },
  ).then((response) => response.standardisation);
}

export function submitBtecStandardisation(token: string, schoolId: string, recordId: string, version: number) {
  return runBtecStandardisationAction(token, schoolId, recordId, "submit", { version });
}

export function confirmBtecStandardisation(token: string, schoolId: string, recordId: string, version: number) {
  return runBtecStandardisationAction(token, schoolId, recordId, "confirm", { version });
}

export function requestBtecStandardisationChanges(
  token: string,
  schoolId: string,
  recordId: string,
  version: number,
  reason: string,
) {
  return runBtecStandardisationAction(token, schoolId, recordId, "request-changes", { version, reason });
}

export function closeBtecStandardisation(token: string, schoolId: string, recordId: string, version: number) {
  return runBtecStandardisationAction(token, schoolId, recordId, "close", { version });
}

export async function downloadBtecStandardisationForm(
  schoolId: string,
  recordId: string,
  academicYearId?: string,
): Promise<void> {
  const response = await fetch(
    `${API_URL}/api/btec/standardisations/${encodeURIComponent(recordId)}/form.docx${schoolQs(
      schoolId,
      academicYearId ? { academicYearId } : undefined,
    )}`,
    viewAsInit(),
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || translateUi("Shkarkimi i procesverbalit dështoi.", "Meeting minutes download failed."));
  }
  const disposition = response.headers.get("content-disposition") || "";
  const serverFilename = disposition.match(/filename="([^"]+)"/i)?.[1];
  const blob = await response.blob();
  await downloadResponseBlob(blob, serverFilename || "Procesverbali i standardizimit BTEC.docx");
}

export function listBtecAssessmentAcademicYears(token: string, schoolId: string) {
  return apiFetch<{ data: { academicYears: BtecAssessmentAcademicYear[] } }>(
    `/api/btec/assessment/academic-years${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data.academicYears);
}

export function fetchBtecProjectsOverview(token: string, schoolId: string) {
  return apiFetch<{ data: { rows: BtecProjectOverviewRow[] } }>(
    `/api/btec/projects-overview${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data.rows);
}

export type AssessmentWindowReopening = {
  id: string;
  studentDueAt: string;
  teacherDueAt: string;
  justification: string;
  requestedBy: string | null;
  reopenedBy: string | null;
  createdAt: string;
};

export type AssessmentWindowCriterion = {
  id: string;
  sourceCriterionId: string | null;
  criterionCode: string;
  learningAim: string | null;
  level: string | null;
  description: string | null;
  sortOrder: number;
};

export type AssessmentWindowDetail = {
  window: Omit<AssessmentWindowListItem, "rosterCount" | "criteriaCount">;
  reopenings: AssessmentWindowReopening[];
  roster?: Array<{ id: string; studentId: string; status: string }>;
  criteria?: AssessmentWindowCriterion[];
  events?: Array<{
    id: string;
    actorId: string | null;
    eventType: string;
    metadata: string | null;
    createdAt: string;
  }>;
};

export function getAssessmentWindow(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: AssessmentWindowDetail }>(
    `/api/btec/assessment/windows/${windowId}${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export type BtecAimWindowSummary = AssessmentWindowListItem;
export type BtecUnitAimProject = {
  scope: string;
  aims: { aimLetter: string; title: string }[];
  windows: BtecAimWindowSummary[];
};
export type BtecUnitAimOverview = {
  unitNumber: string;
  title: string;
  generation: string | null;
  aims: { aimLetter: string; title: string; projectGroup: number | null }[];
  projects: BtecUnitAimProject[];
};

// `viewAsProfessor` lets a dual-role Admin/Profesor account, after switching the
// Dashboard role selector to "Profesor", ask the server to drop admin oversight
// and scope units to what the account actually teaches — same as a plain professor
// account would see. It can only narrow the response, never widen it: the server
// still checks the account's real DB roles first.
export function fetchBtecUnitsOverview(token: string, schoolId: string, viewAsProfessor?: boolean) {
  const viewAsQs = viewAsProfessor ? "&viewAsRole=professor" : "";
  return apiFetch<{ data: { units: BtecUnitAimOverview[] } }>(
    `/api/btec/assessment/units-overview${schoolQs(schoolId)}${viewAsQs}`,
    { token },
  ).then((res) => res.data.units);
}

export function fetchBtecUnitGenerations(token: string, schoolId: string) {
  return apiFetch<{ generations: string[] }>(
    `/api/btec/admin/units/generations${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.generations);
}

// "guide" and "syllabus" stay single-file; scenario briefs and aim-specific extras
// are multi-file (BtecUnitDocumentCategory / getBtecUnitDocuments below).
export type BtecUnitDocKind = "guide" | "syllabus";
export type BtecUnitDocStatus = { fileName: string; fileSize: number | null; updatedAt: string | null };

export function getBtecUnitDocs(token: string, schoolId: string, unitNumber: string, viewAsProfessor = false) {
  const professorQs = viewAsProfessor ? "&as=professor" : "";
  return apiFetch<{ docs: Record<BtecUnitDocKind, BtecUnitDocStatus | null> }>(
    `/api/btec/units/${encodeURIComponent(unitNumber)}/docs${schoolQs(schoolId)}${professorQs}`,
    { token },
  ).then((res) => res.docs);
}

export async function downloadBtecUnitDocumentsZip(schoolId: string, unitNumber: string, viewAsProfessor = false): Promise<void> {
  const professorQs = viewAsProfessor ? "&as=professor" : "";
  const response = await fetch(
    `${API_URL}/api/btec/units/${encodeURIComponent(unitNumber)}/documents.zip${schoolQs(schoolId)}${professorQs}`,
    viewAsInit(),
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || "download failed");
  }
  const disposition = response.headers.get("content-disposition") || "";
  const matchedName = disposition.match(/filename="([^"]+)"/i)?.[1];
  const blob = await response.blob();
  await downloadResponseBlob(blob, matchedName || `BTEC_U${unitNumber}.zip`);
}

export async function downloadAllBtecUnitDocumentsZip(schoolId: string, viewAsProfessor = false): Promise<void> {
  const professorQs = viewAsProfessor ? "&as=professor" : "";
  const response = await fetch(`${API_URL}/api/btec/units/documents.zip${schoolQs(schoolId)}${professorQs}`, viewAsInit());
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || "download failed");
  }
  const disposition = response.headers.get("content-disposition") || "";
  const matchedName = disposition.match(/filename="([^"]+)"/i)?.[1];
  const blob = await response.blob();
  await downloadResponseBlob(blob, matchedName || "BTEC_Units.zip");
}

export type BtecUnitDocumentCategory = "scenario_brief" | "aim_material" | "example_package";
export type BtecUnitDocument = {
  id: string;
  category: BtecUnitDocumentCategory;
  // The project scope this document belongs to (e.g. "A", "B+C") — one of the
  // unit's currently defined learning-aim groupings.
  scope: string | null;
  fileName: string;
  fileSize: number | null;
  updatedAt: string;
  hasExtractedText: boolean;
};
export type BtecUnitDocumentList = { documents: BtecUnitDocument[]; availableScopes: string[] };

// `scope` (a project scope like "A" or "B+C") lets the server auto-resolve which scenario_brief
// is "current" for that Learning Aim: a coordinator-approved unit-wide scenario (scope "ALL")
// outranks everything, then a brief matching this exact scope, then the rest — so documents[0] is
// always the correct default without the caller filtering client-side.
export function getBtecUnitDocuments(
  token: string,
  schoolId: string,
  unitNumber: string,
  category: BtecUnitDocumentCategory,
  scope?: string,
  viewAsProfessor = false,
) {
  const professorQs = viewAsProfessor ? "&as=professor" : "";
  return apiFetch<BtecUnitDocumentList>(
    `/api/btec/units/${encodeURIComponent(unitNumber)}/documents${schoolQs(schoolId)}&category=${category}${scope ? `&scope=${encodeURIComponent(scope)}` : ""}${professorQs}`,
    { token },
  );
}

export function getBtecUnitDocumentText(token: string, schoolId: string, unitNumber: string, documentId: string) {
  return apiFetch<{ text: string; fileName: string }>(
    `/api/btec/units/${encodeURIComponent(unitNumber)}/documents/${documentId}/text${schoolQs(schoolId)}`,
    { token },
  );
}

export function uploadBtecUnitDocument(
  token: string, schoolId: string, unitNumber: string,
  input: { file: File; category: BtecUnitDocumentCategory; scope: string; extractedText?: string },
) {
  const form = new FormData();
  form.append("file", input.file);
  form.append("category", input.category);
  form.append("scope", input.scope);
  if (input.extractedText) form.append("extractedText", input.extractedText);
  return apiFetch<{ document: BtecUnitDocument }>(
    `/api/btec/units/${encodeURIComponent(unitNumber)}/documents${schoolQs(schoolId)}`,
    { method: "POST", token, body: form },
  ).then((res) => res.document);
}

export function replaceBtecExamplePackageDocument(
  schoolId: string, unitNumber: string, documentId: string,
  input: { file: File; path: string; version: string; idempotencyKey: string },
) {
  const body = new FormData();
  body.set("file", input.file);
  body.set("path", input.path);
  body.set("version", input.version);
  return apiFetch<{ ok: boolean }>(
    `/api/btec/units/${encodeURIComponent(unitNumber)}/documents/${encodeURIComponent(documentId)}/member${schoolQs(schoolId)}`,
    { method: "PUT", body, headers: { "Idempotency-Key": input.idempotencyKey } },
  );
}

export function deleteBtecUnitDocument(token: string, schoolId: string, unitNumber: string, documentId: string) {
  return apiFetch<{ ok: boolean }>(
    `/api/btec/units/${encodeURIComponent(unitNumber)}/documents/${documentId}${schoolQs(schoolId)}`,
    { method: "DELETE", token },
  );
}

export function listAssessmentSubmissions(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: { windowId: string; submissions: AssessmentSubmissionState[] } }>(
    `/api/btec/assessment/windows/${windowId}/submissions${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data.submissions);
}

export type HistoricalImportManifestStatus = "matched" | "unmatched" | "ambiguous" | "conflict" | "invalid";

export type HistoricalImportFileDescriptor = {
  clientId: string;
  relativePath: string;
  fileName: string;
  mimeType: string;
  size: number;
  className: string | null;
  checksumSha256: string;
};

export type HistoricalImportManifestItem = HistoricalImportFileDescriptor & {
  itemId?: string;
  studentId: string | null;
  studentName: string | null;
  status: HistoricalImportManifestStatus;
  message?: string | null;
  uploaded?: boolean;
};

export type HistoricalImportPreview = {
  manifest: HistoricalImportManifestItem[];
};

export type HistoricalImportBatch = {
  batchId: string;
  status: "draft" | "uploading" | "ready" | "finalized" | "rolled_back" | "failed";
  items: HistoricalImportManifestItem[];
  importedCount?: number;
  failedCount?: number;
  rolledBackCount?: number;
};

function historicalImportCollectionPath(windowId: string) {
  return `/api/btec/assessment/windows/${encodeURIComponent(windowId)}/historical-imports`;
}

export function previewHistoricalAssessmentImport(
  token: string,
  windowId: string,
  schoolId: string,
  files: HistoricalImportFileDescriptor[],
) {
  return apiFetch<{ data: HistoricalImportPreview }>(
    `${historicalImportCollectionPath(windowId)}/preview`,
    { token, method: "POST", body: JSON.stringify({ schoolId, files }) },
  ).then((res) => res.data);
}

export function createHistoricalAssessmentImport(
  token: string,
  windowId: string,
  schoolId: string,
  manifest: HistoricalImportManifestItem[],
) {
  return apiFetch<{ data: HistoricalImportBatch }>(historicalImportCollectionPath(windowId), {
    token,
    method: "POST",
    body: JSON.stringify({ schoolId, manifest }),
  }).then((res) => res.data);
}

export function uploadHistoricalAssessmentImportItem(
  token: string,
  windowId: string,
  batchId: string,
  itemId: string,
  schoolId: string,
  file: File,
) {
  const form = new FormData();
  form.set("schoolId", schoolId);
  form.set("file", file);
  return apiFetch<{ data: HistoricalImportManifestItem }>(
    `${historicalImportCollectionPath(windowId)}/${encodeURIComponent(batchId)}/items/${encodeURIComponent(itemId)}`,
    { token, method: "PUT", body: form },
  ).then((res) => res.data);
}

export function finalizeHistoricalAssessmentImport(
  token: string,
  windowId: string,
  batchId: string,
  schoolId: string,
) {
  return apiFetch<{ data: HistoricalImportBatch }>(
    `${historicalImportCollectionPath(windowId)}/${encodeURIComponent(batchId)}/finalize`,
    { token, method: "POST", body: JSON.stringify({ schoolId }) },
  ).then((res) => res.data);
}

export function rollbackHistoricalAssessmentImport(
  token: string,
  windowId: string,
  batchId: string,
  schoolId: string,
) {
  return apiFetch<{ data: HistoricalImportBatch }>(
    `${historicalImportCollectionPath(windowId)}/${encodeURIComponent(batchId)}/rollback`,
    { token, method: "POST", body: JSON.stringify({ schoolId }) },
  ).then((res) => res.data);
}

// --- Teacher-submission channel (assessor → coordinator per window) ---

export type TeacherChecklistItem = { key: string; label: string; required: boolean };

export type TeacherSubmissionInfo = {
  id: string;
  windowId: string;
  status: "draft" | "submitted" | "returned" | "accepted";
  submittedAt: string | null;
  submittedLate: boolean;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TeacherSubmissionFileInfo = {
  id: string;
  itemKey: string;
  studentId: string | null;
  originalFilename: string;
  fileSize: number;
  contentType: string;
  createdAt: string;
};

export type TeacherSubmissionData = {
  readinessIssues: ("no_students" | "grades_pending" | "criteria_pending" | "reports_pending")[];
  submission: TeacherSubmissionInfo | null;
  files: TeacherSubmissionFileInfo[];
  checklist: TeacherChecklistItem[];
};

export function getTeacherSubmission(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: TeacherSubmissionData }>(
    `/api/btec/assessment/windows/${windowId}/teacher-submission${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function uploadTeacherSubmissionFile(
  token: string,
  windowId: string,
  schoolId: string,
  params: { file: File; itemKey: string; studentId?: string | null },
) {
  const form = new FormData();
  form.set("file", params.file);
  form.set("itemKey", params.itemKey);
  if (params.studentId) form.set("studentId", params.studentId);
  return apiFetch<{ data: { fileId: string } }>(
    `/api/btec/assessment/windows/${windowId}/teacher-submission/files${schoolQs(schoolId)}`,
    { token, method: "POST", body: form },
  ).then((res) => res.data);
}

export function deleteTeacherSubmissionFile(token: string, windowId: string, fileId: string, schoolId: string) {
  return apiFetch<{ data: { ok: boolean } }>(
    `/api/btec/assessment/windows/${windowId}/teacher-submission/files/${fileId}${schoolQs(schoolId)}`,
    { token, method: "DELETE" },
  ).then((res) => res.data);
}

export async function downloadTeacherSubmissionFile(
  windowId: string,
  file: TeacherSubmissionFileInfo,
  schoolId: string,
) {
  const response = await fetch(
    `${API_URL}/api/btec/assessment/windows/${encodeURIComponent(windowId)}/teacher-submission/files/${encodeURIComponent(file.id)}/download${schoolQs(schoolId)}`,
    { credentials: "include" },
  );
  if (!response.ok) throw new Error(await response.text().catch(() => "Download failed."));
  const blob = await response.blob();
  await downloadResponseBlob(blob, file.originalFilename);
}

export function submitTeacherSubmission(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: { submission: TeacherSubmissionInfo | null } }>(
    `/api/btec/assessment/windows/${windowId}/teacher-submission/submit${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((res) => res.data.submission);
}

export function reviewTeacherSubmission(
  token: string,
  windowId: string,
  schoolId: string,
  action: "accept" | "return",
  note?: string,
) {
  return apiFetch<{ data: { submission: TeacherSubmissionInfo | null } }>(
    `/api/btec/assessment/windows/${windowId}/teacher-submission/review${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({ action, note }) },
  ).then((res) => res.data.submission);
}

export type TeacherSubmissionInboxItem = {
  submissionId: string;
  windowId: string;
  status: string;
  submittedAt: string | null;
  submittedLate: boolean;
  windowTitle: string;
  unitNumber: string;
  learningAimScope: string;
  assessorName: string | null;
};

export function listTeacherSubmissionInbox(token: string, schoolId: string) {
  return apiFetch<{ data: { items: TeacherSubmissionInboxItem[] } }>(
    `/api/btec/assessment/teacher-submissions${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data.items);
}

export function saveWindowTeacherChecklist(
  token: string,
  windowId: string,
  schoolId: string,
  items: TeacherChecklistItem[],
) {
  return apiFetch<{ data: { checklist: TeacherChecklistItem[] } }>(
    `/api/btec/assessment/windows/${windowId}/teacher-checklist${schoolQs(schoolId)}`,
    { token, method: "PUT", body: JSON.stringify({ items }) },
  ).then((res) => res.data.checklist);
}

export function getAssessmentAiResults(token: string, windowId: string, studentId: string, schoolId: string) {
  return apiFetch<{ data: { results: AssessmentAiResult[]; runStatus: string | null } }>(
    `/api/btec/assessment/windows/${windowId}/submissions/${studentId}/ai-results${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export type BtecAssessmentReport = {
  id: string;
  reportKind: BtecAssessmentReportKind;
  status: "draft" | "final";
  fileName: string;
  fileSize: number;
  generatedAt: string;
  finalizedAt: string | null;
};

export type BtecAssessmentReportGeneration = {
  status: "not_started" | "queued" | "generating" | "ready" | "failed" | "stale";
  errorMessage: string | null;
  queuedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  updatedAt: string | null;
};

export type BtecAssessmentReportOverview = {
  window: {
    id: string;
    title: string;
    unitNumber: string;
    learningAimScope: string;
  };
  summary: {
    total: number;
    ready: number;
    inProgress: number;
    failed: number;
    pending: number;
    // Raporte të ndërtuara mbi një shabllon që është zëvendësuar që atëherë. Fusha mungon te
    // përgjigjet e worker-it të vjetër.
    stale?: number;
  };
  students: Array<{
    studentId: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    // Paralelja (students.grade_class); null when the learner has none recorded.
    className?: string | null;
    generation: BtecAssessmentReportGeneration;
    reports: BtecAssessmentReport[];
    declarations: AssessmentDeclaration[];
    // Server-decided preconditions for the assessor declaration: an overall grade must exist and
    // the viewer must be the window's assigned assessor. Older responses omit both.
    hasFinalGrade?: boolean;
    canConfirmDeclaration?: boolean;
  }>;
};

// --- BTEC declarations (electronic sign-off) ---

export type AssessmentDeclaration = {
  studentId: string;
  role: "learner" | "assessor" | string;
  confirmedName: string;
  confirmedAt: string;
};

export function listAssessmentDeclarations(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: { windowId: string; declarations: AssessmentDeclaration[] } }>(
    `/api/btec/assessment/windows/${windowId}/declarations${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function confirmAssessorDeclaration(token: string, windowId: string, studentId: string, schoolId: string) {
  return apiFetch<{ data: { declaration: AssessmentDeclaration; alreadyConfirmed: boolean } }>(
    `/api/btec/assessment/windows/${windowId}/submissions/${studentId}/declaration${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((res) => res.data);
}

export function confirmMyLearnerDeclaration(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: { declaration: AssessmentDeclaration; alreadyConfirmed: boolean } }>(
    `/api/btec/assessment/windows/${windowId}/my-submission/declaration${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((res) => res.data);
}

// --- BTEC "Detyrat e mia" (assessor duties) ---

export type BtecMyTask = {
  windowId: string;
  unitNumber: string;
  unitName: string | null;
  learningAimScope: string;
  title: string;
  className: string | null;
  status: string;
  teacherDueAt: string;
  rosterCount: number;
  gradedCount: number;
  pendingGradeCount: number;
  awaitingReviewCount: number;
  awaitingApprovalCount: number;
  ivActionsCount: number;
  // Brief-verification actions (Pearson assignment-brief checklist points marked "No"),
  // distinct from ivActionsCount (internal verification of grading decisions) — see the
  // comment on the my-tasks route in worker/src/index.ts.
  briefIvActionsCount: number;
  declarationsPendingCount: number;
  scenarioReviewStatus: string;
  scenarioReviewNote: string | null;
  briefIvActions?: string[];
  packageId: string | null;
  reopenCount: number;
  teacherSubmissionStatus: string;
  teacherSubmittedAt: string | null;
};

export type BtecAttentionItem = {
  windowId: string;
  unitNumber: string;
  title: string;
  className: string | null;
  teacherDueAt: string;
  rosterCount: number;
  gradedCount: number;
  submissionStatus: string;
};

export type BtecDashboardAttention = {
  assessorItems: BtecAttentionItem[];
  upcomingDeadlines: Array<{
    windowId: string;
    unitNumber: string;
    title: string;
    className: string | null;
    dueAt: string;
    deadlineType: "student" | "teacher" | "coordinator";
  }>;
  metrics?:
    | { mode: "coordinator"; inbox: number; activeWindows: number; overdueDeadlines: number }
    | { mode: "assessor"; pendingStudents: number; deadlinesThisWeek: number; returnedHandIns: number };
  activeWindows?: BtecActiveWindow[];
  coordinator?: { pendingSubmissions: number; openWindows: number } | null;
};

// Coordinator-only: windows currently in flight, with progress and the deadline that comes next.
// Optional on purpose, so an older Worker paired with a newer frontend degrades to today's behaviour.
export type BtecActiveWindow = {
  windowId: string;
  unitNumber: string;
  title: string;
  className: string | null;
  rosterCount: number;
  gradedCount: number;
  submissionStatus: string;
  nextDueAt: string | null;
  nextDeadlineType: "student" | "teacher" | "coordinator" | null;
};

export function fetchBtecDashboardAttention(token: string, schoolId: string) {
  return apiFetch<{ data: BtecDashboardAttention }>(
    `/api/btec/dashboard/attention${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function fetchBtecMyTasks(token: string, schoolId: string) {
  return apiFetch<{ data: { tasks: BtecMyTask[] } }>(
    `/api/btec/assessment/my-tasks${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data.tasks);
}

export type BtecBriefIvStatus = { actionKeys: string[]; note: string | null };

// The failed checklist points + coordinator note behind a package's "actions_required" brief
// verification — used by BtecPackageWizard to show the author what to fix on the scenario step.
export function fetchBriefIvStatus(token: string, packageId: string) {
  return apiFetch<{ data: BtecBriefIvStatus }>(
    `/api/btec/packages/${packageId}/brief-iv-status`,
    { token },
  ).then((res) => res.data);
}

export type BtecPendingDeclarationStudent = { studentId: string; studentName: string; finalGrade: string };

export function fetchPendingDeclarations(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: { students: BtecPendingDeclarationStudent[] } }>(
    `/api/btec/assessment/windows/${windowId}/pending-declarations${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data.students);
}

// Streams the pre-filled unit grade workbook (nothing persisted server-side).
export async function downloadUnitGradeWorkbook(token: string, windowId: string, schoolId: string, unitNumber: string) {
  const response = await fetch(
    `${API_URL}/api/btec/assessment/windows/${encodeURIComponent(windowId)}/grade-workbook${schoolQs(schoolId)}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error || translateUi("Shkarkimi i Excel-it dështoi.", "Excel download failed."));
  }
  const blob = await response.blob();
  await downloadResponseBlob(blob, `notat-unit-${unitNumber}.xlsx`);
}

// --- BTEC authenticity (text-overlap signal) ---

export type BtecSimilarityPair = {
  studentA: string;
  studentAName: string;
  studentB: string;
  studentBName: string;
  score: number;
  samples: string[];
  previousYear: boolean;
};

export type BtecSimilarityGroup = {
  students: string[];
  sharedShingles: number;
  samples: string[];
};

export type BtecSimilarityWidespread = {
  students: string[];
  studentCount: number;
  sharedShingles: number;
  samples: string[];
};

export type BtecSimilarityResult = {
  windowId: string;
  comparedStudents: number;
  referencesCompared: number;
  briefTextSubtracted: boolean;
  pendingFiles: number;
  pairs: BtecSimilarityPair[];
  groups: BtecSimilarityGroup[];
  widespread: BtecSimilarityWidespread[];
  templateOnly: string[];
};

export function uploadAuthenticityTemplate(token: string, windowId: string, schoolId: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  return apiFetch<{ data: { registered: boolean; textLength: number } }>(
    `/api/btec/assessment/windows/${windowId}/authenticity-template${schoolQs(schoolId)}`,
    { token, method: "POST", body: form },
  ).then((res) => res.data);
}

export function runWindowSimilarityCheck(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: BtecSimilarityResult }>(
    `/api/btec/assessment/windows/${windowId}/similarity${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((res) => res.data);
}

export function getSavedWindowSimilarityCheck(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: { result: BtecSimilarityResult | null; generatedAt: string | null } }>(
    `/api/btec/assessment/windows/${windowId}/similarity${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

// --- BTEC Internal Verification (IV) ---

export type IvDecision = "pending" | "agreed" | "actions_required" | "signed_off";

export type IvReview = {
  studentId: string;
  decision: IvDecision | string;
  criteriaAccurate: boolean | null;
  feedbackLinked: boolean | null;
  contentCovered: boolean | null;
  comments: string | null;
  actionsRequired: string | null;
  cohortAction: boolean;
  signedOffAt: string | null;
  updatedAt: string;
  verifierName: string | null;
};

export type IvWindowState = {
  windowId: string;
  ivRequired: boolean;
  ivCompletedAt: string | null;
  ivCompletedByName: string | null;
  gateActive: boolean;
  assessorUserId: string;
  callerIsAssessor: boolean;
  reviews: IvReview[];
  roster: Array<{ studentId: string; studentName: string; email: string | null; overallGrade: string | null }>;
};

export function getWindowIv(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: IvWindowState }>(
    `/api/btec/assessment/windows/${windowId}/iv${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function setWindowIvSettings(token: string, windowId: string, schoolId: string, ivRequired: boolean) {
  return apiFetch<{ data: { ivRequired: boolean } }>(
    `/api/btec/assessment/windows/${windowId}/iv/settings${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({ ivRequired }) },
  ).then((res) => res.data);
}

export function addIvSampleStudents(token: string, windowId: string, schoolId: string, studentIds: string[]) {
  return apiFetch<{ data: { added: number; reviews: IvReview[] } }>(
    `/api/btec/assessment/windows/${windowId}/iv/reviews${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({ studentIds }) },
  ).then((res) => res.data);
}

export function removeIvSampleStudent(token: string, windowId: string, schoolId: string, studentId: string) {
  return apiFetch<{ data: { removed: boolean } }>(
    `/api/btec/assessment/windows/${windowId}/iv/reviews/${studentId}${schoolQs(schoolId)}`,
    { token, method: "DELETE" },
  ).then((res) => res.data);
}

export function updateIvReview(
  token: string,
  windowId: string,
  schoolId: string,
  studentId: string,
  input: {
    decision: IvDecision;
    criteriaAccurate?: boolean | null;
    feedbackLinked?: boolean | null;
    contentCovered?: boolean | null;
    comments?: string | null;
    actionsRequired?: string | null;
    cohortAction?: boolean;
  },
) {
  return apiFetch<{ data: { review: IvReview | null } }>(
    `/api/btec/assessment/windows/${windowId}/iv/reviews/${studentId}${schoolQs(schoolId)}`,
    { token, method: "PATCH", body: JSON.stringify(input) },
  ).then((res) => res.data);
}

// Streams the official IV-of-assessment-decisions DOCX (nothing persisted server-side).
export async function downloadIvForm(token: string, windowId: string, schoolId: string, unitNumber: string, scope: string) {
  const response = await fetch(
    `${API_URL}/api/btec/assessment/windows/${encodeURIComponent(windowId)}/iv-form.docx${schoolQs(schoolId)}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error || translateUi("Shkarkimi i formularit dështoi.", "Form download failed."));
  }
  const blob = await response.blob();
  await downloadResponseBlob(blob, `verifikimi-unit-${unitNumber}-${scope}.docx`);
}

export function completeWindowIv(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: { completed: boolean; notified: number } }>(
    `/api/btec/assessment/windows/${windowId}/iv/complete${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((res) => res.data);
}

// --- BTEC window audit timeline ---

export type AssessmentWindowAuditEvent = {
  id: string;
  actorId: string | null;
  actorName: string | null;
  eventType: string;
  metadata: string | null;
  createdAt: string;
};

export type AssessmentWindowAuditExportCapability = {
  allowed: boolean;
  reason: "not_authorized" | null;
};

export function listAssessmentWindowEvents(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: {
    windowId: string;
    auditExport: AssessmentWindowAuditExportCapability;
    events: AssessmentWindowAuditEvent[];
  } }>(
    `/api/btec/assessment/windows/${windowId}/events${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export async function downloadAssessmentWindowAudit(windowId: string, schoolId: string): Promise<void> {
  const response = await fetch(
    `${API_URL}/api/btec/assessment/windows/${encodeURIComponent(windowId)}/audit-export${schoolQs(schoolId)}`,
    viewAsInit({ method: "POST" }),
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || "download failed");
  }
  const disposition = response.headers.get("content-disposition") || "";
  const matchedName = disposition.match(/filename="([^"]+)"/i)?.[1];
  const blob = await response.blob();
  await downloadResponseBlob(blob, matchedName || "AUDITI-BTEC.zip");
}

// --- BTEC analytics (Analitika) ---

export type BtecAnalyticsGradeCounts = {
  Distinction: number;
  Merit: number;
  Pass: number;
  Fail: number;
  "Not Submitted": number;
};

export type BtecAiTeacherDecisionMetrics = {
  comparableCount: number;
  editedCount: number;
  manualCount: number;
  divergenceRate: number | null;
};

export type BtecAiTeacherDecisionByApprover = BtecAiTeacherDecisionMetrics & {
  approverUserId: string;
  approverName: string;
};

export type BtecAnalytics = {
  units: Array<{ unitNumber: string; unitName: string | null; counts: BtecAnalyticsGradeCounts; total: number }>;
  classes: Array<{ className: string; counts: BtecAnalyticsGradeCounts; total: number }>;
  assessors: Array<{ assessorUserId: string; assessorName: string; counts: BtecAnalyticsGradeCounts; total: number; highGradeShare: number }>;
  windows: Array<{ windowId: string; title: string; unitNumber: string; learningAimScope: string; studentDueAt: string; counts: BtecAnalyticsGradeCounts; total: number }>;
  timeliness: { rostered: number; submittedOnTime: number; submittedLate: number; notSubmitted: number; onTimeRate: number };
  gradedTotal: number;
  aiTeacherDecisions: {
    overall: BtecAiTeacherDecisionMetrics;
    byApprover: BtecAiTeacherDecisionByApprover[];
  };
};

export function fetchBtecAnalytics(token: string, schoolId: string) {
  return apiFetch<{ data: BtecAnalytics }>(
    `/api/btec/assessment/analytics${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

// --- BTEC unified notifications ---

export type BtecNotificationItem = {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string;
  isRead: boolean;
  createdAt: string;
};

export function markNotificationRead(token: string, notificationId: string) {
  return apiFetch<{ ok: boolean }>(`/api/notifications/${notificationId}/read`, {
    token,
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function fetchBtecNotifications(token: string, schoolId: string) {
  return apiFetch<{ data: { notifications: BtecNotificationItem[]; unread: number } }>(
    `/api/btec/notifications${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function listAssessmentReports(token: string, windowId: string, studentId: string, schoolId: string) {
  return apiFetch<{ data: { reports: BtecAssessmentReport[]; generation: BtecAssessmentReportGeneration } }>(
    `/api/btec/assessment/windows/${windowId}/submissions/${studentId}/reports${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function getAssessmentReportOverview(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: BtecAssessmentReportOverview }>(
    `/api/btec/assessment/windows/${windowId}/reports-overview${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export type BtecAssessmentReportGenerationStatus = {
  summary: BtecAssessmentReportOverview["summary"];
  students: { studentId: string; generation: BtecAssessmentReportOverview["students"][number]["generation"] }[];
};

// Statuses only, for polling while a batch generates. The full overview carries every student's
// reports and declarations, none of which move until the batch finishes.
export function getAssessmentReportGenerationStatus(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: BtecAssessmentReportGenerationStatus }>(
    `/api/btec/assessment/windows/${windowId}/reports-overview/status${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function regenerateAllAssessmentReports(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: { queued: number; skipped: number; considered: number } }>(
    `/api/btec/assessment/windows/${encodeURIComponent(windowId)}/reports/regenerate-all${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((res) => res.data);
}

export function retryAssessmentReports(token: string, windowId: string, studentId: string, schoolId: string) {
  return apiFetch<{ data: { generation: BtecAssessmentReportGeneration } }>(
    `/api/btec/assessment/windows/${windowId}/submissions/${studentId}/reports/retry${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((res) => res.data);
}

export async function downloadAssessmentReport(
  _token: string,
  windowId: string,
  studentId: string,
  report: BtecAssessmentReport,
  schoolId: string,
) {
  const response = await fetch(
    `${API_URL}/api/btec/assessment/windows/${encodeURIComponent(windowId)}/submissions/${encodeURIComponent(studentId)}/reports/${encodeURIComponent(report.id)}/download${schoolQs(schoolId)}`,
    { credentials: "include" },
  );
  if (!response.ok) throw new Error(await response.text().catch(() => translateUi("Shkarkimi dështoi.", "Download failed.")));
  const blob = await response.blob();
  await downloadResponseBlob(blob, report.fileName);
}

export function runAssessmentAiReview(token: string, windowId: string, studentId: string, schoolId: string) {
  return apiFetch<{ data: unknown }>(
    `/api/btec/assessment/windows/${windowId}/submissions/${studentId}/ai-review${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  );
}

export type AssessmentAiReviewAllResult = {
  windowId: string;
  reviewed: number;
  skipped: number;
  failed: number;
  rateLimited: boolean;
  perStudent: Array<{ studentId: string; status: "reviewed" | "skipped" | "failed"; runStatus?: string; reason?: string }>;
};

export const ASSESSMENT_AI_BATCH_MAX = 10;

export function runAssessmentAiReviewAll(
  token: string,
  windowId: string,
  schoolId: string,
  studentIds: string[],
) {
  return apiFetch<{ data: AssessmentAiReviewAllResult }>(
    `/api/btec/assessment/windows/${windowId}/ai-review-all${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({ studentIds }) },
  ).then((res) => res.data);
}

export function getAssessmentApprovals(token: string, windowId: string, studentId: string, schoolId: string) {
  return apiFetch<{ data: { approvals: AssessmentApproval[]; overallGrade: AssessmentOverallGrade | null } }>(
    `/api/btec/assessment/windows/${windowId}/submissions/${studentId}/approval${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function saveAssessmentApproval(
  token: string,
  windowId: string,
  studentId: string,
  schoolId: string,
  criteria: AssessmentApprovalInputRow[],
) {
  return apiFetch<{ data: { approvals: AssessmentApproval[]; reportsQueued: boolean } }>(
    `/api/btec/assessment/windows/${windowId}/submissions/${studentId}/approval${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({ criteria }) },
  ).then((res) => res.data);
}

export type AssessmentFinalGradeRow = {
  criterionCode: string | null;
  learningAim: string | null;
  finalGrade: string;
  source: string;
  editedAfterApproval: boolean;
  approvedAt: string;
};

export type AssessmentOverallGrade = {
  studentId: string;
  finalGrade: string;
  finalFeedback: string | null;
  source: string;
  approvedBy: string;
  approvedAt: string;
  editedAfterApproval: boolean;
};

export const OVERALL_GRADE_VALUES = ["Pass", "Merit", "Distinction", "Fail", "Not Submitted"] as const;

export type AssessmentFinalGradesStudent = {
  studentId: string;
  studentName: string;
  email: string | null;
  submissionStatus: "missing" | "draft" | "submitted" | "overdue" | "unknown";
  overallGrade: AssessmentOverallGrade | null;
  grades: AssessmentFinalGradeRow[];
};

export function setAssessmentOverallGrade(
  token: string,
  windowId: string,
  studentId: string,
  schoolId: string,
  finalGrade: string,
  finalFeedback: string,
  source: "manual" | "ai_accepted" | "ai_edited" = "manual",
) {
  return apiFetch<{ data: { overallGrade: AssessmentOverallGrade | null; edited: boolean; reportsQueued: boolean } }>(
    `/api/btec/assessment/windows/${windowId}/submissions/${studentId}/overall-grade${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({ finalGrade, finalFeedback, source }) },
  ).then((res) => res.data);
}

export type AssessmentFinalGrades = {
  windowId: string;
  unitNumber: number;
  unitName: string | null;
  unitGradeBreakdownAvailable: boolean;
  students: AssessmentFinalGradesStudent[];
};

export type ExportTemplateAimScope = { scope: string; sheet: string };

export type AssessmentExportTemplate = {
  id: string;
  name: string;
  originalFilename: string;
  fileSize: number;
  isActive: boolean;
  scope: "school" | "global";
  unitNumber: string | null;
  aimScopes: ExportTemplateAimScope[];
  createdAt: string;
};

export function listAssessmentExportTemplates(token: string, schoolId: string) {
  return apiFetch<{ data: { templates: AssessmentExportTemplate[] } }>(
    `/api/btec/assessment/export-templates${schoolQs(schoolId)}`,
    { token },
  ).then((r) => r.data.templates);
}

export function uploadAssessmentExportTemplate(
  token: string,
  schoolId: string,
  file: File,
  options?: { name?: string; unitNumber?: string; aimScopes?: ExportTemplateAimScope[] },
) {
  const form = new FormData();
  form.append("file", file);
  if (options?.name) form.append("name", options.name);
  if (options?.unitNumber) form.append("unitNumber", options.unitNumber);
  if (options?.aimScopes && options.aimScopes.length > 0) {
    form.append("aimScopes", JSON.stringify(options.aimScopes));
  }
  return apiFetch<{ data: AssessmentExportTemplate }>(
    `/api/btec/assessment/export-templates${schoolQs(schoolId)}`,
    { token, method: "POST", body: form },
  ).then((r) => r.data);
}

export function updateAssessmentExportTemplate(
  token: string,
  schoolId: string,
  id: string,
  options: { name: string; originalFilename: string; unitNumber?: string | null; aimScopes?: ExportTemplateAimScope[] },
) {
  return apiFetch<{ data: AssessmentExportTemplate | null }>(
    `/api/btec/assessment/export-templates/${encodeURIComponent(id)}${schoolQs(schoolId)}`,
    { token, method: "PATCH", body: JSON.stringify(options) },
  ).then((r) => r.data);
}

export async function deleteAssessmentExportTemplate(token: string, schoolId: string, id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/api/btec/assessment/export-templates/${encodeURIComponent(id)}${schoolQs(schoolId)}`,
    { token, method: "DELETE" },
  );
}

export type AssessmentUnitFinalGrades = {
  unitNumber: number;
  unitName: string | null;
  aims: string[];
  students: Array<{
    studentId: string;
    studentName: string;
    email: string | null;
    aims: Array<{ scope: string; rostered: boolean; grade: string | null; windowId: string | null }>;
    finalGrade: string | null;
    pending: boolean;
  }>;
};

export function getAssessmentUnitFinalGrades(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: AssessmentUnitFinalGrades }>(
    `/api/btec/assessment/windows/${windowId}/unit-final-grades${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function getAssessmentFinalGrades(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: AssessmentFinalGrades }>(
    `/api/btec/assessment/windows/${windowId}/final-grades${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function approveAllAssessment(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: { studentsApproved: number; studentsSkipped: number; criteriaApproved: number; reportsQueued: number } }>(
    `/api/btec/assessment/windows/${windowId}/approve-all${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((res) => res.data);
}

export function generateAssessmentExport(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: AssessmentExport }>(
    `/api/btec/assessment/windows/${windowId}/export${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((res) => res.data);
}

// Streams the stored CSV to the browser via an authenticated fetch; the object key never leaves the server.
export async function downloadAssessmentExport(token: string, exportId: string, fileName: string) {
  const response = await fetch(`${API_URL}/api/btec/assessment/exports/${exportId}/download`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error(translateUi("Shkarkimi i eksportit dështoi.", "Export download failed."));
  const blob = await response.blob();
  await downloadResponseBlob(blob, fileName || "eksport.csv");
}

// --- Year-end qualification-placement workbook ---

export type PlacementTemplate = {
  id: string;
  name: string;
  originalFilename: string;
  fileSize: number;
  isActive: boolean;
  academicYearId: string;
  createdAt: string;
};

export function listPlacementTemplates(token: string, schoolId: string) {
  return apiFetch<{ data: { templates: PlacementTemplate[] } }>(
    `/api/btec/assessment/placement-template${schoolQs(schoolId)}`,
    { token },
  ).then((r) => r.data.templates);
}

export function uploadPlacementTemplate(token: string, schoolId: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  return apiFetch<{ data: PlacementTemplate }>(
    `/api/btec/assessment/placement-template${schoolQs(schoolId)}`,
    { token, method: "POST", body: form },
  ).then((r) => r.data);
}

export async function deletePlacementTemplate(token: string, schoolId: string, id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/api/btec/assessment/placement-template/${encodeURIComponent(id)}${schoolQs(schoolId)}`,
    { token, method: "DELETE" },
  );
}

export function generatePlacementExport(token: string, schoolId: string) {
  return apiFetch<{ data: { id: string; fileName: string; version: number; learnerCount: number; createdAt: string } }>(
    `/api/btec/assessment/placement-export${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({}) },
  ).then((r) => r.data);
}

export async function downloadPlacementExport(exportId: string, fileName: string) {
  const response = await fetch(`${API_URL}/api/btec/assessment/placement-export/${exportId}/download`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error(translateUi("Shkarkimi i eksportit dështoi.", "Export download failed."));
  const blob = await response.blob();
  await downloadResponseBlob(blob, fileName || "vendosja-kualifikimit.xlsx");
}

export type WindowConflictInfo = {
  type: "generation" | "assessor";
  generation: string | null;
  otherWindow: { id: string; unitNumber: string; title: string; releaseAt: string | null; studentDueAt: string; teacherDueAt: string };
};

export type AssessmentWindowPatch = Partial<{
  title: string;
  status: string;
  className: string | null;
  unitName: string | null;
  studentDueAt: string;
  teacherDueAt: string;
  coordinatorDueAt: string | null;
  releaseAt: string;
  confirmOverlap: boolean;
}>;

export type UpdateAssessmentWindowResult =
  | { needsConfirmation: true; conflicts: WindowConflictInfo[] }
  | { needsConfirmation?: false };

export function updateAssessmentWindow(token: string, windowId: string, schoolId: string, patch: AssessmentWindowPatch) {
  return apiFetch<{ data: UpdateAssessmentWindowResult }>(
    `/api/btec/assessment/windows/${windowId}${schoolQs(schoolId)}`,
    { token, method: "PATCH", body: JSON.stringify(patch) },
  ).then((res) => res.data);
}

export function updateAssessmentTeacherDeadline(token: string, windowId: string, schoolId: string, input: { teacherDueAt: string; justification: string }) {
  return apiFetch<{ data: { window: AssessmentWindowListItem | null } }>(
    `/api/btec/assessment/windows/${windowId}/teacher-deadline${schoolQs(schoolId)}`,
    { token, method: "PATCH", body: JSON.stringify(input) },
  ).then((res) => res.data);
}

export type AssessmentWindowDeletionPreview = {
  windowId: string;
  schoolId: string;
  academicYearId: string;
  title: string;
  studentCount: number;
  submissionCount: number;
  fileCount: number;
  fileBytes: number;
  reportCount: number;
  exportCount: number;
  linkedAssessmentCount: number;
  fingerprint: string;
};

export function getAssessmentWindowDeletionPreview(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: AssessmentWindowDeletionPreview }>(
    `/api/btec/assessment/windows/${windowId}/deletion-preview${schoolQs(schoolId)}`,
    { token },
  ).then((response) => response.data);
}

export function deleteAssessmentWindow(
  token: string,
  windowId: string,
  schoolId: string,
  confirmation: { fingerprint: string; confirmationText: "FSHIJ" },
) {
  return apiFetch<{ ok: true }>(
    `/api/btec/assessment/windows/${windowId}${schoolQs(schoolId)}`,
    { token, method: "DELETE", body: JSON.stringify(confirmation) },
  );
}

export function reopenAssessmentWindow(
  token: string,
  windowId: string,
  schoolId: string,
  body: { studentDueAt: string; teacherDueAt: string; justification: string },
) {
  return apiFetch<{ data: unknown }>(
    `/api/btec/assessment/windows/${windowId}/reopen${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify(body) },
  );
}

export type AssessmentCreatePackage = {
  id: string;
  title: string;
  unitNumber: string | null;
  unitName: string | null;
  status: string;
  criteriaCount: number;
};
export type AssessmentCreateAssessor = { id: string; name: string | null; email: string; unitNumbers: string[] };
export type AssessmentCreateStudent = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  gradeClass: string | null;
};
export type AssessmentCreateOptions = {
  packages: AssessmentCreatePackage[];
  assessors: AssessmentCreateAssessor[];
  students: AssessmentCreateStudent[];
};

export function getAssessmentCreateOptions(token: string, schoolId: string) {
  return apiFetch<{ data: AssessmentCreateOptions }>(
    `/api/btec/assessment/create-options${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export type CreateAssessmentWindowInput = {
  unitNumber: string;
  unitName?: string | null;
  learningAimScope: string;
  title: string;
  className?: string | null;
  assessorUserId: string;
  studentIds: string[];
  studentDueAt: string;
  teacherDueAt: string;
  coordinatorDueAt?: string | null;
  releaseAt: string;
  confirmOverlap?: boolean;
};

export type CreateAssessmentWindowResult =
  | { needsConfirmation: true; conflicts: WindowConflictInfo[] }
  | { needsConfirmation?: false; window: { id: string } | null };

export function createAssessmentWindow(token: string, schoolId: string, input: CreateAssessmentWindowInput) {
  return apiFetch<{ data: CreateAssessmentWindowResult }>(
    `/api/btec/assessment/windows${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify(input) },
  ).then((res) => res.data);
}

export type AssessmentCriteriaSnapshotPreview = {
  packageId: string;
  packageTitle: string | null;
  unitNumber: string;
  scope: string;
  criteria: Array<{
    code: string;
    learningAim: string | null;
    level: "PASS" | "MERIT" | "DISTINCTION";
    description: string;
    sortOrder: number;
  }>;
  includedCount: number;
  excludedCount: number;
  checksum: string;
  allowed: boolean;
  reason: string | null;
};

export function previewAssessmentCriteriaSnapshot(token: string, windowId: string, schoolId: string) {
  return apiFetch<{ data: AssessmentCriteriaSnapshotPreview }>(
    `/api/btec/assessment/windows/${encodeURIComponent(windowId)}/criteria-snapshot/preview${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function applyAssessmentCriteriaSnapshot(
  token: string,
  windowId: string,
  schoolId: string,
  input: { packageId: string; checksum: string },
) {
  return apiFetch<{ data: { created: boolean; count: number; checksum: string } }>(
    `/api/btec/assessment/windows/${encodeURIComponent(windowId)}/criteria-snapshot${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify(input) },
  ).then((res) => res.data);
}

// ---------------------------------------------------------------------------
// Chat module
// ---------------------------------------------------------------------------

export type ChatContactUser = { id: string; name: string; email: string; role: string; hasPhoto: boolean };
export type ChatContactGroup = { id: string; name: string; category: string };
export type ChatContacts = { users: ChatContactUser[]; groups: ChatContactGroup[] };

export type ChatConversation = {
  id: string;
  type: "direct" | "group";
  title: string;
  otherId: string | null;
  groupId: string | null;
  groupCategory: string | null;
  hasPhoto: boolean;
  lastMessage: string | null;
  lastMessageAt: string | null;
  unread: number;
};

export type ChatAttachment = { id?: string; name: string; type: string; size: number };

export type ChatAssignmentReference = {
  assessmentId: string;
  assessmentTitle: string;
  subjectName: string;
  dueDate: string;
  questionId: string | null;
  questionPrompt: string | null;
};

export type ChatAssignmentOption = {
  id: string;
  title: string;
  subjectName: string;
  dueDate: string;
  questions: Array<{ id: string; prompt: string }>;
};

export type ChatMessage = {
  id: string;
  senderId: string;
  senderName: string;
  mine: boolean;
  body: string;
  createdAt: string;
  attachment?: ChatAttachment | null;
  assignment?: ChatAssignmentReference | null;
};

export type ChatReceipt = {
  userId: string;
  name: string;
  lastReadAt: string | null;
};

export type ChatTypingUser = { userId: string; name: string };

export const CHAT_ATTACHMENT_MAX_BYTES = 8 * 1024 * 1024;
export const CHAT_BLOCKED_EXTENSIONS = ["zip", "rar", "7z", "tar", "gz", "tgz", "bz2", "xz", "cab", "iso"];

export type WellbeingRequest = {
  id: string;
  reason: string;
  preferredAt: string | null;
  urgency: "normal" | "urgent";
  status: "pending" | "accepted" | "declined" | "cancelled";
  counselorNote: string | null;
  createdAt: string;
  updatedAt: string;
  studentSeenAt: string | null;
  preferredCounselorId: string | null;
  preferredCounselorName: string | null;
};

export type WellbeingAppointment = {
  id: string;
  scheduledAt: string;
  status: string;
  meetingType: "student" | "parent";
  counselorName: string | null;
};

export type WellbeingCounselor = {
  id: string;
  name: string;
};

const viewAsQs = (viewAsUserId?: string) => (viewAsUserId ? `?viewAsUserId=${encodeURIComponent(viewAsUserId)}` : "");

export function fetchStudentWellbeing(viewAsUserId?: string) {
  return apiFetch<{ requests: WellbeingRequest[]; appointments: WellbeingAppointment[] }>(`/api/student/wellbeing${viewAsQs(viewAsUserId)}`);
}

export function fetchWellbeingCounselors(viewAsUserId?: string) {
  return apiFetch<{ counselors: WellbeingCounselor[] }>(`/api/student/wellbeing/counselors${viewAsQs(viewAsUserId)}`).then((res) => res.counselors);
}

export function fetchCounselorBookedSlots(counselorId: string, viewAsUserId?: string) {
  return apiFetch<{ bookedSlots: string[] }>(`/api/student/wellbeing/counselors/${counselorId}/booked-slots${viewAsQs(viewAsUserId)}`).then((res) => res.bookedSlots);
}

export type ViewAsCandidate = {
  id: string;
  name: string;
  email: string;
  gradeClass: string;
  studentName?: string;
};

export function fetchViewAsCandidates(schoolId: string, type: "student" | "parent") {
  return apiFetch<{ candidates: ViewAsCandidate[] }>(`/api/admin/view-as/candidates?type=${type}${schoolQs(schoolId).replace("?", "&")}`)
    .then((res) => res.candidates);
}

// ---- Test users (superadmin view-only preview accounts) ----
export type TestUserRole = "student" | "parent" | "professor" | "student_adviser" | "admin" | "food_company";

export type TestUser = {
  id: string;
  role: TestUserRole;
  label: string;
  gradeClass: string | null;
  linkedStudentId: string | null;
  linkedStudentName: string | null;
};

export function fetchTestUsers(schoolId: string) {
  return apiFetch<{ testUsers: TestUser[] }>(`/api/admin/test-users${schoolQs(schoolId)}`).then((res) => res.testUsers);
}

export function createTestUser(
  schoolId: string,
  input: { role: TestUserRole; name: string; gradeClass?: string; linkedStudentId?: string },
) {
  return apiFetch<{ ok: true; id: string }>(`/api/admin/test-users${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function deleteTestUser(schoolId: string, id: string) {
  return apiFetch<{ ok: true }>(`/api/admin/test-users/${id}${schoolQs(schoolId)}`, { method: "DELETE" });
}

// Auto-create the default set (one per role + a test student per class). Idempotent.
export function seedTestUsers(schoolId: string) {
  return apiFetch<{ ok: true; created: number; repaired: number }>(`/api/admin/test-users/seed${schoolQs(schoolId)}`, { method: "POST" });
}

export type InboxMessage = {
  id: string;
  senderId: string;
  title: string;
  body: string;
  senderName: string;
  createdAt: string;
  readAt: string | null;
  acknowledgedAt: string | null;
  receiptRequired: boolean;
  recipientListHidden: boolean;
  replyMode: BroadcastReplyMode;
  poll: BroadcastPollOption[];
  attachment?: ChatAttachment | null;
  attachments: ChatAttachment[];
};

export type SentBroadcastMessage = {
  id: string;
  title: string;
  body: string;
  audience: "all" | "all_schools" | "class" | "user" | "staff";
  gradeClass: string | null;
  recipientName: string | null;
  recipientScope: "both" | "students" | "parents";
  createdAt: string;
  scheduledAt: string | null;
  emailRequested: boolean;
  recipientCount: number;
  readCount: number;
  acknowledgedCount: number;
  receiptRequired: boolean;
  recipientListHidden: boolean;
  replyMode: BroadcastReplyMode;
  poll: BroadcastPollOption[];
  emailQueuedCount: number;
  emailSentCount: number;
  emailFailedCount: number;
  emailPendingCount: number;
  emailProcessingCount: number;
  emailLastUpdatedAt: string | null;
  attachment?: ChatAttachment | null;
  attachments: ChatAttachment[];
};

export type BroadcastReplyMode = "author_only" | "all" | "disabled";

export type BroadcastPollOption = {
  id: string;
  label: string;
  voteCount: number;
  selected: boolean;
};

export type BroadcastMessageTemplate = {
  id: string;
  name: string;
  title: string;
  body: string;
  updatedAt: string;
};

export type BroadcastMessageReply = {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
  mine: boolean;
};

export type MessageCandidate = {
  id: string;
  fullName: string;
  email: string;
  gradeClass: string | null;
  role: "student" | "professor" | "parent";
};

export type OverviewTimetableRole = "admin" | "professor" | "student" | "parent";

export type OverviewTimetableEntry = {
  id: string;
  dayOfWeek: string;
  slotNumber: number | null;
  slotLabel: string | null;
  startTime: string | null;
  endTime: string | null;
  subjectName: string;
  classLabel: string | null;
  teacherName: string | null;
  room: string;
};

export type OverviewTimetableSlot = {
  id: string;
  slotNumber: number | null;
  label: string;
  startTime: string | null;
  endTime: string | null;
};

export type OverviewTimetableResponse = {
  calendarBreak?: CalendarBreak | null;
  meta?: { lessonSlots?: OverviewTimetableSlot[] };
  termId: string;
  weekTypeId: string;
  onVacation: boolean;
  vacationName: string | null;
  classLabel?: string;
  entries: OverviewTimetableEntry[];
};

export type OverviewTimetableLoadItem = {
  id: string;
  source: "assessment" | "btec" | "substitute";
  kind: "assessment" | "homework" | "project" | "deadline" | "substitution";
  title: string;
  classLabel: string | null;
  subjectName: string | null;
  slotNumber: number | null;
  slotLabel: string | null;
  startTime: string | null;
  endTime: string | null;
  date: string;
};

export type AssessmentTaskSummary = {
  id: string;
  title: string;
  kind: string;
  subjectName: string;
  classLabel: string;
  ownerName: string;
  occurrenceDate: string;
  startsAt: string;
  endsAt: string;
  dueDate: string;
  lateDueDate: string;
  status: string;
  notes: string;
  tasks: Array<{ type: string; prompt: string }>;
};

export type AssessmentClassWorkloadItem = {
  id: string;
  source: "assessment" | "btec";
  kind: "assessment" | "homework" | "project" | "deadline";
  title: string;
  subjectName: string | null;
  ownerName: string | null;
  startTime: string | null;
  endTime: string | null;
  date: string;
};

export type AssessmentClassWorkload = {
  classId: string;
  classLabel: string;
  date: string;
  counts: {
    total: number;
    tests: number;
    homework: number;
    btec: number;
  };
  items: AssessmentClassWorkloadItem[];
};

export function fetchAssessmentClassWorkload(
  input: { classId: string; classLabel?: string; date: string; excludeAssessmentId?: string; schoolId?: string },
  options: Pick<ApiOptions, "token"> = {},
) {
  const params = new URLSearchParams({ classId: input.classId, date: input.date });
  if (input.classLabel) params.set("classLabel", input.classLabel);
  if (input.excludeAssessmentId) params.set("excludeAssessmentId", input.excludeAssessmentId);
  if (input.schoolId) params.set("schoolId", input.schoolId);
  return apiFetch<{ workload: AssessmentClassWorkload }>(`/api/professor/assessment-class-workload?${params.toString()}`, options)
    .then((r) => r.workload);
}

// Read-only detail of a homework/assessment shown in the timetable (professor/admin only).
export function fetchAssessmentSummary(schoolId: string, assessmentId: string) {
  return apiFetch<{ summary: AssessmentTaskSummary }>(`/api/professor/assessments/${assessmentId}/summary${schoolQs(schoolId)}`)
    .then((r) => r.summary);
}

// Student view of a timetable homework/assessment (homework tasks shown; test questions hidden
// until the test is open).
export function fetchStudentAssessmentSummary(assessmentId: string, viewAsUserId?: string) {
  return apiFetch<{ summary: AssessmentTaskSummary }>(`/api/student/assessments/${assessmentId}/summary${viewAsQs(viewAsUserId)}`)
    .then((r) => r.summary);
}

export type OverviewCalendarEvent = {
  id: string;
  date: string;
  kind: string;
  title: string;
  subjectName: string | null;
  classLabel: string | null;
  startTime: string | null;
  endTime: string | null;
};

// Assignments and canonical non-teaching calendar ranges share one role-scoped read.
export function fetchOverviewCalendarData(input: { role: OverviewTimetableRole; schoolId: string; from: string; to: string; viewAsUserId?: string }) {
  const params = new URLSearchParams({ role: input.role, from: input.from, to: input.to, school: input.schoolId });
  if (input.viewAsUserId) params.set("viewAsUserId", input.viewAsUserId);
  return apiFetch<{ events: OverviewCalendarEvent[]; calendarBreaks?: CalendarBreak[] }>(`/api/overview/calendar?${params.toString()}`)
    .then(result => ({ events: result.events, calendarBreaks: result.calendarBreaks || [] }));
}

export function fetchOverviewCalendar(input: Parameters<typeof fetchOverviewCalendarData>[0]) {
  return fetchOverviewCalendarData(input).then(result => result.events);
}

export type OverviewReminder = {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string | null;
};

export function fetchOverviewReminders(input: { schoolId: string; ownerUserId: string; from: string; to: string }) {
  const params = new URLSearchParams({ school: input.schoolId, ownerUserId: input.ownerUserId, from: input.from, to: input.to });
  return apiFetch<{ reminders: OverviewReminder[] }>(`/api/overview/reminders?${params.toString()}`).then((r) => r.reminders);
}

export function createOverviewReminder(input: { schoolId: string; ownerUserId: string; title: string; description?: string; date: string; time?: string }) {
  return apiFetch<{ reminder: OverviewReminder }>("/api/overview/reminders", {
    method: "POST",
    body: JSON.stringify(input),
  }).then((r) => r.reminder);
}

export function deleteOverviewReminder(schoolId: string, ownerUserId: string, reminderId: string) {
  const params = new URLSearchParams({ school: schoolId, ownerUserId });
  return apiFetch<{ ok: true }>(`/api/overview/reminders/${encodeURIComponent(reminderId)}?${params.toString()}`, {
    method: "DELETE",
  });
}

export type RoomNowLesson = {
  room: string;
  subjectName: string;
  classLabel: string;
  teacherName: string;
  slotLabel: string;
  startTime: string;
  endTime: string;
};

// Admin/superadmin: today's lessons per room, to see which subject is being held now.
export function fetchRoomsNow(schoolId: string, dayOfWeek: string) {
  return apiFetch<{ onVacation: boolean; vacationName: string | null; lessons: RoomNowLesson[] }>(
    `/api/overview/rooms-now?dayOfWeek=${encodeURIComponent(dayOfWeek)}${schoolQs(schoolId).replace("?", "&")}`,
  );
}

export function fetchOverviewTimetable(input: { role: OverviewTimetableRole; schoolId: string; viewAsUserId?: string; classLabel?: string; date?: string }) {
  const schoolQuery = schoolQs(input.schoolId);
  const dateQuery = input.date ? `&date=${encodeURIComponent(input.date)}` : "";
  if (input.role === "admin") {
    const classQuery = input.classLabel ? `&class=${encodeURIComponent(input.classLabel)}` : "";
    return apiFetch<OverviewTimetableResponse>(`/api/admin/timetable${schoolQuery}${classQuery}${dateQuery}`);
  }
  const viewAs = input.viewAsUserId ? `${schoolQuery}&viewAsUserId=${encodeURIComponent(input.viewAsUserId)}` : schoolQuery;
  const path = input.role === "professor" ? `/api/professor/my-schedule${schoolQuery}`
    : input.role === "parent" ? `/api/parent/my-schedule${viewAs}` : `/api/student/my-schedule${viewAs}`;
  return apiFetch<OverviewTimetableResponse & { dateEntries?: OverviewTimetableEntry[]; dateOnVacation?: boolean }>(`${path}${dateQuery}`)
    .then(result => input.date && result.dateEntries ? {
      ...result, entries: result.dateEntries, onVacation: result.dateOnVacation ?? result.onVacation,
    } : result);
}

export type { PlanScheduledPreparationTarget } from "../../shared/plan-scheduled-preparation.ts";

export function getPlanScheduledPreparationTarget(
  token: string,
  input: { schoolId: string; academicYearId?: string; timetableEntryId: string; date: string },
) {
  const params = new URLSearchParams({ schoolId: input.schoolId, date: input.date });
  if (input.academicYearId) params.set("academicYearId", input.academicYearId);
  return apiFetch<{ target: import("../../shared/plan-scheduled-preparation.ts").PlanScheduledPreparationTarget }>(
    `/api/professor/my-schedule/${encodeURIComponent(input.timetableEntryId)}/preparation-topic?${params.toString()}`, { token },
  ).then((result) => result.target);
}

export function getTeacherLessonWorkspace(
  target: Pick<import("../../shared/plan-scheduled-preparation.ts").PlanScheduledPreparationTarget, "schoolId" | "academicYearId" | "timetableEntryId" | "date">,
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({ schoolId: target.schoolId, academicYearId: target.academicYearId,
    timetableEntryId: target.timetableEntryId, date: target.date });
  return apiFetch<{ workspace: import("../../shared/teacher-lesson-workspace.ts").TeacherLessonWorkspace }>(
    `/api/professor/lesson-workspace?${params}`, { signal },
  ).then(result => result.workspace);
}

export function fetchOverviewTimetableLoad(input: { role: OverviewTimetableRole; schoolId: string; date: string; dayOfWeek?: string | null; viewAsUserId?: string }) {
  const params = new URLSearchParams({ schoolId: input.schoolId, role: input.role, date: input.date });
  if (input.dayOfWeek) params.set("dayOfWeek", input.dayOfWeek);
  if (input.viewAsUserId) params.set("viewAsUserId", input.viewAsUserId);
  return apiFetch<{ items: OverviewTimetableLoadItem[] }>(`/api/overview/timetable-load?${params.toString()}`);
}

export function fetchInboxMessages(viewAsUserId?: string) {
  return apiFetch<{ messages: InboxMessage[]; unreadCount: number }>(`/api/messages${viewAsQs(viewAsUserId)}`);
}

export function fetchNotificationBellSummary() {
  return apiFetch<{
    notifications: {
      notifications: Array<{ id: string; type: string; title: string; body: string; link: string; isRead: number; createdAt: string }>;
      unread: number;
    };
    inbox: { messages: InboxMessage[]; unreadCount: number };
  }>("/api/notification-summary");
}

// Broadcast so any other mounted component (e.g. the notifications bell) showing the same
// message can drop its own unread state immediately, instead of waiting for its next poll.
export const MESSAGE_READ_EVENT = "edubgt:message-read";

export function markMessageRead(id: string) {
  return apiFetch<{ ok: true }>(`/api/messages/${id}/read`, { method: "POST" }).then((res) => {
    window.dispatchEvent(new CustomEvent(MESSAGE_READ_EVENT, { detail: { id } }));
    return res;
  });
}

export function markAllMessagesRead() {
  return apiFetch<{ ok: true }>("/api/messages/read-all", { method: "POST" });
}

export function acknowledgeMessage(id: string) {
  return apiFetch<{ ok: true; acknowledgedAt: string }>(`/api/messages/${id}/acknowledge`, { method: "POST" });
}

export function voteOnMessage(id: string, optionId: string) {
  return apiFetch<{ ok: true; poll: BroadcastPollOption[] }>(`/api/messages/${id}/vote`, {
    method: "POST",
    body: JSON.stringify({ optionId }),
  });
}

export function fetchMessageReplies(messageId: string) {
  return apiFetch<{ replies: BroadcastMessageReply[] }>(`/api/messages/${messageId}/replies`).then((res) => res.replies);
}

export function replyToMessage(messageId: string, body: string) {
  return apiFetch<{ ok: true; reply: BroadcastMessageReply }>(`/api/messages/${messageId}/replies`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

export function fetchMessageClasses(schoolId: string) {
  return apiFetch<{ classes: string[] }>(`/api/messages/classes${schoolQs(schoolId)}`).then((res) => res.classes);
}

export function fetchMessageCandidates(schoolId: string, role: "student" | "professor" | "parent") {
  return apiFetch<{ candidates: MessageCandidate[] }>(`/api/messages/candidates?role=${role}${schoolQs(schoolId).replace("?", "&")}`)
    .then((res) => res.candidates);
}

export function fetchMessageTemplates(schoolId: string) {
  return apiFetch<{ templates: BroadcastMessageTemplate[] }>(`/api/messages/templates${schoolQs(schoolId)}`).then((res) => res.templates);
}

export function createMessageTemplate(schoolId: string, input: { name: string; title: string; body: string }) {
  return apiFetch<{ template: BroadcastMessageTemplate }>(`/api/messages/templates${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  }).then((res) => res.template);
}

export function deleteMessageTemplate(schoolId: string, templateId: string) {
  return apiFetch<{ ok: true }>(`/api/messages/templates/${encodeURIComponent(templateId)}${schoolQs(schoolId)}`, { method: "DELETE" });
}

export function sendBroadcastMessage(
  schoolId: string,
  input: { title: string; body: string; audience: "all" | "class" | "user" | "staff"; gradeClasses?: string[]; recipientUserIds?: string[]; includeGuardian?: boolean; recipientScope: "both" | "students" | "parents"; sendEmail?: boolean; scheduledAt?: string; receiptRequired?: boolean; recipientListHidden?: boolean; replyMode?: BroadcastReplyMode; pollOptions?: string[] },
  files: File[] = [],
) {
  if (files.length) {
    const form = new FormData();
    form.append("title", input.title);
    form.append("body", input.body);
    form.append("audience", input.audience);
    if (input.gradeClasses) form.append("gradeClasses", JSON.stringify(input.gradeClasses));
    if (input.recipientUserIds) form.append("recipientUserIds", JSON.stringify(input.recipientUserIds));
    if (input.includeGuardian) form.append("includeGuardian", "true");
    form.append("recipientScope", input.recipientScope);
    if (input.sendEmail) form.append("sendEmail", "true");
    if (input.scheduledAt) form.append("scheduledAt", input.scheduledAt);
    if (input.receiptRequired) form.append("receiptRequired", "true");
    if (input.recipientListHidden) form.append("recipientListHidden", "true");
    if (input.replyMode) form.append("replyMode", input.replyMode);
    if (input.pollOptions?.length) form.append("pollOptions", JSON.stringify(input.pollOptions));
    files.forEach((file) => form.append("files", file));
    return apiFetch<{ ok: true; id: string; recipientCount: number; emailRecipientCount: number; scheduledAt: string | null }>(`/api/messages${schoolQs(schoolId)}`, {
      method: "POST",
      body: form,
    });
  }
  return apiFetch<{ ok: true; id: string; recipientCount: number; emailRecipientCount: number; scheduledAt: string | null }>(`/api/messages${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// Fetch a broadcast message's attachment as an object URL, for inline image previews.
export async function fetchMessageAttachmentUrl(messageId: string, attachmentId?: string): Promise<string> {
  const path = attachmentId
    ? `/api/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`
    : `/api/messages/attachments/${encodeURIComponent(messageId)}`;
  const response = await fetch(`${API_URL}${path}`, viewAsInit());
  if (!response.ok) throw new Error("attachment failed");
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export function fetchSentMessages(schoolId: string) {
  return apiFetch<{ messages: SentBroadcastMessage[] }>(`/api/messages/sent${schoolQs(schoolId)}`).then((res) => res.messages);
}

export function deleteScheduledBroadcastMessage(schoolId: string, messageId: string) {
  return apiFetch<{ ok: true }>(`/api/messages/${encodeURIComponent(messageId)}/scheduled${schoolQs(schoolId)}`, {
    method: "DELETE",
  });
}

export function createWellbeingRequest(input: { reason: string; preferredAt?: string | null; urgency?: "normal" | "urgent"; counselorId?: string | null }) {
  return apiFetch<{ ok: true; id: string }>(`/api/student/wellbeing/requests`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function cancelWellbeingRequest(id: string) {
  return apiFetch<{ ok: true }>(`/api/student/wellbeing/requests/${id}/cancel`, { method: "POST" });
}

export function markWellbeingRequestsSeen() {
  return apiFetch<{ ok: true }>("/api/student/wellbeing/requests/seen", { method: "POST" });
}

// ---- Food ordering ----
export type FoodMenuItem = { id: string; catalogItemId: string | null; name: string; description: string; price: number; menuDate: string; isMonthlyDefault: boolean; customizationOptions: string[]; imageUrl: string | null };
export type FoodDrink = { id: string; name: string; price: number };
export type FoodOrder = { id: string; menuDate: string; status: string; itemId: string | null; itemName: string; drinkName: string | null; createdAt: string; source: "manual" | "monthly_default" | "custom"; note: string };
export type FoodOrderSubmission = { itemName: string; drinkName: string | null; source: "manual" | "monthly_default" | "custom"; note: string };
export type FoodOrderSource = "manual" | "monthly_default" | "admin_manual" | "custom";
export type FoodMonthlyPaymentStatus = "paid" | "unpaid";
export type FoodOrderGroup = { itemId: string; itemName: string; orderers: Array<{ orderId: string; userId: string | null; name: string; drinkName: string | null; source: FoodOrderSource; role: string; note: string; monthlyPaymentStatus: FoodMonthlyPaymentStatus | null }> };
export type FoodCompanyOrders = { date: string; items: FoodOrderGroup[] };
export type AdminFoodOrder = { id: string; date: string; itemName: string; ordererName: string; status: "placed" | "cancelled"; note: string; placedBy: "self" | "administration" | "monthly_default"; placedByName: string | null; createdAt: string };
export type FoodMenuTemplate = { id: string; cycleWeek: number; dayOfWeek: number; name: string; description: string; price: number; isMonthlyDefault: boolean; customizationOptions: string[] };
export type FoodOrderRow = { date: string; itemName: string; drinkName?: string | null; price: number; ordererName: string; ordererRole: string; planType: FoodPlanType };
export type FoodCatalogItem = { id: string; name: string; description: string; price: number; itemKind: "food" | "drink"; customizationOptions: string[]; imageUrl: string | null };
export type FoodMenuCopyResult = { ok: true; sourceSchoolId: string; targetSchoolId: string; catalogItems: number; templates: number; menuItems: number; preservedOrderedMenuItems: number; settingsCopied: boolean; repeated: boolean };
export type FoodPlanType = "none" | "daily" | "monthly";
export type FoodCompanyStudent = { id: string; firstName: string; lastName: string; fullName: string; gradeClass: string; generation: string; planType: FoodPlanType };
export type FoodCompanyPendingStudent = { id: string; firstName: string; lastName: string; fullName: string; gradeClass: string; generation: string };
export type FoodCompanyStaffMember = { id: string; fullName: string; role: string; planType: FoodPlanType };
export type AdminFoodOrderStudent = { id: string; userId: string | null; fullName: string; gradeClass: string };
type AdminFoodManualOrderItem =
  | { menuItemId: string; catalogItemId?: never }
  | { menuItemId?: never; catalogItemId: string }
  | { menuItemId?: never; catalogItemId?: never; requestText: string };

export type AdminFoodManualOrderInput = AdminFoodManualOrderItem & (
  | { deliverySchoolId: string; orderFor: "student"; rosterStudentId: string }
  | { deliverySchoolId: string; orderFor: "guest"; firstName: string; lastName: string }
);

function foodDateQs(date?: string) {
  return date ? `&date=${encodeURIComponent(date)}` : "";
}

export function fetchFoodMenu(schoolId: string, date?: string) {
  return apiFetch<{ date: string; items: FoodMenuItem[]; drinks: FoodDrink[]; catalogDailyOrdering: boolean; planType: FoodPlanType; orderOpenTime: string; orderCloseTime: string; orderingOpen: boolean }>(
    `/api/food/menu${schoolQs(schoolId)}${foodDateQs(date)}`,
  );
}

export function fetchMyFoodOrder(schoolId: string, date?: string) {
  return apiFetch<{ order: FoodOrder | null; submission: FoodOrderSubmission | null }>(`/api/food/orders/mine${schoolQs(schoolId)}${foodDateQs(date)}`);
}

export function placeFoodOrder(schoolId: string, input: { menuItemId?: string; catalogItemId?: string; drinkCatalogItemId?: string; date: string; note?: string; deliverySchoolId?: string }) {
  return apiFetch<{ ok: true }>(`/api/food/orders${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function fetchAdminFoodOrderStudents(schoolId: string) {
  return apiFetch<{ students: AdminFoodOrderStudent[] }>(`/api/admin/food/order-students${schoolQs(schoolId)}`);
}

export function placeCustomFoodOrder(schoolId: string, input: { date: string; requestText: string; deliverySchoolId?: string }) {
  return apiFetch<{ ok: true }>(`/api/food/orders/custom${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function createAdminFoodManualOrder(schoolId: string, input: AdminFoodManualOrderInput) {
  return apiFetch<{ ok: true; id: string; orderFor: "student" | "guest" }>(`/api/admin/food/manual-orders${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function fetchAdminFoodOrders(schoolId: string, date?: string) {
  return apiFetch<{ date: string; orders: AdminFoodOrder[] }>(`/api/admin/food/orders${schoolQs(schoolId)}${foodDateQs(date)}`);
}

export function cancelFoodOrder(id: string) {
  return apiFetch<{ ok: true }>(`/api/food/orders/${id}/cancel`, { method: "POST" });
}

export function fetchCompanyFoodMenu(schoolId: string, date?: string) {
  return apiFetch<{ date: string; items: FoodMenuItem[] }>(`/api/food-company/menu${schoolQs(schoolId)}${foodDateQs(date)}`);
}

export function createFoodMenuItem(schoolId: string, input: { date: string; catalogItemId: string }) {
  return apiFetch<{ ok: true; id: string }>(`/api/food-company/menu${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function deleteFoodMenuItem(schoolId: string, id: string) {
  return apiFetch<{ ok: true }>(`/api/food-company/menu/${id}${schoolQs(schoolId)}`, { method: "DELETE" });
}

export function fetchCompanyFoodOrders(schoolId: string, date?: string) {
  return apiFetch<FoodCompanyOrders>(`/api/food-company/orders${schoolQs(schoolId)}${foodDateQs(date)}`);
}

export function deleteCompanyFoodOrder(schoolId: string, id: string) {
  return apiFetch<{ ok: true }>(`/api/food-company/orders/${id}${schoolQs(schoolId)}`, { method: "DELETE" });
}

export function updateFoodMonthlyPaymentStatus(schoolId: string, studentUserId: string, paymentMonth: string, status: FoodMonthlyPaymentStatus) {
  return apiFetch<{ ok: true; status: FoodMonthlyPaymentStatus }>(`/api/food-company/monthly-payments/${encodeURIComponent(studentUserId)}${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify({ paymentMonth, status }),
  });
}

export function fetchCompanyFoodOrdersRange(schoolId: string, start: string, end: string) {
  return apiFetch<{ rows: FoodOrderRow[] }>(
    `/api/food-company/orders/range${schoolQs(schoolId)}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
  );
}

export function fetchFoodCompanyStudents(schoolId: string) {
  return apiFetch<{ students: FoodCompanyStudent[]; pendingStudents: FoodCompanyPendingStudent[] }>(`/api/food-company/students${schoolQs(schoolId)}`);
}

export function updateFoodCompanyStudentPlan(schoolId: string, studentId: string, planType: FoodPlanType) {
  return apiFetch<{ ok: true }>(`/api/food-company/students/${studentId}/plan${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify({ planType }),
  });
}

export function clearAllFoodCompanyStudentPlans(schoolId: string) {
  return apiFetch<{ ok: true; updated: number }>(`/api/food-company/students/plans${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify({ planType: "none" }),
  });
}

export function setAllFoodCompanyStudentPlans(schoolId: string, planType: "daily") {
  return apiFetch<{ ok: true; updated: number }>(`/api/food-company/students/plans${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify({ planType }),
  });
}

export function fetchFoodCompanyStaff(schoolId: string) {
  return apiFetch<{ staff: FoodCompanyStaffMember[] }>(`/api/food-company/staff${schoolQs(schoolId)}`);
}

export function updateFoodCompanyStaffPlan(schoolId: string, staffId: string, planType: FoodPlanType) {
  return apiFetch<{ ok: true }>(`/api/food-company/staff/${staffId}/plan${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify({ planType }),
  });
}

export function clearAllFoodCompanyStaffPlans(schoolId: string) {
  return apiFetch<{ ok: true; updated: number }>(`/api/food-company/staff/plans${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify({ planType: "none" }),
  });
}

export function setAllFoodCompanyStaffPlans(schoolId: string, planType: "daily") {
  return apiFetch<{ ok: true; updated: number }>(`/api/food-company/staff/plans${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify({ planType }),
  });
}

export function fetchFoodOrderSettings(schoolId: string) {
  return apiFetch<{ orderOpenTime: string; orderCloseTime: string }>(`/api/food-company/settings${schoolQs(schoolId)}`);
}

export function updateFoodOrderSettings(schoolId: string, input: { orderOpenTime: string; orderCloseTime: string }) {
  return apiFetch<{ ok: true; orderOpenTime: string; orderCloseTime: string }>(`/api/food-company/settings${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function fetchFoodMenuTemplates(schoolId: string) {
  return apiFetch<{ templates: FoodMenuTemplate[]; today: string }>(`/api/food-company/templates${schoolQs(schoolId)}`);
}

export function createFoodMenuTemplate(schoolId: string, input: { cycleWeek: number; dayOfWeek: number; catalogItemId: string }) {
  return apiFetch<{ ok: true; id: string }>(`/api/food-company/templates${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function deleteFoodMenuTemplate(schoolId: string, id: string) {
  return apiFetch<{ ok: true }>(`/api/food-company/templates/${id}${schoolQs(schoolId)}`, { method: "DELETE" });
}

export function setFoodMenuTemplateMonthlyDefault(schoolId: string, id: string) {
  return apiFetch<{ ok: true }>(`/api/food-company/templates/${id}/monthly-default${schoolQs(schoolId)}`, { method: "PUT" });
}

export function applyFoodMenuTemplates(schoolId: string, input: { startDate: string; endDate: string }) {
  return apiFetch<{ ok: true; created: number }>(`/api/food-company/templates/apply${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function fetchFoodCatalog(schoolId: string) {
  return apiFetch<{ items: FoodCatalogItem[] }>(`/api/food-company/catalog${schoolQs(schoolId)}`);
}

export function createFoodCatalogItem(schoolId: string, input: { name: string; description?: string; price: number; itemKind?: "food" | "drink" }) {
  return apiFetch<{ ok: true; id: string }>(`/api/food-company/catalog${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateFoodCatalogItem(schoolId: string, id: string, input: { name: string; description?: string; price: number }) {
  return apiFetch<{ ok: true }>(`/api/food-company/catalog/${encodeURIComponent(id)}${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function updateFoodCatalogItemOptions(schoolId: string, id: string, customizationOptions: string[]) {
  return apiFetch<{ ok: true; customizationOptions: string[] }>(`/api/food-company/catalog/${id}/options${schoolQs(schoolId)}`, {
    method: "PUT",
    body: JSON.stringify({ customizationOptions }),
  });
}

export function uploadFoodCatalogItemPhoto(schoolId: string, id: string, file: File) {
  return apiFetch<{ ok: true; imageUrl: string }>(`/api/food-company/catalog/${encodeURIComponent(id)}/photo${schoolQs(schoolId)}`, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
}

export function deleteFoodCatalogItemPhoto(schoolId: string, id: string) {
  return apiFetch<{ ok: true }>(`/api/food-company/catalog/${encodeURIComponent(id)}/photo${schoolQs(schoolId)}`, { method: "DELETE" });
}

export function deleteFoodCatalogItem(schoolId: string, id: string) {
  return apiFetch<{ ok: true }>(`/api/food-company/catalog/${id}${schoolQs(schoolId)}`, { method: "DELETE" });
}

export function copyFoodMenuToSchool(sourceSchoolId: string, targetSchoolId: string) {
  return apiFetch<FoodMenuCopyResult>(`/api/food-company/menu-copy${schoolQs(sourceSchoolId)}`, {
    method: "POST",
    headers: { "Idempotency-Key": crypto.randomUUID() },
    body: JSON.stringify({ targetSchoolId }),
  });
}

export type CounselorMeetRequest = {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  gradeClass: string;
  reason: string;
  preferredAt: string | null;
  urgency: "normal" | "urgent";
  status: "pending" | "accepted" | "declined" | "cancelled";
  counselorNote: string | null;
  createdAt: string;
  updatedAt: string;
  preferredCounselorId: string | null;
  preferredCounselorName: string | null;
  isForMe: boolean;
};

export function fetchCounselorMeetRequests(schoolId: string) {
  return apiFetch<{ requests: CounselorMeetRequest[] }>(`/api/student-care/counselor/student-meet${schoolQs(schoolId)}`);
}

export function acceptCounselorMeetRequest(schoolId: string, id: string, input: { scheduledAt: string; note?: string }) {
  return apiFetch<{ ok: true; appointmentId: string }>(`/api/student-care/counselor/student-meet/${id}/accept${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function declineCounselorMeetRequest(schoolId: string, id: string, note?: string) {
  return apiFetch<{ ok: true }>(`/api/student-care/counselor/student-meet/${id}/decline${schoolQs(schoolId)}`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export function fetchChatBlocks(token: string) {
  return apiFetch<{ blocked: string[] }>(`/api/chat/blocks`, { token }).then((res) => res.blocked);
}

export function blockChatUser(token: string, userId: string) {
  return apiFetch<{ ok: true }>(`/api/chat/blocks`, { token, method: "POST", body: JSON.stringify({ userId }) });
}

export function unblockChatUser(token: string, userId: string) {
  return apiFetch<{ ok: true }>(`/api/chat/blocks/${userId}`, { token, method: "DELETE" });
}

export function fetchChatContacts(token: string, schoolId: string, activeRole?: string) {
  const roleQuery = activeRole ? `&activeRole=${encodeURIComponent(activeRole)}` : "";
  return apiFetch<ChatContacts>(`/api/chat/contacts${schoolQs(schoolId)}${roleQuery}`, { token });
}

export function fetchChatConversations(token: string, schoolId: string) {
  return apiFetch<{ conversations: ChatConversation[] }>(`/api/chat/conversations${schoolQs(schoolId)}`, { token })
    .then((res) => res.conversations);
}

export function openChatConversation(token: string, schoolId: string, target: { userId?: string; groupId?: string }, activeRole?: string) {
  return apiFetch<{ conversationId: string }>(`/api/chat/conversations${schoolQs(schoolId)}`, {
    token,
    method: "POST",
    body: JSON.stringify({ userId: target.userId, groupId: target.groupId, activeRole }),
  }).then((res) => res.conversationId);
}

export function fetchChatMessages(token: string, conversationId: string, after?: string) {
  const qs = after ? `?after=${encodeURIComponent(after)}` : "";
  return apiFetch<{ messages: ChatMessage[]; receipts?: ChatReceipt[]; typing?: ChatTypingUser[] }>(`/api/chat/conversations/${conversationId}/messages${qs}`, { token })
    .then((res) => ({ messages: res.messages, receipts: res.receipts ?? [], typing: res.typing ?? [] }));
}

export function markChatConversationRead(token: string, conversationId: string, readAt: string) {
  return apiFetch<{ ok: true; readAt: string }>(`/api/chat/conversations/${conversationId}/read`, {
    token,
    method: "POST",
    body: JSON.stringify({ readAt }),
  });
}

export function setChatTyping(token: string, conversationId: string, typing: boolean) {
  return apiFetch<{ ok: true }>(`/api/chat/conversations/${conversationId}/typing`, {
    token,
    method: typing ? "PUT" : "DELETE",
  });
}

export function fetchChatAssignmentOptions(token: string, conversationId: string) {
  return apiFetch<{ assignments: ChatAssignmentOption[] }>(`/api/chat/conversations/${conversationId}/assignment-options`, { token })
    .then((res) => res.assignments);
}

export function sendChatMessage(token: string, conversationId: string, body: string, file?: File | null, assignment?: { assessmentId: string; questionId?: string | null } | null) {
  if (file) {
    const form = new FormData();
    form.append("body", body);
    form.append("file", file);
    if (assignment) form.append("assignment", JSON.stringify(assignment));
    return apiFetch<{ message: ChatMessage }>(`/api/chat/conversations/${conversationId}/messages`, {
      token,
      method: "POST",
      body: form,
    }).then((res) => res.message);
  }
  return apiFetch<{ message: ChatMessage }>(`/api/chat/conversations/${conversationId}/messages`, {
    token,
    method: "POST",
    body: JSON.stringify({ body, assignment: assignment || undefined }),
  }).then((res) => res.message);
}

export type ChatConversationMember = { id: string; name: string; role: string; mine: boolean };

export function fetchChatConversationMembers(token: string, conversationId: string) {
  return apiFetch<{ members: ChatConversationMember[] }>(`/api/chat/conversations/${conversationId}/members`, { token })
    .then((res) => res.members);
}

export function leaveChatConversation(token: string, conversationId: string) {
  return apiFetch<{ ok: true }>(`/api/chat/conversations/${conversationId}/leave`, { token, method: "DELETE" });
}

// Generic authenticated file download (blob + temporary object URL), for any GET file endpoint.
export type FileDownloadProgress = (loaded: number, total: number | null) => void;

async function readBlobWithProgress(response: Response, onProgress: FileDownloadProgress) {
  const total = Number(response.headers.get("content-length")) || null;
  if (!response.body) { const blob = await response.blob(); onProgress(blob.size, total); return blob; }
  const reader = response.body.getReader(), chunks: Uint8Array[] = [];
  let loaded = 0;
  onProgress(0, total);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value); loaded += value.byteLength; onProgress(loaded, total);
  }
  return new Blob(chunks as BlobPart[], { type: response.headers.get("content-type") || "application/octet-stream" });
}

export async function fetchFileBlob(path: string, signal?: AbortSignal, onProgress?: FileDownloadProgress) {
  if (!path.startsWith("/api/")) throw new Error(translateUi("Adresa e skedarit nuk është e vlefshme.", "The file address is invalid."));
  return retryTransientRead(async (readSignal) => {
  const { response } = await sessionTransport.request(withElearningAcademicYear(path), { ...viewAsInit(), signal: readSignal });
  if (!response.ok) throw new ApiRequestError(response.status === 401 ? translateUi("Sesioni ka skaduar. Hyr përsëri.", "The session has expired. Sign in again.")
    : response.status === 403 ? translateUi("Materiali nuk është i disponueshëm për këtë llogari ose këtë orar.", "The material is unavailable for this account or timetable.")
      : response.status === 404 ? translateUi("Skedari nuk u gjet.", "File not found.") : response.status === 413 ? translateUi("Skedarët tejkalojnë kufirin e ZIP-it. Shkarkoji veçmas.", "The files exceed the ZIP size limit. Download them separately.") : translateUi("Skedari nuk u lexua. Provo përsëri.", "The file could not be read. Try again."), response.status);
  return onProgress ? readBlobWithProgress(response, onProgress) : response.blob();
  // Large session/activity ZIPs are built on the server for longer than the 60 s read deadline.
  }, signal, { timeoutMs: 0 });
}

export type ActivityWorkScope = { cardId: string; teacher?: boolean; schoolId?: string; asProfessor?: boolean };
export function activityWorkPath(scope: ActivityWorkScope, suffix = '') {
  const query = new URLSearchParams();
  if (scope.schoolId) query.set('schoolId', scope.schoolId);
  if (scope.teacher) { query.set('audience', 'teacher'); if(scope.asProfessor !== false) query.set('as', 'professor'); }
  return `/api/elearning/activity-work/${encodeURIComponent(scope.cardId)}${suffix}${query.size ? `?${query}` : ''}`;
}
export async function fetchActivityWork(scope: ActivityWorkScope, signal?: AbortSignal) {
  return (await apiFetch<{view: import('../../shared/activity-submissions').ActivityWorkView}>(activityWorkPath(scope), {signal})).view;
}
export async function mutateActivityWork(scope: ActivityWorkScope, suffix: string, method: 'POST'|'PUT'|'DELETE', body: Record<string, unknown>|FormData, requestId?: string) {
  return (await apiFetch<{view: import('../../shared/activity-submissions').ActivityWorkView}>(activityWorkPath(scope,suffix), {
    method, body: body instanceof FormData ? body : JSON.stringify(body),
    headers: requestId ? {'Idempotency-Key': requestId} : undefined,
  })).view;
}
export async function fetchActivityCollections(schoolId: string, signal?: AbortSignal) {
  return (await apiFetch<{collections: import('../../shared/activity-submissions').ActivityCollectionOverview[]}>(
    `/api/professor/activity-work?${new URLSearchParams({schoolId,as:'professor'})}`,{signal})).collections;
}

export async function downloadFile(path: string, fileName: string) {
  if (!await downloadFileIfAvailable(path, fileName)) throw new Error(translateUi("Skedari nuk u gjet.", "File not found."));
}

export async function downloadApiFileUrl(url: string, fileName = translateUi("Dokument", "Document")) {
  const parsed = new URL(url, API_URL);
  if (![configuredApiUrl, directApiUrl].includes(parsed.origin) || !parsed.pathname.startsWith("/api/")) throw new Error(translateUi("Adresa e skedarit nuk është e vlefshme.", "The file address is invalid."));
  await downloadFile(`${parsed.pathname}${parsed.search}`, fileName);
}

export async function downloadFileIfAvailable(path: string, fileName: string) {
  const { response } = await sessionTransport.request(withElearningAcademicYear(path), viewAsInit());
  if (response.status === 404) return false;
  if (!response.ok) throw new Error(response.status === 401 ? translateUi("Sesioni ka skaduar. Hyr përsëri.", "The session has expired. Sign in again.") : translateUi("Shkarkimi dështoi. Provo përsëri.", "Download failed. Try again."));
  const contentDisposition = response.headers.get("Content-Disposition") || "";
  const encodedName = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)?.[1];
  const quotedName = /filename="([^"]+)"/i.exec(contentDisposition)?.[1];
  let responseFileName = fileName;
  try {
    responseFileName = encodedName ? decodeURIComponent(encodedName) : quotedName || fileName;
  } catch {
    responseFileName = quotedName || fileName;
  }
  await downloadResponseBlob(await response.blob(), responseFileName);
  return true;
}

export async function downloadResponseBlob(blob: Blob, fileName: string) {
  const { isNativeApp, downloadNativeFile } = await import("./native-app.ts");
  if (isNativeApp()) {
    await downloadNativeFile(blob, fileName);
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export async function fetchAuthenticatedBlobUrl(path: string): Promise<string> {
  const response = await fetch(`${API_URL}${path}`, viewAsInit());
  if (!response.ok) throw new Error("image download failed");
  return URL.createObjectURL(await response.blob());
}

export type LostFoundItem = {
  id: string;
  title: string;
  description: string;
  foundLocation: string;
  foundDate: string;
  status: "active" | "returned";
  createdByName: string;
  createdAt: string;
  photoPath: string;
};

export function fetchLostFoundItems(schoolId: string) {
  return apiFetch<{ items: LostFoundItem[]; canManage: boolean }>(`/api/elearning/lost-found?schoolId=${encodeURIComponent(schoolId)}`);
}

export function createLostFoundItem(schoolId: string, form: FormData) {
  return apiFetch<{ ok: true; id: string }>(`/api/elearning/lost-found?schoolId=${encodeURIComponent(schoolId)}`, { method: "POST", body: form });
}

export function updateLostFoundStatus(schoolId: string, id: string, status: "active" | "returned") {
  return apiFetch<{ ok: true }>(`/api/elearning/lost-found/${encodeURIComponent(id)}?schoolId=${encodeURIComponent(schoolId)}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export type ElearningEvent = {
  id: string;
  title: string;
  destination: string;
  description: string;
  startsOn: string;
  endsOn: string;
  status: "open" | "closed";
  selectionDeadlineAt: string | null;
  selectedSupervisorUserId: string | null;
  supervisors: Array<{ userId: string; name: string; capacity: number; choiceCount: number; students: Array<{ userId: string; name: string }> }>;
};

export function fetchElearningEvents(schoolId: string) {
  return apiFetch<{ events: ElearningEvent[]; canManage: boolean; isStudent: boolean; availableProfessors: Array<{ id: string; name: string }> }>(`/api/elearning/events?schoolId=${encodeURIComponent(schoolId)}`);
}

export function createElearningEvent(schoolId: string, payload: { title: string; destination: string; description: string; startsOn: string; endsOn: string; selectionDeadlineAt: string | null; supervisors: Array<{ userId: string; capacity: number }> }) {
  return apiFetch<{ ok: true; id: string }>(`/api/elearning/events?schoolId=${encodeURIComponent(schoolId)}`, { method: "POST", body: JSON.stringify(payload) });
}

export function chooseEventSupervisor(schoolId: string, eventId: string, supervisorUserId: string) {
  return apiFetch<{ ok: true }>(`/api/elearning/events/${encodeURIComponent(eventId)}/supervisor-choice?schoolId=${encodeURIComponent(schoolId)}`, { method: "POST", body: JSON.stringify({ supervisorUserId }) });
}

export function updateElearningEventStatus(schoolId: string, eventId: string, status: "open" | "closed") {
  return apiFetch<{ ok: true }>(`/api/elearning/events/${encodeURIComponent(eventId)}?schoolId=${encodeURIComponent(schoolId)}`, { method: "PATCH", body: JSON.stringify({ status }) });
}

// --- Përpjekjet (arkivi i ridorëzimeve) ---

export type BtecAttemptArtifact = { id: string; name: string; fileSize: number; kind: "file" | "report" };

export type BtecAttempt = {
  id: string;
  attemptNumber: number;
  finalGrade: string | null;
  closedAt: string;
  note: string | null;
  files: Array<{ id: string; originalFilename: string; contentType: string; fileSize: number }>;
  reports: Array<{ id: string; reportKind: string; fileName: string; fileSize: number }>;
};

export type BtecAttemptsOverview = {
  currentAttempt: number;
  canOpenSecondAttempt: boolean;
  attempts: BtecAttempt[];
};

export function listBtecStudentAttempts(token: string, windowId: string, studentId: string, schoolId: string) {
  return apiFetch<{ data: BtecAttemptsOverview }>(
    `/api/btec/assessment/windows/${encodeURIComponent(windowId)}/submissions/${encodeURIComponent(studentId)}/attempts${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function openBtecSecondAttempt(token: string, windowId: string, studentId: string, schoolId: string, note?: string) {
  return apiFetch<{ data: { attemptId: string; archivedFiles: number; archivedReports: number } }>(
    `/api/btec/assessment/windows/${encodeURIComponent(windowId)}/submissions/${encodeURIComponent(studentId)}/second-attempt${schoolQs(schoolId)}`,
    { token, method: "POST", body: JSON.stringify({ note: note ?? null }) },
  ).then((res) => res.data);
}

export async function downloadBtecAttemptArtifact(
  windowId: string,
  studentId: string,
  attemptId: string,
  artifact: { id: string; name: string; kind: "file" | "report" },
  schoolId: string,
): Promise<void> {
  const query = schoolQs(schoolId);
  const url = `${API_URL}/api/btec/assessment/windows/${encodeURIComponent(windowId)}`
    + `/submissions/${encodeURIComponent(studentId)}/attempts/${encodeURIComponent(attemptId)}`
    + `/files/${encodeURIComponent(artifact.id)}/download${query}${query ? "&" : "?"}kind=${artifact.kind}`;
  const response = await fetch(url, viewAsInit());
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || "download failed");
  }
  const blob = await response.blob();
  await downloadResponseBlob(blob, artifact.name);
}

// --- A learner's submitted work, for the assessor ---

export type BtecSubmissionFile = {
  id: string;
  fileKind: string;
  originalFilename: string;
  fileSize: number;
  contentType: string;
  // Nuk kthehen më: adapteri i evidencës nuk i ekspozon, sepse kanali i Qendrës së Vlerësimeve
  // nuk i mban në të njëjtën formë. Mbeten opsionale për përgjigjet e vjetra.
  uploadedBy?: string;
  createdAt?: string;
};

export function listBtecStudentSubmissionFiles(token: string, windowId: string, studentId: string, schoolId: string) {
  return apiFetch<{ data: { submittedAt: string | null; files: BtecSubmissionFile[] } }>(
    `/api/btec/assessment/windows/${encodeURIComponent(windowId)}/submissions/${encodeURIComponent(studentId)}/files${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

function btecStudentFileUrl(windowId: string, studentId: string, fileId: string, schoolId: string) {
  return `${API_URL}/api/btec/assessment/windows/${encodeURIComponent(windowId)}`
    + `/submissions/${encodeURIComponent(studentId)}/files/${encodeURIComponent(fileId)}/download${schoolQs(schoolId)}`;
}

// The route is cookie/session authorized, so the bytes have to come through fetch rather than a
// plain link — which suits preview too, since both need the blob anyway.
export async function fetchBtecStudentSubmissionFileBlob(windowId: string, studentId: string, fileId: string, schoolId: string) {
  const response = await fetch(btecStudentFileUrl(windowId, studentId, fileId, schoolId), viewAsInit());
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || "download failed");
  }
  return response.blob();
}

export async function downloadBtecStudentSubmissionFile(
  windowId: string,
  studentId: string,
  file: { id: string; originalFilename: string },
  schoolId: string,
): Promise<void> {
  const blob = await fetchBtecStudentSubmissionFileBlob(windowId, studentId, file.id, schoolId);
  await downloadResponseBlob(blob, file.originalFilename);
}

// Caller owns the returned object URL and must revoke it when the preview closes.
export async function openBtecStudentSubmissionFilePreview(
  windowId: string,
  studentId: string,
  fileId: string,
  schoolId: string,
): Promise<string> {
  return URL.createObjectURL(await fetchBtecStudentSubmissionFileBlob(windowId, studentId, fileId, schoolId));
}

export async function downloadBtecFinalDeliveries(windowId: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/btec/assessment/windows/${encodeURIComponent(windowId)}/final-deliveries.zip`, viewAsInit());
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || "download failed");
  }
  const disposition = response.headers.get("content-disposition") || "";
  const matchedName = disposition.match(/filename="([^"]+)"/i)?.[1];
  const blob = await response.blob();
  await downloadResponseBlob(blob, matchedName || "BTEC_Delivery_Finale.zip");
}

export async function downloadBtecStageDeliveries(windowId: string, stageNumber: number): Promise<void> {
  const response = await fetch(
    `${API_URL}/api/btec/assessment/windows/${encodeURIComponent(windowId)}/stages/${stageNumber}/deliveries.zip`,
    viewAsInit(),
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error || "download failed");
  }
  const disposition = response.headers.get("content-disposition") || "";
  const matchedName = disposition.match(/filename="([^"]+)"/i)?.[1];
  const blob = await response.blob();
  await downloadResponseBlob(blob, matchedName || `BTEC_Stage_${stageNumber}_Dorezimet.zip`);
}

export type BtecReviewImportPreviewStudent = {
  studentId: string;
  studentName: string;
  classLabel: string;
  criterionCount: number;
  metCount: number;
  manualReviewCount: number;
  proposedGrade: string | null;
  declaredGrade: string | null;
  gradeAdjusted: boolean;
};

export type BtecReviewImportPreview = {
  packageType: "btec_final_deliveries_review" | "btec_stage_deliveries_review";
  windowId: string;
  stageNumber: number;
  studentCount: number;
  criterionResultCount: number;
  manualReviewCount: number;
  students: BtecReviewImportPreviewStudent[];
  imported?: boolean;
};

function btecReviewImportRequest(
  token: string,
  windowId: string,
  file: File,
  action: "preview" | "apply",
) {
  const form = new FormData();
  form.append("file", file, file.name);
  return apiFetch<{ data: BtecReviewImportPreview }>(
    `/api/btec/assessment/windows/${encodeURIComponent(windowId)}/review-import/${action}`,
    { token, method: "POST", body: form },
  ).then((response) => response.data);
}

export function previewBtecReviewImport(token: string, windowId: string, file: File) {
  return btecReviewImportRequest(token, windowId, file, "preview");
}

export function applyBtecReviewImport(token: string, windowId: string, file: File) {
  return btecReviewImportRequest(token, windowId, file, "apply");
}

// Fetch an attachment as an object URL; used for inline image previews.
export async function fetchChatAttachmentUrl(token: string, messageId: string): Promise<string> {
  const response = await fetch(`${API_URL}/api/chat/attachments/${messageId}`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("attachment failed");
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

// Fetch an attachment as a blob and trigger a download/open.
export async function downloadChatAttachment(token: string, messageId: string, fileName: string) {
  const response = await fetch(`${API_URL}/api/chat/attachments/${messageId}`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("download failed");
  const blob = await response.blob();
  await downloadResponseBlob(blob, fileName);
}

// --- Assessment plagiarism (text-overlap signal) ---

export type AssessmentPlagiarismDocRef = {
  studentUserId: string;
  studentName: string;
  classLabel: string;
  assessmentId: string;
  sameAssessment: boolean;
};

export type AssessmentPlagiarismPair = {
  a: AssessmentPlagiarismDocRef;
  b: AssessmentPlagiarismDocRef;
  score: number;
  samples: string[];
};

export type AssessmentPlagiarismFinding = {
  members: AssessmentPlagiarismDocRef[];
  sharedShingles: number;
  samples: string[];
  studentCount?: number;
};

export type AssessmentPlagiarismQuestion = {
  questionId: string;
  promptExcerpt: string;
  type: string;
  medianWords: number;
  comparedDocs: number;
  pairs: AssessmentPlagiarismPair[];
  groups: AssessmentPlagiarismFinding[];
  widespread: AssessmentPlagiarismFinding[];
  restatedOnly: AssessmentPlagiarismDocRef[];
};

export type AssessmentPlagiarismResult = {
  assessmentId: string;
  comparedStudents: number;
  comparedQuestions: number;
  pendingFiles: number;
  unreadableFiles: number;
  boilerplateSubtracted: boolean;
  parallelAssessments: Array<{ assessmentId: string; title: string; classLabel: string; teacherName: string }>;
  skippedQuestions: Array<{
    questionId: string;
    promptExcerpt: string;
    reason: "too_short" | "too_few_docs" | "no_text" | "unsupported_type";
    medianWords: number;
  }>;
  questions: AssessmentPlagiarismQuestion[];
  pairSummary: Array<{
    a: AssessmentPlagiarismDocRef;
    b: AssessmentPlagiarismDocRef;
    questionCount: number;
    scores: number[];
    maxScore: number;
  }>;
};

export function getSavedAssessmentPlagiarismCheck(token: string, assessmentId: string, schoolId: string) {
  return apiFetch<{ data: { result: AssessmentPlagiarismResult | null; generatedAt: string | null; canRun: boolean } }>(
    `/api/professor/assessments/${encodeURIComponent(assessmentId)}/plagiarism${schoolQs(schoolId)}`,
    { token },
  ).then((res) => res.data);
}

export function runAssessmentPlagiarismCheck(token: string, assessmentId: string, schoolId: string) {
  return apiFetch<{ data: AssessmentPlagiarismResult }>(
    `/api/professor/assessments/${encodeURIComponent(assessmentId)}/plagiarism${schoolQs(schoolId)}`,
    { token, method: "POST" },
  ).then((res) => res.data);
}

// Worksheet-et e nje ore te vetme, per profesorin qe e pergatit oren. Vetem lexim: skema
// e dokumentit dhe shabllloni bosh, pa pergjigje nxenesish dhe pa krijuar sesion.
export type LessonWorksheetField = { id: string; label: string; required: true; maxLength: number };

export type LessonWorksheetSchema = {
  version: number;
  title: string;
  intro: string[];
  tables: Array<{ blockIndex: number; cells: Array<Array<{ text: string; fieldId: string | null; header: boolean; rowLabel: boolean }>> }>;
  standaloneFieldIds: string[];
  fields: LessonWorksheetField[];
};

export type LessonWorksheetDocument = {
  id: string;
  title: string;
  fileName: string;
  schema: LessonWorksheetSchema | null;
  fieldCount: number;
  requiresConfiguration: boolean;
  error: string;
};

export type LessonWorksheetScope = Pick<PlanSourceLessonScopeInput, "planTopicId" | "schoolId" | "asProfessor">;

function lessonWorksheetPath(path: string, scope: LessonWorksheetScope) {
  const query = new URLSearchParams();
  if (scope.schoolId) query.set("schoolId", scope.schoolId);
  if (scope.asProfessor) query.set("as", "professor");
  const encoded = query.toString();
  return encoded ? `${path}${path.includes("?") ? "&" : "?"}${encoded}` : path;
}

export function listLessonWorksheets(scope: LessonWorksheetScope) {
  return apiFetch<{ documents: LessonWorksheetDocument[] }>(
    lessonWorksheetPath(`/api/professor/lesson-worksheets?topicId=${encodeURIComponent(scope.planTopicId)}`, scope),
  ).then((result) => result.documents || []);
}

// E njejta permbajtje qe merr nxenesi kur hap worksheet-in, pa sesion dhe pa pergjigje.
export type LessonWorksheetStudentView = {
  lesson: {
    topicId: string; lessonNumber: number; topic: string; subtopic: string;
    subjectName: string; classLabel: string; opensAt: string | null; closesAt: string | null;
  };
  security: { copyPasteBlocked: boolean; cameraEnabled: boolean; tabSwitchBlocked: boolean; fullscreenRequired: boolean; downloadAllowed?: boolean };
  documents: Array<{ id: string; title: string; fileName: string; schema: LessonWorksheetSchema | null; requiresConfiguration: boolean; error: string; allowedLearningAims?: Array<"A" | "B" | "C" | "D"> }>;
  supportDocuments: Array<{
    id: string; title: string; fileName: string;
    kind: "scenario_summary" | "case_study" | "draft_checklist" | "research_log";
    mode: "read_only" | "student_fillable";
    content?: string; schema?: LessonWorksheetSchema; requiresConfiguration?: boolean; error?: string;
    allowedLearningAims?: Array<"A" | "B" | "C" | "D">;
  }>;
};

export function getLessonWorksheetStudentView(scope: LessonWorksheetScope) {
  return apiFetch<LessonWorksheetStudentView>(
    lessonWorksheetPath(`/api/professor/lesson-worksheets/${encodeURIComponent(scope.planTopicId)}/student-view`, scope),
  );
}

export type WorksheetEditableBlock = { kind: "paragraph"; text: string } | { kind: "table"; rows: string[][] };

export function getLessonWorksheetEditor(scope: LessonWorksheetScope, documentId: string) {
  return apiFetch<{ blocks: WorksheetEditableBlock[]; sourceSha256: string }>(lessonWorksheetPath(
    `/api/professor/lesson-worksheets/${encodeURIComponent(scope.planTopicId)}/${encodeURIComponent(documentId)}/editor`, scope,
  ));
}

export function saveLessonWorksheetEditor(scope: LessonWorksheetScope, documentId: string, sourceSha256: string, blocks: WorksheetEditableBlock[]) {
  return apiFetch<{ success: true; sourceSha256: string; unchanged: boolean }>(lessonWorksheetPath(
    `/api/professor/lesson-worksheets/${encodeURIComponent(scope.planTopicId)}/${encodeURIComponent(documentId)}/editor`, scope,
  ), { method: "PUT", body: JSON.stringify({ sourceSha256, blocks }), headers: { "Idempotency-Key": crypto.randomUUID() } });
}

export function lessonWorksheetSupportDownloadPath(scope: LessonWorksheetScope, supportDocumentId: string) {
  return lessonWorksheetPath(
    `/api/professor/lesson-worksheets/${encodeURIComponent(scope.planTopicId)}/support/${encodeURIComponent(supportDocumentId)}/download`,
    scope,
  );
}

export function lessonWorksheetDownloadPath(
  scope: LessonWorksheetScope,
  documentId: string,
) {
  return lessonWorksheetPath(
    `/api/professor/lesson-worksheets/${encodeURIComponent(scope.planTopicId)}/${encodeURIComponent(documentId)}/download`,
    scope,
  );
}

export type LessonChallengeScope = { topicId: string; schoolId?: string; teacher?: boolean; asProfessor?: boolean };

function lessonChallengePath(scope: LessonChallengeScope, suffix = '') {
  const query = new URLSearchParams({ audience: scope.teacher ? 'teacher' : 'student' });
  if (scope.schoolId) query.set('schoolId', scope.schoolId);
  if (scope.teacher && scope.asProfessor) query.set('as', 'professor');
  return `/api/elearning/lesson-challenges/${encodeURIComponent(scope.topicId)}${suffix}?${query}`;
}

export async function getLessonChallenge(scope: LessonChallengeScope) {
  return (await apiFetch<{ view: import('../../shared/lesson-challenges.ts').LessonChallengeView }>(lessonChallengePath(scope))).view;
}

export async function saveLessonChallengeDraft(scope: LessonChallengeScope, input: {
  revision: number; title: string; questions: import('../../shared/lesson-challenges.ts').LessonChallengeQuestion[];
}) {
  return (await apiFetch<{ view: import('../../shared/lesson-challenges.ts').LessonChallengeView }>(lessonChallengePath(scope, '/draft'), { method: 'PUT', body: JSON.stringify(input) })).view;
}

export async function changeLessonChallengeStatus(scope: LessonChallengeScope, action: 'open' | 'close', revision: number) {
  return (await apiFetch<{ view: import('../../shared/lesson-challenges.ts').LessonChallengeView }>(lessonChallengePath(scope, `/${action}`), { method: 'POST', body: JSON.stringify({ revision }) })).view;
}

export async function submitLessonChallengeAnswers(scope: LessonChallengeScope, input: {
  id: string; revision: number; answers: import('../../shared/lesson-challenges.ts').LessonChallengeAnswer[];
}) {
  return (await apiFetch<{ view: import('../../shared/lesson-challenges.ts').LessonChallengeView }>(lessonChallengePath(scope, '/answers'), { method: 'POST', headers: { 'Idempotency-Key': input.id }, body: JSON.stringify(input) })).view;
}

export function getLessonChallengeSuggestions(scope: LessonChallengeScope) {
  return apiFetch<{ sourceVersionId: string | null; questions: import('../../shared/lesson-challenges.ts').LessonChallengeQuestion[] }>(lessonChallengePath(scope, '/suggestions'));
}

// Central school-scoped privilege administration uses the shared authenticated API client.
export function fetchEffectivePermissions(schoolId:string,viewAsId:string|null=null){return apiFetch<import('../../shared/platform-permissions.ts').PermissionSnapshot>(`/api/permissions/effective?schoolId=${encodeURIComponent(schoolId)}`,{headers:{"X-View-As":viewAsId||""}});}
export function fetchPermissionMatrix(schoolId:string){return apiFetch<import('../../shared/platform-permissions.ts').PermissionMatrix>(`/api/admin/permissions/matrix?schoolId=${encodeURIComponent(schoolId)}`);}
export function savePermissionMatrix(schoolId:string,input:{policyScope:'school'|'global';changes:import('../../shared/platform-permissions.ts').PermissionRoleChange[];expectedVersion:import('../../shared/platform-permissions.ts').PermissionVersion;reason:string}){return apiFetch<import('../../shared/platform-permissions.ts').PermissionSaveResult>(`/api/admin/permissions/matrix?schoolId=${encodeURIComponent(schoolId)}`,{method:'PATCH',body:JSON.stringify(input)});}
export function fetchIndividualPermissions(userId:string,schoolId:string){return apiFetch<import('../../shared/platform-permissions.ts').IndividualPermissionProfile>(`/api/admin/users/${encodeURIComponent(userId)}/permissions?schoolId=${encodeURIComponent(schoolId)}`);}
export function saveIndividualPermissions(userId:string,schoolId:string,input:{changes:import('../../shared/platform-permissions.ts').PermissionUserChange[];expectedVersion:import('../../shared/platform-permissions.ts').PermissionVersion;reason:string}){return apiFetch<import('../../shared/platform-permissions.ts').PermissionSaveResult>(`/api/admin/users/${encodeURIComponent(userId)}/permissions?schoolId=${encodeURIComponent(schoolId)}`,{method:'PATCH',body:JSON.stringify(input)});}
export function fetchPermissionScopeOptions(schoolId:string,permission:import('../../shared/platform-permissions.ts').PermissionId){return apiFetch<import('../../shared/platform-permissions.ts').PermissionScopeOptions>(`/api/admin/permissions/scope-options?schoolId=${encodeURIComponent(schoolId)}&permission=${encodeURIComponent(permission)}`);}
